import { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { API_URL } from '../config';
import ReactFlow, {
  Node,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  MiniMap,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Settings, Trash2, Edit } from 'lucide-react';
import { Handle, Position } from 'reactflow';
import FlowHeader from './FlowHeader';
import MetadataPanel from './MetadataPanel';
import ChatSimulator from './ChatSimulator';
import TestRunPanel from './TestRunPanel';
import SettingsPanel from './SettingsPanel';
import FlowLibraryPanel from './FlowLibraryPanel';
import GenerateFlowPanel from './GenerateFlowPanel';
import PromptifyPanel from './PromptifyPanel';
import ModifyFlowPanel from './ModifyFlowPanel';
import ChatHistoryPanel from './ChatHistoryPanel';
import Sidebar from './Sidebar';
import KnowledgeBasePanel from './KnowledgeBasePanel';
import IndexManagementPanel from './IndexManagementPanel';
import ServicesAPIPanel from './ServicesAPIPanel';
import ServicesPanel from './ServicesPanel';
import EmbedCodeGenerator from './EmbedCodeGenerator';
import SchedulerPanel from './SchedulerPanel';
import WebhookPanel from './WebhookPanel';
import CredentialsPanel from './CredentialsPanel';
import ExecutionsPanel from './ExecutionsPanel';
import TemplatesPanel from './TemplatesPanel';
import VersioningPanel from './VersioningPanel';
import AIAssistant from './AIAssistant';
import WorkspaceSettings from './WorkspaceSettings';
import { importBotpressFile } from '../utils/botpressImporter';
import { useAuth0UserId } from '../hooks/useAuth0UserId';
import { useAuth0 } from '@auth0/auth0-react';
import * as promptApi from '../services/promptApi';
import * as flowApi from '../services/flowApi';

// Variable Autocomplete Component
interface VariableAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  availableVariables: string[];
  placeholder?: string;
  className?: string;
  multiline?: boolean;
  rows?: number;
}

function VariableAutocomplete({ 
  value, 
  onChange, 
  availableVariables, 
  placeholder, 
  className,
  multiline = false,
  rows = 4
}: VariableAutocompleteProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredVars, setFilteredVars] = useState<string[]>([]);
  const [_cursorPosition, setCursorPosition] = useState(0);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Detect when user types to show suggestions
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    onChange(newValue);
    setCursorPosition(cursorPos);

    // Check if we should show suggestions
    // Show if there are available variables and user hasn't typed complete text
    if (availableVariables.length > 0) {
      // Filter variables based on current input
      const filtered = availableVariables.filter(v => 
        v.toLowerCase().includes(newValue.toLowerCase())
      );
      setFilteredVars(filtered);
      setShowSuggestions(filtered.length > 0 && newValue.length > 0);
    }
  };

  // Insert variable at cursor position
  const insertVariable = (variable: string) => {
    const input = inputRef.current;
    if (!input) return;

    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    
    // For Say messages, wrap in {} if not multiline context
    const insertText = multiline ? `{${variable}}` : variable;
    
    const newValue = value.substring(0, start) + insertText + value.substring(end);
    onChange(newValue);
    setShowSuggestions(false);
    
    // Set cursor after inserted text
    setTimeout(() => {
      const newCursorPos = start + insertText.length;
      input.setSelectionRange(newCursorPos, newCursorPos);
      input.focus();
    }, 0);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as HTMLElement) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as HTMLElement)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="space-y-2">
      {/* Variables button - shown before the input */}
      {availableVariables.length > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              setFilteredVars(availableVariables);
              setShowSuggestions(!showSuggestions);
            }}
            className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-lg font-bold shadow-sm transition flex items-center gap-1"
            title="Mostra tutte le variabili disponibili"
          >
            🏷️ Variabili ({availableVariables.length})
          </button>
        </div>
      )}
      
      <div className="relative">
        {multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={value}
            onChange={handleChange}
            onFocus={() => {
              if (availableVariables.length > 0 && value.length > 0) {
                const filtered = availableVariables.filter(v => 
                  v.toLowerCase().includes(value.toLowerCase())
                );
                setFilteredVars(filtered);
                setShowSuggestions(filtered.length > 0);
              }
            }}
            className={className}
            placeholder={placeholder}
            rows={rows}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            value={value}
            onChange={handleChange}
            onFocus={() => {
              if (availableVariables.length > 0 && value.length > 0) {
                const filtered = availableVariables.filter(v => 
                  v.toLowerCase().includes(value.toLowerCase())
                );
                setFilteredVars(filtered);
                setShowSuggestions(filtered.length > 0);
              }
            }}
            className={className}
            placeholder={placeholder}
          />
        )}
        
        {/* Suggestions Dropdown */}
        {showSuggestions && filteredVars.length > 0 && (
          <div 
            ref={suggestionsRef}
            className="absolute z-50 w-full mt-1 bg-white border-2 border-blue-300 rounded-lg shadow-xl max-h-48 overflow-y-auto"
          >
            <div className="p-2 bg-blue-50 border-b border-blue-200 text-xs font-bold text-blue-800">
              📋 Variabili disponibili - Clicca per inserire
            </div>
            {filteredVars.map((variable, idx) => (
              <button
                key={idx}
                onClick={() => insertVariable(variable)}
                className="w-full text-left px-4 py-2 hover:bg-blue-100 transition flex items-center gap-2 border-b border-gray-100 last:border-b-0"
              >
                <span className="text-purple-600 font-bold">🏷️</span>
                <code className="text-sm font-mono text-gray-900">{variable}</code>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Helper component per mostrare numero nodo
function NodeNumber({ nodeNumber, color = '#9333ea' }: { nodeNumber: number; color?: string }) {
  return (
    <div 
      className="absolute bottom-1 right-1 text-white text-xs font-bold px-1.5 py-0.5 rounded shadow-lg z-10"
      style={{ backgroundColor: color }}
    >
      {nodeNumber}
    </div>
  );
}

// Custom Node Components
function StartNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 min-w-[220px] relative">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🚀</span>
        <div className="font-bold text-xl text-green-900">{data.label || 'Start'}</div>
      </div>
      {data.message && (
        <div className="text-sm text-gray-700 mt-2 line-clamp-3 italic bg-green-100 rounded p-2">
          "{data.message}"
        </div>
      )}
      {!data.message && (
        <div className="text-xs text-gray-500 mt-2">Inizio del flow</div>
      )}
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-green-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#10b981" />}
    </div>
  );
}

function SayNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-blue-500 bg-gradient-to-br from-blue-50 to-cyan-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-blue-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">💬</span>
        <div className="font-bold text-xl text-blue-900">{data.label || 'Say'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2 line-clamp-3">{data.message || 'Messaggio...'}</div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-blue-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#3b82f6" />}
    </div>
  );
}

function InputNode({ data }: { data: any }) {
  // Mappa validazione a emoji/label
  const validationLabels: Record<string, string> = {
    'email': '📧 Email',
    'phone': '📱 Telefono',
    'telefono': '📱 Telefono',
    'name': '👤 Nome',
    'nome': '👤 Nome',
    'number': '🔢 Numero',
    'numero': '🔢 Numero',
    'cap': '📮 CAP',
  };
  const validationType = data.validation_type || '';
  const validationLabel = validationLabels[validationType] || '';
  
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-purple-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">⌨️</span>
        <div className="font-bold text-xl text-purple-900">{data.label || 'Input'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">{data.prompt || 'Attendi input utente'}</div>
      {validationLabel && (
        <div className="mt-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full inline-block font-medium">
          ✓ {validationLabel}
        </div>
      )}
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-purple-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#a855f7" />}
    </div>
  );
}

function RAGNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-orange-500 bg-gradient-to-br from-orange-50 to-amber-50 min-w-[240px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-orange-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🔍</span>
        <div className="font-bold text-xl text-orange-900">{data.label || 'RAG Query'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        <div className="font-semibold text-orange-800 flex items-center gap-1">
          📚 {data.index_name || '(nessun indice)'}
        </div>
        {data.scope_prompt && (
          <div className="text-xs text-gray-500 mt-1 line-clamp-1 italic">
            🎯 {data.scope_prompt.substring(0, 40)}...
          </div>
        )}
        {data.max_interactions && (
          <div className="text-xs text-orange-600 mt-1">
            🔄 Max: {data.max_interactions} interazioni
          </div>
        )}
      </div>
      {/* 🔄 RAG cicla internamente - NO uscita destra! */}
      {/* Uscita "fuori scope" */}
      <Handle 
        type="source" 
        position={Position.Bottom} 
        id="out_of_scope" 
        className="w-4 h-4 !bg-gray-400" 
        style={{ left: '30%' }}
      />
      {/* Uscita "max interazioni" */}
      <Handle 
        type="source" 
        position={Position.Bottom} 
        id="max_interactions" 
        className="w-4 h-4 !bg-red-400" 
        style={{ left: '70%' }}
      />
      <div className="absolute -bottom-6 left-0 right-0 flex justify-between text-[10px] text-gray-500 px-2">
        <span style={{ marginLeft: '20%' }}>fuori scope</span>
        <span style={{ marginRight: '15%' }}>max inter.</span>
      </div>
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#f97316" />}
    </div>
  );
}

// 🔍 RAG Simple Node - Query semplice KB: query → risposta → next
function RagSimpleNode({ data }: { data: any }) {
  return (
    <div className="px-5 py-3 shadow-xl rounded-xl border-3 border-teal-500 bg-gradient-to-br from-teal-50 to-cyan-50 min-w-[200px]">
      <Handle type="target" position={Position.Left} className="w-3 h-3 !bg-teal-500" />
      <div className="flex items-center gap-2 mb-1">
        <span className="text-2xl">📚</span>
        <div className="font-bold text-lg text-teal-900">{data.label || 'RAG Simple'}</div>
      </div>
      <div className="text-sm text-gray-700">
        <div className="font-semibold text-teal-700 flex items-center gap-1">
          🗂️ {data.index_name || '(nessun indice)'}
        </div>
        {data.query && (
          <div className="text-xs text-gray-500 mt-1 italic">
            🔍 {data.query}
          </div>
        )}
        {data.output_variable && (
          <div className="text-xs text-teal-600 mt-1">
            💾 → {data.output_variable}
          </div>
        )}
      </div>
      {/* Una sola uscita - passa automaticamente al next */}
      <Handle type="source" position={Position.Right} className="w-3 h-3 !bg-teal-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#14b8a6" />}
    </div>
  );
}

function RagLoopNode({ data }: { data: any }) {
  return (
    <div className="px-5 py-3 shadow-xl rounded-xl border-3 border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50 min-w-[220px]">
      <Handle type="target" position={Position.Left} className="w-3 h-3 !bg-emerald-500" />
      <div className="flex items-center gap-2 mb-1">
        <span className="text-2xl">🔄📚</span>
        <div className="font-bold text-lg text-emerald-900">{data.label || 'RAG Loop'}</div>
      </div>
      <div className="text-sm text-gray-700 space-y-1">
        <div className="font-semibold text-emerald-700 flex items-center gap-1">
          🗂️ {data.index_name || '(nessun indice)'}
        </div>
        <div className="text-xs text-gray-600 italic truncate max-w-[180px]">
          💬 {data.welcome_message?.substring(0, 30) || 'Cosa vuoi sapere?'}...
        </div>
        <div className="text-xs text-emerald-600 flex items-center gap-1">
          🔁 Max {data.max_interactions || 5} domande
        </div>
      </div>
      {/* Una sola uscita - esce quando finisce il loop */}
      <Handle type="source" position={Position.Right} className="w-3 h-3 !bg-emerald-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#10b981" />}
    </div>
  );
}

function ConditionNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-yellow-500 bg-gradient-to-br from-yellow-50 to-amber-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-yellow-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🔀</span>
        <div className="font-bold text-xl text-yellow-900">{data.label || 'Condition'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2 font-mono text-xs">
        {data.condition || 'if (...)'}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-yellow-500" />
      <Handle type="source" position={Position.Bottom} id="else" className="w-4 h-4 !bg-red-400" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#eab308" />}
    </div>
  );
}

function CaseNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-amber-500 bg-gradient-to-br from-amber-50 to-yellow-50 min-w-[220px] relative pb-8">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-amber-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🔢</span>
        <div className="font-bold text-xl text-amber-900">{data.label || 'Case'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">Variable: {data.variable || 'N/A'}</div>
      <div className="text-xs text-amber-600 mt-1">
        {data.cases ? `${data.cases.length} case(s)` : 'No cases defined'}
      </div>
      {/* Uscita normale per i case definiti */}
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-amber-500" />
      {/* Uscita "altro" per input non corrispondente ai case */}
      <Handle 
        type="source" 
        position={Position.Bottom} 
        id="altro" 
        className="w-4 h-4 !bg-gray-400" 
        style={{ left: '50%' }}
      />
      <div className="absolute -bottom-5 left-0 right-0 text-center text-[10px] text-gray-500">
        altro
      </div>
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#f59e0b" />}
    </div>
  );
}

function JsonConditionNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-emerald-500 bg-gradient-to-br from-emerald-50 to-green-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-emerald-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🎯</span>
        <div className="font-bold text-xl text-emerald-900">{data.label || 'LLM-Condition'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">Variable: {data.variable || 'N/A'}</div>
      <div className="text-xs text-emerald-600 mt-1">
        {data.cases ? `${data.cases.length} case(s)` : 'No cases defined'}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-emerald-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#10b981" />}
    </div>
  );
}

function LLMCaseNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-cyan-500 bg-gradient-to-br from-cyan-50 to-blue-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-cyan-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🤖</span>
        <div className="font-bold text-xl text-cyan-900">{data.label || 'LLM-Case'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">Variable: {data.variable || 'N/A'}</div>
      <div className="text-xs text-cyan-600 mt-1">
        {data.cases ? `${data.cases.length} case(s)` : 'No cases defined'}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-cyan-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#06b6d4" />}
    </div>
  );
}

function ActionNode({ data }: { data: any }) {
  const actionText = typeof data.action === 'string' 
    ? data.action 
    : data.action?.label || 'Azione custom';
  
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-indigo-500 bg-gradient-to-br from-indigo-50 to-blue-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-indigo-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">⚙️</span>
        <div className="font-bold text-xl text-indigo-900">{data.label || 'Action'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2 line-clamp-2 italic">{actionText}</div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-indigo-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#6366f1" />}
    </div>
  );
}

function ApiCallNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-teal-500 bg-gradient-to-br from-teal-50 to-cyan-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-teal-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🌐</span>
        <div className="font-bold text-xl text-teal-900">{data.label || 'API Call'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        <div className="font-semibold text-teal-800 text-xs">
          {data.method || 'POST'} {data.api_url ? '🔗' : '❌'}
        </div>
        <div className="text-xs text-gray-600 mt-1 line-clamp-1">
          {data.api_url || 'No URL configured'}
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-teal-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#14b8a6" />}
    </div>
  );
}

// LLM Simple - Per elaborazione diretta (non conversazionale)
function LLMSimpleNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-violet-500 bg-gradient-to-br from-violet-50 to-purple-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-violet-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🧠</span>
        <div className="font-bold text-xl text-violet-900">{data.label || 'LLM Simple'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        {data.system_prompt && (
          <div className="font-semibold text-violet-800 text-xs truncate max-w-[180px]">
            📋 {data.system_prompt.substring(0, 30)}...
          </div>
        )}
        <div className="text-xs text-gray-600 mt-1">
          {data.model || 'groq/llama-3.3-70b'}
        </div>
        {data.output_variable && (
          <div className="text-xs text-violet-600 mt-1">
            📤 {data.output_variable}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-violet-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#8b5cf6" />}
    </div>
  );
}

// LLM Loop - Conversazione multi-turno con limite di interazioni
function LLMLoopNode({ data }: { data: any }) {
  const maxInteractions = data.max_interactions || 5;
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-indigo-500 bg-gradient-to-br from-indigo-50 to-blue-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-indigo-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🔄</span>
        <div className="font-bold text-xl text-indigo-900">{data.label || 'LLM Loop'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        {data.welcome_message && (
          <div className="font-semibold text-indigo-800 text-xs truncate max-w-[180px]">
            👋 {data.welcome_message.substring(0, 25)}...
          </div>
        )}
        <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
          <span className="text-indigo-600">🔢</span> Max {maxInteractions} domande
        </div>
        {data.exit_phrases && (
          <div className="text-xs text-gray-500 mt-1">
            ⛔ Uscita: {(data.exit_phrases as string[]).slice(0, 3).join(', ')}
          </div>
        )}
        {data.output_variable && (
          <div className="text-xs text-indigo-600 mt-1">
            📤 {data.output_variable}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-indigo-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#6366f1" />}
    </div>
  );
}

function LLMQualifierNode({ data }: { data: any }) {
  const interactions = data.max_interactions || 3;
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-pink-500 bg-gradient-to-br from-pink-50 to-rose-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-pink-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🎯</span>
        <div className="font-bold text-xl text-pink-900">{data.label || 'LLM Qualifier'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        <div className="font-semibold text-pink-800 text-xs">
          {data.input_variable ? `📥 ${data.input_variable}` : '❌ No input'}
        </div>
        <div className="text-xs text-gray-600 mt-1">
          {interactions} interazioni max
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-green-500" />
      <Handle type="source" position={Position.Bottom} id="no" className="w-4 h-4 !bg-red-400" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#ec4899" />}
    </div>
  );
}

function ReplayNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-violet-500 bg-gradient-to-br from-violet-50 to-purple-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-violet-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">💬</span>
        <div className="font-bold text-xl text-violet-900">{data.label || 'Replay'}</div>
      </div>
      <div className="space-y-2 mt-2">
        <div className="text-xs font-semibold text-violet-800">Risposta:</div>
        <div className="text-sm text-gray-700 line-clamp-2 bg-violet-100 rounded p-2">{data.response || 'Risposta bot...'}</div>
        <div className="text-xs font-semibold text-violet-800 mt-2">Domanda:</div>
        <div className="text-sm text-gray-700 line-clamp-2 bg-purple-100 rounded p-2">{data.question || 'Domanda utente...'}</div>
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-violet-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#8b5cf6" />}
    </div>
  );
}

function IntentLLMNode({ data }: { data: any }) {
  const interactions = data.max_interactions || 10;
  const intents = data.intents || ['comprare', 'affittare', 'vendere', 'info'];
  const intentsDisplay = intents.slice(0, 3).join(', ') + (intents.length > 3 ? '...' : '');

  return (
    <div className="relative px-6 py-4 shadow-2xl rounded-2xl border-4 border-purple-500 bg-gradient-to-br from-purple-50 to-violet-50 min-w-[220px]">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-purple-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🎯</span>
        <div className="font-bold text-xl text-purple-900">{data.label || 'Intent Detector'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        <div className="font-semibold text-purple-800 text-xs">
          {data.input_variable ? `📥 ${data.input_variable}` : '❌ No input'}
        </div>
        <div className="text-xs text-gray-600 mt-1">
          {interactions} interazioni max
        </div>
        <div className="text-xs text-purple-700 mt-1 font-medium">
          🎯 {intentsDisplay}
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-green-500" />
      <Handle type="source" position={Position.Bottom} id="no" className="w-4 h-4 !bg-red-400" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#9333ea" />}
    </div>
  );
}

function PropositionNode({ data }: { data: any }) {
  return (
    <div className={`px-6 py-4 shadow-2xl rounded-2xl border-4 border-fuchsia-500 bg-gradient-to-br from-fuchsia-50 to-pink-50 min-w-[220px] relative`}>
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-fuchsia-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">💬</span>
        <div className="font-bold text-xl text-fuchsia-900">{data.label || 'Proposition'}</div>
      </div>
      <div className="space-y-2 mt-2">
        <div className="text-xs font-semibold text-fuchsia-800">Testo:</div>
        <div className="text-sm text-gray-700 line-clamp-2 bg-fuchsia-100 rounded p-2">{data.text || 'Testo proposizione...'}</div>
        <div className="text-xs text-fuchsia-600 mt-1">
          {data.options ? `${data.options.length} opzione/i` : 'No opzioni'}
        </div>
      </div>
      {/* Handle principale per le opzioni */}
      <Handle 
        type="source" 
        position={Position.Right} 
        id="options"
        className="w-4 h-4 !bg-fuchsia-500" 
        style={{ top: '40%' }}
      />
      {/* Handle "altro" per testo libero */}
      <Handle 
        type="source" 
        position={Position.Right} 
        id="altro"
        className="w-4 h-4 !bg-gray-400" 
        style={{ top: '70%' }}
      />
      <div className="absolute right-[-8px] top-[68%] text-[9px] text-gray-500 font-bold">altro</div>
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#d946ef" />}
    </div>
  );
}

function EndNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-red-500 bg-gradient-to-br from-red-50 to-rose-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-red-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🏁</span>
        <div className="font-bold text-xl text-red-900">{data.label || 'End'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">{data.message || 'Fine conversazione'}</div>
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#ef4444" />}
    </div>
  );
}

// Default Node for unknown types
function DefaultNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-gray-500 bg-gradient-to-br from-gray-50 to-gray-100 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} id="default-in" className="w-4 h-4 !bg-gray-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">❓</span>
        <div className="font-bold text-xl text-gray-900">{data.label || 'Unknown'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">{data.type || 'Tipo sconosciuto'}</div>
      <Handle type="source" position={Position.Right} id="default-out" className="w-4 h-4 !bg-gray-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#6b7280" />}
    </div>
  );
}

// ========================================
// n8n-style Nodes
// ========================================

function RSSFeedNode({ data }: { data: any }) {
  const feedCount = data.feed_urls?.length || 0;
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-orange-500 bg-gradient-to-br from-orange-50 to-amber-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-orange-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">📰</span>
        <div className="font-bold text-xl text-orange-900">{data.label || 'RSS Feed'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        <div className="font-semibold text-orange-800 text-xs">
          {feedCount} feed{feedCount !== 1 ? 's' : ''} configurati
        </div>
        {data.items_per_feed && (
          <div className="text-xs text-orange-600 mt-1">
            Max {data.items_per_feed} items/feed
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-orange-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#f97316" />}
    </div>
  );
}

function EmailSendNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-rose-500 bg-gradient-to-br from-rose-50 to-pink-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-rose-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">📧</span>
        <div className="font-bold text-xl text-rose-900">{data.label || 'Email'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        {data.to_emails && (
          <div className="font-semibold text-rose-800 text-xs">
            A: {typeof data.to_emails === 'string' ? data.to_emails : data.to_emails.join(', ')}
          </div>
        )}
        {data.subject && (
          <div className="text-xs text-rose-600 mt-1 line-clamp-1">
            📋 {data.subject}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-rose-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#f43f5e" />}
    </div>
  );
}

function HttpRequestNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-sky-500 bg-gradient-to-br from-sky-50 to-blue-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-sky-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🌐</span>
        <div className="font-bold text-xl text-sky-900">{data.label || 'HTTP Request'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        <div className="font-semibold text-sky-800 text-xs">
          {data.method || 'GET'} {data.url ? '🔗' : '❌'}
        </div>
        <div className="text-xs text-sky-600 mt-1 line-clamp-1">
          {data.url || 'No URL'}
        </div>
        {data.retry_count > 0 && (
          <div className="text-xs text-sky-500 mt-1">
            🔄 Retry: {data.retry_count}x
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-sky-500" />
      <Handle type="source" position={Position.Bottom} id="error" className="w-4 h-4 !bg-red-400" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#0ea5e9" />}
    </div>
  );
}

function WaitNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-slate-500 bg-gradient-to-br from-slate-50 to-gray-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-slate-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">⏳</span>
        <div className="font-bold text-xl text-slate-900">{data.label || 'Wait'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        <div className="font-semibold text-slate-800 text-xs">
          ⏱️ {data.wait_seconds || 0} secondi
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-slate-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#64748b" />}
    </div>
  );
}

function SetNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-lime-500 bg-gradient-to-br from-lime-50 to-green-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-lime-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">📝</span>
        <div className="font-bold text-xl text-lime-900">{data.label || 'Set Variable'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        {data.variable_name && (
          <div className="font-mono text-xs text-lime-800">
            {data.variable_name} = ...
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-lime-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#84cc16" />}
    </div>
  );
}

function CodeNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-emerald-500 bg-gradient-to-br from-emerald-50 to-green-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-emerald-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🐍</span>
        <div className="font-bold text-xl text-emerald-900">{data.label || 'Code'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        <div className="font-mono text-xs text-emerald-800 line-clamp-2">
          {data.code ? data.code.substring(0, 50) + '...' : 'Python code...'}
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-emerald-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#10b981" />}
    </div>
  );
}

function SwitchNode({ data }: { data: any }) {
  const caseCount = data.cases?.length || 0;
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-violet-500 bg-gradient-to-br from-violet-50 to-purple-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-violet-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🔀</span>
        <div className="font-bold text-xl text-violet-900">{data.label || 'Switch'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        {data.variable && (
          <div className="font-mono text-xs text-violet-800">
            switch({data.variable})
          </div>
        )}
        <div className="text-xs text-violet-600 mt-1">
          {caseCount} case{caseCount !== 1 ? 's' : ''}
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-violet-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#8b5cf6" />}
    </div>
  );
}

function FilterNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-cyan-500 bg-gradient-to-br from-cyan-50 to-teal-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-cyan-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🔍</span>
        <div className="font-bold text-xl text-cyan-900">{data.label || 'Filter'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        {data.field && (
          <div className="font-mono text-xs text-cyan-800">
            {data.field} {data.operator || '=='} {data.value}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-cyan-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#06b6d4" />}
    </div>
  );
}

function AggregateNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-fuchsia-500 bg-gradient-to-br from-fuchsia-50 to-pink-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-fuchsia-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">📊</span>
        <div className="font-bold text-xl text-fuchsia-900">{data.label || 'Aggregate'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        <div className="font-semibold text-fuchsia-800 text-xs">
          {data.operation || 'count'}
          {data.field && ` (${data.field})`}
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-fuchsia-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#d946ef" />}
    </div>
  );
}

// Integration Nodes
function SlackNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-purple-500 bg-gradient-to-br from-purple-50 to-violet-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-purple-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">💬</span>
        <div className="font-bold text-xl text-purple-900">{data.label || 'Slack'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        <div className="font-semibold text-purple-800 text-xs">
          {data.operation || 'send_message'}
        </div>
        {data.channel && (
          <div className="text-xs text-purple-600 mt-1">
            📢 {data.channel}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-purple-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#a855f7" />}
    </div>
  );
}

function TelegramNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-blue-500 bg-gradient-to-br from-blue-50 to-cyan-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-blue-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">📱</span>
        <div className="font-bold text-xl text-blue-900">{data.label || 'Telegram'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        <div className="font-semibold text-blue-800 text-xs">
          {data.operation || 'send_message'}
        </div>
        {data.chat_id && (
          <div className="text-xs text-blue-600 mt-1">
            👤 Chat: {data.chat_id}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-blue-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#3b82f6" />}
    </div>
  );
}

function GoogleSheetsNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-green-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">📊</span>
        <div className="font-bold text-xl text-green-900">{data.label || 'Google Sheets'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        <div className="font-semibold text-green-800 text-xs">
          {data.operation || 'read'}
        </div>
        {data.range && (
          <div className="text-xs text-green-600 mt-1">
            📋 {data.range}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-green-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#22c55e" />}
    </div>
  );
}

function AirtableNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-yellow-500 bg-gradient-to-br from-yellow-50 to-amber-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-yellow-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">📋</span>
        <div className="font-bold text-xl text-yellow-900">{data.label || 'Airtable'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        <div className="font-semibold text-yellow-800 text-xs">
          {data.operation || 'list'}
        </div>
        {data.table_name && (
          <div className="text-xs text-yellow-600 mt-1">
            🗄️ {data.table_name}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-yellow-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#eab308" />}
    </div>
  );
}

function WebhookTriggerNode({ data }: { data: any }) {
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 min-w-[220px] relative">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🔗</span>
        <div className="font-bold text-xl text-indigo-900">{data.label || 'Webhook'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        <div className="font-semibold text-indigo-800 text-xs">
          {data.method || 'POST'}
        </div>
        {data.path && (
          <div className="text-xs text-indigo-600 mt-1 font-mono">
            /{data.path}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-indigo-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#6366f1" />}
    </div>
  );
}

function ScheduleTriggerNode({ data }: { data: any }) {
  const triggerType = data.trigger_type || 'interval';
  const intervalValue = data.interval_value || 5;
  const intervalUnit = data.interval_unit || 'minutes';
  const unitLabels: Record<string, string> = { seconds: 'sec', minutes: 'min', hours: 'ore', days: 'giorni' };
  
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-amber-500 bg-gradient-to-br from-amber-50 to-yellow-50 min-w-[220px] relative">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">⏰</span>
        <div className="font-bold text-xl text-amber-900">{data.label || 'Schedule'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2">
        {triggerType === 'cron' && data.cron_expression && (
          <div className="font-mono text-xs text-amber-800">
            🕐 {data.cron_expression}
          </div>
        )}
        {triggerType === 'interval' && (
          <div className="text-xs text-amber-700 mt-1">
            🔄 Ogni {intervalValue} {unitLabels[intervalUnit] || intervalUnit}
          </div>
        )}
        {triggerType === 'daily' && (
          <div className="text-xs text-amber-700 mt-1">
            📅 Ogni giorno alle {data.daily_time || '09:00'}
          </div>
        )}
        {triggerType === 'weekly' && (
          <div className="text-xs text-amber-700 mt-1">
            📆 Settimanale
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-amber-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#f59e0b" />}
    </div>
  );
}

function AgentNode({ data }: { data: any }) {
  const agentId = data.agent_id || 'N/A';
  const inputMessage = data.input_message || '';
  
  return (
    <div className="px-6 py-4 shadow-2xl rounded-2xl border-4 border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 min-w-[220px] relative">
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-purple-500" />
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl">🤖</span>
        <div className="font-bold text-xl text-purple-900">{data.label || 'Agent'}</div>
      </div>
      <div className="text-sm text-gray-700 mt-2 space-y-1">
        <div className="text-xs font-semibold text-purple-800">
          ID: {agentId}
        </div>
        {inputMessage && (
          <div className="text-xs text-purple-600 mt-1 line-clamp-2">
            📥 {inputMessage.substring(0, 40)}{inputMessage.length > 40 ? '...' : ''}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-purple-500" />
      {data.nodeNumber && <NodeNumber nodeNumber={data.nodeNumber} color="#9333ea" />}
    </div>
  );
}

const nodeTypes = {
  // Core nodes
  start: StartNode,
  say: SayNode,
  input: InputNode,
  replay: ReplayNode,
  proposition: PropositionNode,
  rag: RAGNode,
  rag_simple: RagSimpleNode,
  rag_loop: RagLoopNode,
  condition: ConditionNode,
  case: CaseNode,
  json_condition: JsonConditionNode,
  llm_case: LLMCaseNode,
  action: ActionNode,
  api_call: ApiCallNode,
  llm_simple: LLMSimpleNode,
  llm_loop: LLMLoopNode,
  llm_qualifier: LLMQualifierNode,
  IntentLLM: IntentLLMNode,
  // n8n-style nodes
  rss_feed: RSSFeedNode,
  email_send: EmailSendNode,
  http_request: HttpRequestNode,
  wait: WaitNode,
  set: SetNode,
  code: CodeNode,
  switch: SwitchNode,
  filter: FilterNode,
  aggregate: AggregateNode,
  schedule_trigger: ScheduleTriggerNode,
  webhook_trigger: WebhookTriggerNode,
  // Multi-Agent
  agent: AgentNode,
  // Integration nodes
  slack: SlackNode,
  telegram: TelegramNode,
  google_sheets: GoogleSheetsNode,
  airtable: AirtableNode,
  end: EndNode,
  default: DefaultNode,
};

// Node Templates for Palette - PRINCIPALI (usati da AI Help)
const nodeTemplates = [
  // === Core Nodes ===
  { type: 'start', label: '🚀 Start', color: 'bg-green-500', category: 'core' },
  { type: 'say', label: '💬 Say', color: 'bg-blue-500', category: 'core' },
  { type: 'input', label: '⌨️ Input', color: 'bg-purple-500', category: 'core' },
  { type: 'end', label: '🏁 End', color: 'bg-red-500', category: 'core' },
  
  // === AI & Knowledge ===
  { type: 'llm_simple', label: '🧠 LLM Simple', color: 'bg-violet-500', category: 'ai' },
  { type: 'llm_loop', label: '🔄 LLM Loop', color: 'bg-indigo-500', category: 'ai' },
  { type: 'llm_qualifier', label: '🎯 LLM Qualifier', color: 'bg-pink-500', category: 'ai' },
  { type: 'rag_simple', label: '📚 RAG Simple', color: 'bg-teal-500', category: 'ai' },
  { type: 'rag_loop', label: '🔄📚 RAG Loop', color: 'bg-emerald-500', category: 'ai' },
  
  // === Logic ===
  { type: 'condition', label: '🔀 Condition', color: 'bg-yellow-500', category: 'logic' },
  { type: 'case', label: '🔢 Case', color: 'bg-amber-500', category: 'logic' },
  
  // === Integration ===
  { type: 'email_send', label: '📧 Email', color: 'bg-rose-500', category: 'integration' },
  { type: 'api_call', label: '🌐 API Call', color: 'bg-teal-500', category: 'integration' },
  
  // === Flow Control ===
  { type: 'wait', label: '⏳ Wait', color: 'bg-slate-500', category: 'flow' },
];

// Node Templates SPECIALIZZATI (NON usati da AI Help - per utenti esperti)
const specializedNodeTemplates = [
  // === AI Avanzati ===
  { type: 'IntentLLM', label: '🎯 Intent LLM', color: 'bg-rose-500', category: 'ai-advanced' },
  { type: 'llm_case', label: '🤖 LLM-Case', color: 'bg-cyan-500', category: 'ai-advanced' },
  { type: 'proposition', label: '📋 Proposition', color: 'bg-indigo-500', category: 'ai-advanced' },
  { type: 'replay', label: '💬 Replay', color: 'bg-violet-500', category: 'ai-advanced' },
  { type: 'rag', label: '🔍 RAG (Ciclo)', color: 'bg-orange-500', category: 'ai-advanced' },
  
  // === Logic Avanzato ===
  { type: 'switch', label: '🔀 Switch', color: 'bg-violet-500', category: 'logic-advanced' },
  { type: 'filter', label: '🔍 Filter', color: 'bg-cyan-500', category: 'logic-advanced' },
  
  // === Code & Transform ===
  { type: 'action', label: '⚙️ Action (JS)', color: 'bg-indigo-500', category: 'code' },
  { type: 'code', label: '🐍 Code (Python)', color: 'bg-emerald-500', category: 'code' },
  { type: 'set', label: '📝 Set Variable', color: 'bg-lime-500', category: 'code' },
  { type: 'aggregate', label: '📊 Aggregate', color: 'bg-fuchsia-500', category: 'code' },
  
  // === Integration Avanzate ===
  { type: 'http_request', label: '🌐 HTTP Request', color: 'bg-sky-500', category: 'integration' },
  { type: 'rss_feed', label: '📰 RSS Feed', color: 'bg-orange-500', category: 'integration' },
  { type: 'slack', label: '💬 Slack', color: 'bg-purple-500', category: 'integration' },
  { type: 'telegram', label: '📱 Telegram', color: 'bg-blue-500', category: 'integration' },
  { type: 'google_sheets', label: '📊 Google Sheets', color: 'bg-green-500', category: 'integration' },
  { type: 'airtable', label: '📋 Airtable', color: 'bg-yellow-500', category: 'integration' },
  
  // === Trigger ===
  { type: 'schedule_trigger', label: '⏰ Schedule', color: 'bg-amber-500', category: 'trigger' },
  { type: 'webhook_trigger', label: '🔗 Webhook', color: 'bg-indigo-500', category: 'trigger' },
];

// Main Component
export default function FlowStudio() {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [flowName, setFlowName] = useState('my-flow');
  const [showProperties, setShowProperties] = useState(true);
  const [showPalette, setShowPalette] = useState(true); // Sidebar nodi
  const [showSimulator, setShowSimulator] = useState(false); // Chat simulator
  const [showSettings, setShowSettings] = useState(false); // Settings panel (prompt)
  const [showGeneratePanel, setShowGeneratePanel] = useState(false); // Generate flow panel
  const [showPromptifyPanel, setShowPromptifyPanel] = useState(false); // Promptify panel
  const [showModifyPanel, setShowModifyPanel] = useState(false); // Modify flow panel
  const [showFlowLibrary, setShowFlowLibrary] = useState(false); // Flow library panel
  const [showChatHistory, setShowChatHistory] = useState(false); // Chat history panel
  const [showServicesPanel, setShowServicesPanel] = useState(false); // Services/API panel
  const [loadedChatMessages, setLoadedChatMessages] = useState<any[] | undefined>(undefined); // Messages from loaded chat
  const [showEmbedGenerator, setShowEmbedGenerator] = useState(false); // Embed code generator
  const [showSchedulerPanel, setShowSchedulerPanel] = useState(false); // Scheduler panel (n8n-style)
  const [showWebhookPanel, setShowWebhookPanel] = useState(false); // Webhook panel (n8n-style)
  const [showCredentialsPanel, setShowCredentialsPanel] = useState(false); // Credentials vault
  const [showExecutionsPanel, setShowExecutionsPanel] = useState(false); // Execution history
  const [showTemplatesPanel, setShowTemplatesPanel] = useState(false); // Templates library
  const [showVersioningPanel, setShowVersioningPanel] = useState(false); // Version control
  const [showAIAssistant, setShowAIAssistant] = useState(false); // AI Assistant
  const [showWorkspaceSettings, setShowWorkspaceSettings] = useState(false); // Workspace Settings
  const [showSpecializedNodes, setShowSpecializedNodes] = useState(false); // Sezione nodi specializzati
  
  // Preconfigured nodes from database
  const userId = useAuth0UserId();
  const { logout, user } = useAuth0();
  const [preconfiguredNodes, setPreconfiguredNodes] = useState<Array<{ id: string; nodeType: string; nodeName: string; label: string }>>([]);
  
  // Import flowApi for database operations
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_isSavingFlow, setIsSavingFlow] = useState(false);
  
  // Load preconfigured nodes from database
  const loadPreconfiguredNodes = async () => {
    if (!userId) {
      console.log('⚠️ FlowStudio: userId non disponibile, non posso caricare nodi preconfigurati');
      setPreconfiguredNodes([]);
      return;
    }

    try {
      console.log('🔍 FlowStudio: Caricamento nodi preconfigurati...');
      console.log('🔍 FlowStudio: userId =', userId);
      
      const result = await promptApi.listPrompts(userId, {
        node_type: 'NodeTesting',
        limit: 100
      });
      
      console.log(`📦 FlowStudio: Trovati ${result.prompts.length} prompt totali`);
      console.log('📦 FlowStudio: Raw result =', JSON.stringify(result, null, 2));
      
      // Filtra solo i fixed_prompt VISIBILI IN STUDIO e crea lista nodi
      const allFixedPrompts = result.prompts.filter(p => {
        const isFixed = p.metadata?.is_fixed_prompt;
        console.log(`  🔍 ${p.name}: is_fixed_prompt = ${isFixed} (type: ${typeof isFixed})`);
        return isFixed === true || isFixed === 'true' || isFixed === 1;
      });
      
      console.log(`📋 FlowStudio: Trovati ${allFixedPrompts.length} nodi fixed_prompt totali`);
      
      allFixedPrompts.forEach(p => {
        const isVisible = p.metadata?.is_visible_in_studio;
        const isVisibleType = typeof isVisible;
        const isVisibleValue = isVisible === true || isVisible === 'true' || isVisible === 1;
        console.log(`  📊 ${p.name}:`, {
          is_visible_in_studio: isVisible,
          type: isVisibleType,
          willShow: isVisibleValue,
          fullMetadata: JSON.stringify(p.metadata, null, 2)
        });
      });
      
      // Filtro robusto: accetta true, "true", 1
      // null, undefined, false, "false", 0 vengono considerati come false
      const fixedPrompts = allFixedPrompts.filter(p => {
        const isVisible = p.metadata?.is_visible_in_studio;
        // Esplicitamente controlla solo i valori "true"
        const willShow = isVisible === true || isVisible === 'true' || isVisible === 1;
        console.log(`  🎯 ${p.name}: isVisible=${isVisible} (${typeof isVisible}), willShow=${willShow}`);
        return willShow;
      });
      
      console.log(`⭐ FlowStudio: Trovati ${fixedPrompts.length} nodi visibili in studio (dopo filtro)`);
      fixedPrompts.forEach(p => {
        console.log(`  ✅ ${p.name} (${p.metadata?.node_type}/${p.metadata?.node_name})`);
      });
      
      const nodes = fixedPrompts.map(p => ({
        id: p.id,
        nodeType: p.metadata?.node_type || '',
        nodeName: p.metadata?.node_name || p.name,
        label: p.name
      }));
      
      console.log(`✅ FlowStudio: ${nodes.length} nodi preconfigurati caricati nello state`);
      console.log('✅ FlowStudio: nodes array =', JSON.stringify(nodes, null, 2));
      
      setPreconfiguredNodes(nodes);
    } catch (error) {
      console.error('❌ FlowStudio: Errore caricamento nodi preconfigurati:', error);
    }
  };

  // Load node configuration from database
  const loadNodeConfiguration = async (nodeType: string, nodeName: string) => {
    if (!userId) return null;

    try {
      const result = await promptApi.listPrompts(userId, {
        node_type: 'NodeTesting',
        limit: 100
      });

      const fixedPrompt = result.prompts.find(
        p => p.metadata?.is_fixed_prompt &&
             p.metadata?.node_type === nodeType &&
             p.metadata?.node_name === nodeName
      );

      if (fixedPrompt) {
        return {
          fixed_prompt: fixedPrompt.content,
          prompt: fixedPrompt.metadata?.variable_prompt || '',
          // Carica TUTTI i parametri dal metadata
          intents: fixedPrompt.metadata?.intents || [],
          max_interactions: fixedPrompt.metadata?.max_interactions,
          model: fixedPrompt.metadata?.model,
          temperature: fixedPrompt.metadata?.temperature,
          max_tokens: fixedPrompt.metadata?.max_tokens,
          input_variable: fixedPrompt.metadata?.input_variable,
          output_variable: fixedPrompt.metadata?.output_variable,
          intents_variable: fixedPrompt.metadata?.intents_variable,
          intent_variable: fixedPrompt.metadata?.intent_variable,
          decision_variable: fixedPrompt.metadata?.decision_variable,
          label: fixedPrompt.metadata?.label
        };
      }
    } catch (error) {
      console.error('Error loading node configuration:', error);
    }
    return null;
  };

  // Load preconfigured nodes on mount
  useEffect(() => {
    if (userId) {
      loadPreconfiguredNodes();
    }
  }, [userId]);
  
  // Extract all available variables from the flow
  const getAvailableVariables = (excludeVariable?: string): string[] => {
    const variables = new Set<string>();
    
    // Extract from INPUT and REPLAY nodes
    nodes.forEach(node => {
      if ((node.type === 'input' || node.type === 'replay' || node.type === 'proposition') && node.data.variableName) {
        // Escludi la variabile specificata (per nodi Input/Replay/Proposition che non devono mostrare la loro stessa variabile)
        if (excludeVariable && node.data.variableName === excludeVariable) {
          return;
        }
        variables.add(node.data.variableName);
      }
      // NOTA: Per Proposition, user_choice_XX cattura TUTTO (sia click su bottoni che testo libero)
      // Non serve più user_text_XX separato
      // Extract from API_CALL nodes (response_variable)
      if (node.type === 'api_call' && node.data.response_variable) {
        variables.add(node.data.response_variable);
      }
      // Extract from API_CALL nodes (extract_fields)
      if (node.type === 'api_call' && node.data.extract_fields) {
        const fields = typeof node.data.extract_fields === 'string' 
          ? JSON.parse(node.data.extract_fields || '{}') 
          : node.data.extract_fields;
        Object.keys(fields).forEach(key => variables.add(key));
      }
      // Extract from ACTION nodes (scan for temp.variable patterns)
      if (node.type === 'action' && node.data.action?.code) {
        const code = node.data.action.code;
        const tempVarMatches = code.match(/temp\.(\w+)\s*=/g);
        if (tempVarMatches) {
          tempVarMatches.forEach((match: string) => {
            const varName = match.match(/temp\.(\w+)/)?.[1];
            if (varName) variables.add(varName);
          });
        }
      }
      // Extract from RAG nodes (variabili numerate basate su nodeNumber)
      if (node.type === 'rag' && node.data.nodeNumber) {
        const num = node.data.nodeNumber.toString().padStart(2, '0');
        variables.add(`rag_response_${num}`);
        variables.add(`rag_query_${num}`);
        variables.add(`rag_sources_${num}`);
      }
      // Extract from llm_qualifier nodes (output_variable and decision_variable)
      if (node.type === 'llm_qualifier') {
        if (node.data.output_variable) {
          variables.add(node.data.output_variable);
        }
        if (node.data.decision_variable) {
          variables.add(node.data.decision_variable);
        }
      }
      // Extract from IntentLLM nodes (output_variable, intents_variable, intent_variable)
      if (node.type === 'IntentLLM') {
        if (node.data.output_variable) {
          variables.add(node.data.output_variable);
        }
        if (node.data.intents_variable) {
          variables.add(node.data.intents_variable);
        }
        // 🔥 BUG FIX: Usa intent_variable direttamente, non derivato!
        if (node.data.intent_variable) {
          variables.add(node.data.intent_variable);  // Es: llm_output_intent_02
        }
      }
      
      // n8n-style nodes output variables
      if (node.type === 'http_request' && node.data.output_variable) {
        variables.add(node.data.output_variable);
        // Add common response fields
        variables.add(`${node.data.output_variable}_status`);
        variables.add(`${node.data.output_variable}_data`);
      }
      if (node.type === 'rss_feed' && node.data.output_variable) {
        variables.add(node.data.output_variable);
        variables.add('rss_item_count');
      }
      if (node.type === 'set' && node.data.variable_name) {
        variables.add(node.data.variable_name);
      }
      if (node.type === 'code' && node.data.output_variable) {
        variables.add(node.data.output_variable);
      }
      if (node.type === 'llm_simple' && node.data.output_variable) {
        variables.add(node.data.output_variable);
      }
      if (node.type === 'aggregate' && node.data.output_variable) {
        variables.add(node.data.output_variable);
      }
      if (node.type === 'slack' && node.data.output_variable) {
        variables.add(node.data.output_variable);
      }
      if (node.type === 'telegram' && node.data.output_variable) {
        variables.add(node.data.output_variable);
      }
      if (node.type === 'google_sheets' && node.data.output_variable) {
        variables.add(node.data.output_variable);
      }
      if (node.type === 'airtable' && node.data.output_variable) {
        variables.add(node.data.output_variable);
      }
      
      // Webhook trigger provides payload variables
      if (node.type === 'webhook_trigger') {
        variables.add('webhook_payload');
        variables.add('webhook_method');
        variables.add('webhook_headers');
        variables.add('webhook_query');
      }
      
      // Schedule trigger provides trigger info
      if (node.type === 'schedule_trigger') {
        variables.add('trigger_time');
        variables.add('trigger_type');
      }
    });
    
    return Array.from(variables).sort();
  };
  
  // Prompt personalizzati (caricati da localStorage)
  const [customGeneratePrompt, setCustomGeneratePrompt] = useState<string | null>(null);
  // const [customPromptifyPrompt, setCustomPromptifyPrompt] = useState<string | null>(null); // Not used anymore
  
  // Libreria prompt utente (descrizioni per generare flow) - now handled by GenerateFlowPanel
  // const [savedUserPrompts, setSavedUserPrompts] = useState<Array<{id: string, name: string, prompt: string, timestamp: string}>>([]);
  
  // Botpress metadata
  const [botSettings, setBotSettings] = useState<any>(null);
  const [flowVariables, setFlowVariables] = useState<Record<string, any[]>>({});
  const [intents, setIntents] = useState<any[]>([]);
  const [knowledgeBase, setKnowledgeBase] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [showMetadataPanel, setShowMetadataPanel] = useState(false);
  
  // Active section (Sidebar)
  const [activeSection, setActiveSection] = useState<'flows' | 'knowledge' | 'indices' | 'services' | 'help'>('flows');
  const [servicesTab, setServicesTab] = useState<'catalog' | 'connections'>('catalog');

  // Knowledge Base indices (for RAG node)
  const [kbIndices, setKbIndices] = useState<Array<{id: string, name: string}>>([]);

  // Node Types con useMemo per performance
  /* const nodeTypes = useMemo(() => ({
    start: StartNode,
    say: SayNode,
    input: InputNode,
    rag: RAGNode,
    condition: ConditionNode,
    case: CaseNode,
    action: ActionNode,
    end: EndNode,
    default: DefaultNode,
  }), []); */

  const onConnect = useCallback(
    (params: Connection) => {
      const sourceNode = nodes.find(n => n.id === params.source);
      
      // Se il source è un nodo CONDITION, aggiungi label T/F
      if (sourceNode?.type === 'condition') {
        const existingEdgesFromCondition = edges.filter(e => e.source === params.source);
        const label = existingEdgesFromCondition.length === 0 ? 'T' : 'F';
        
        setEdges((eds) => addEdge({ 
          ...params, 
          label,
          type: 'default',
          style: { stroke: existingEdgesFromCondition.length === 0 ? '#10b981' : '#ef4444', strokeWidth: 2 },
          labelStyle: { fill: existingEdgesFromCondition.length === 0 ? '#10b981' : '#ef4444', fontWeight: 'bold', fontSize: 16 },
          labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
        }, eds));
      }
      // Se il source è un nodo LLM_QUALIFIER, aggiungi label Yes/No
      else if (sourceNode?.type === 'llm_qualifier') {
        const existingEdgesFromQualifier = edges.filter(e => e.source === params.source);
        const label = existingEdgesFromQualifier.length === 0 ? 'Yes' : 'No';
        
        setEdges((eds) => addEdge({ 
          ...params, 
          label,
          type: 'default',
          style: { stroke: existingEdgesFromQualifier.length === 0 ? '#10b981' : '#ef4444', strokeWidth: 2 },
          labelStyle: { fill: existingEdgesFromQualifier.length === 0 ? '#10b981' : '#ef4444', fontWeight: 'bold', fontSize: 16 },
          labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
        }, eds));
      }
      // Se il source è un nodo CASE, aggiungi label numerica o personalizzata
      else if (sourceNode?.type === 'case' || sourceNode?.type === 'json_condition' || sourceNode?.type === 'llm_case') {
        // Controlla se la connessione è dal handle "altro"
        if (params.sourceHandle === 'altro') {
          // Connessione "altro" - per input non corrispondente ai case
          setEdges((eds) => addEdge({ 
            ...params, 
            label: '✏️ altro',
            type: 'default',
            style: { stroke: '#6b7280', strokeWidth: 2, strokeDasharray: '5,5' },
            labelStyle: { fill: '#6b7280', fontWeight: 'bold', fontSize: 12 },
            labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
          }, eds));
        } else {
          // Connessione case normale
          const existingEdgesFromCase = edges.filter(e => e.source === params.source && e.sourceHandle !== 'altro');
          const caseIndex = existingEdgesFromCase.length;
          
          // Controlla se c'è una label personalizzata per questo case
          const customLabel = sourceNode.data.cases?.[caseIndex]?.label;
          const label = customLabel || `${caseIndex + 1}`;
          
          // Colori arcobaleno per i vari case
          const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444'];
          const color = colors[caseIndex % colors.length];
          
          setEdges((eds) => addEdge({ 
            ...params, 
            label,
            type: 'default',
            style: { stroke: color, strokeWidth: 2 },
            labelStyle: { fill: color, fontWeight: 'bold', fontSize: 14 },
            labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
          }, eds));
        }
      }
      // Se il source è un nodo PROPOSITION, aggiungi label dall'opzione
      else if (sourceNode?.type === 'proposition') {
        // Controlla se la connessione è dal handle "altro"
        if (params.sourceHandle === 'altro') {
          // Connessione "altro" - per testo libero
          setEdges((eds) => addEdge({ 
            ...params, 
            label: '✏️ altro',
            type: 'default',
            style: { stroke: '#6b7280', strokeWidth: 2, strokeDasharray: '5,5' },
            labelStyle: { fill: '#6b7280', fontWeight: 'bold', fontSize: 12 },
            labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
          }, eds));
        } else {
          // Connessione opzione normale
          const existingEdgesFromOptions = edges.filter(e => e.source === params.source && e.sourceHandle !== 'altro');
          const optionIndex = existingEdgesFromOptions.length;
          
          // Controlla se c'è una label personalizzata per questa opzione
          const customLabel = sourceNode.data.options?.[optionIndex]?.label;
          const label = customLabel || `Opzione ${optionIndex + 1}`;
          
          // Colori arcobaleno per le opzioni
          const colors = ['#d946ef', '#a855f7', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];
          const color = colors[optionIndex % colors.length];
          
          setEdges((eds) => addEdge({ 
            ...params, 
            label,
            type: 'default',
            style: { stroke: color, strokeWidth: 2 },
            labelStyle: { fill: color, fontWeight: 'bold', fontSize: 14 },
            labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
          }, eds));
        }
      }
      // 🔶 Nodo RAG - preserva sourceHandle per out_of_scope e max_interactions
      else if (sourceNode?.type === 'rag') {
        const handleId = params.sourceHandle;
        let label = '';
        let color = '#f97316'; // arancione default
        
        if (handleId === 'out_of_scope') {
          label = '🚫 fuori scope';
          color = '#6b7280'; // grigio
        } else if (handleId === 'max_interactions') {
          label = '⏱️ max interazioni';
          color = '#ef4444'; // rosso
        }
        
        console.log(`🔶 RAG onConnect: sourceHandle=${handleId}, label=${label}`);
        
        setEdges((eds) => addEdge({ 
          ...params, 
          label,
          type: 'default',
          style: { stroke: color, strokeWidth: 2 },
          labelStyle: { fill: color, fontWeight: 'bold', fontSize: 12 },
          labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
        }, eds));
      } else {
        setEdges((eds) => addEdge(params, eds));
      }
    },
    [setEdges, nodes, edges]
  );

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
  }, []);

  // Handler per cancellazione edge con DELETE
  const onEdgesDelete = useCallback((edgesToDelete: any[]) => {
    console.log('🗑️ Cancellazione edges:', edgesToDelete);
    setEdges((eds) => eds.filter((e) => !edgesToDelete.find((de) => de.id === e.id)));
  }, [setEdges]);

  // Helper: Remove duplicate edges and ensure unique IDs
  const deduplicateEdges = (edges: any[]) => {
    const seenIds = new Set<string>();
    const uniqueEdges: any[] = [];
    
    edges.forEach((edge, index) => {
      let edgeId = edge.id;
      
      // If ID is duplicate, generate a new unique one
      if (seenIds.has(edgeId)) {
        edgeId = `e-${edge.source}-${edge.target}-${Date.now()}-${index}`;
        console.warn(`⚠️ Duplicate edge ID detected: ${edge.id} → ${edgeId}`);
      }
      
      seenIds.add(edgeId);
      uniqueEdges.push({
        ...edge,
        id: edgeId
      });
    });
    
    return uniqueEdges;
  };

  // Funzione per assegnare numeri univoci ai nodi
  const assignNodeNumbers = (nodeList: Node[]): Node[] => {
    // Ordina i nodi per posizione Y (dall'alto verso il basso), poi per X (da sinistra a destra)
    const sortedNodes = [...nodeList].sort((a, b) => {
      if (a.position.y !== b.position.y) {
        return a.position.y - b.position.y;
      }
      return a.position.x - b.position.x;
    });

    // Costruisci array di nodi aggiornati usando un loop (non .map) per accedere ai nodi già processati
    const updatedNodes: Node[] = [];
    
    for (let index = 0; index < sortedNodes.length; index++) {
      const node = sortedNodes[index];
      const nodeNumber = index + 1;
      const updatedData = { ...node.data, nodeNumber };
      
      // Per i nodi input e replay, usa variableName esistente o assegna default basato sulla posizione
      // Rispetta variableName se già definito (es: da AI generation), altrimenti usa user_input_XX
      if (node.type === 'input' || node.type === 'replay') {
        if (!node.data.variableName) {
          updatedData.variableName = `user_input_${nodeNumber.toString().padStart(2, '0')}`;
        }
      }
      // Per i nodi proposition, usa variableName esistente o assegna default
      if (node.type === 'proposition') {
        if (!node.data.variableName) {
          updatedData.variableName = `user_choice_${nodeNumber.toString().padStart(2, '0')}`;
        }
      }
      // Per i nodi case, llm_case, json_condition: assegna variable basandosi sull'Input più vicino SOPRA
      if (node.type === 'case' || node.type === 'llm_case' || node.type === 'json_condition') {
        // Cerca nell'array GIÀ PROCESSATO (updatedNodes) per avere le variabili aggiornate!
        let foundInputVar = null;
        for (let i = updatedNodes.length - 1; i >= 0; i--) {
          const prevNode = updatedNodes[i];
          if ((prevNode.type === 'input' || prevNode.type === 'replay') && prevNode.data.variableName) {
            foundInputVar = prevNode.data.variableName;
            break; // Prendi il più vicino sopra (già aggiornato!)
          }
        }
        
        // SEMPRE usa l'input trovato sopra, sovrascrivi anche se già esiste
        updatedData.variable = foundInputVar || `user_input_${nodeNumber.toString().padStart(2, '0')}`;
      }
      // Per i nodi IntentLLM, auto-genera llm_output_intent_xx basato sul numero del nodo
      if (node.type === 'IntentLLM') {
        if (!updatedData.intent_variable || !updatedData.intent_variable.startsWith('llm_output_intent_')) {
          updatedData.intent_variable = `llm_output_intent_${nodeNumber.toString().padStart(2, '0')}`;
        }
        // Assicurati che anche intents_variable e output_variable siano generati se non presenti
        if (!updatedData.intents_variable) {
          updatedData.intents_variable = `detected_intents_${nodeNumber.toString().padStart(2, '0')}`;
        }
        if (!updatedData.output_variable) {
          updatedData.output_variable = `llm_response_${nodeNumber.toString().padStart(2, '0')}`;
        }
        if (!updatedData.input_variable) {
          updatedData.input_variable = `user_input_${nodeNumber.toString().padStart(2, '0')}`;
        }
      }
      
      // Per i nodi RAG, auto-genera variabili di output
      if (node.type === 'rag') {
        const num = nodeNumber.toString().padStart(2, '0');
        if (!updatedData.output_variable) {
          updatedData.output_variable = `rag_response_${num}`;
        }
        if (!updatedData.query_variable) {
          updatedData.query_variable = `rag_query_${num}`;
        }
        if (!updatedData.sources_variable) {
          updatedData.sources_variable = `rag_sources_${num}`;
        }
        // Default max_interactions se non impostato
        if (!updatedData.max_interactions) {
          updatedData.max_interactions = 5;
        }
      }
      
      // Aggiungi il nodo aggiornato all'array
      updatedNodes.push({
        ...node,
        data: updatedData,
      });
    }
    
    // Restituisci l'array di nodi aggiornati
    return updatedNodes;
  };

  // Aggiorna i numeri dei nodi quando cambiano (solo quando cambia il numero o le posizioni)
  const nodesKey = useMemo(() => {
    return nodes.map(n => `${n.id}:${n.position.x},${n.position.y}`).join('|');
  }, [nodes]);
  
  useEffect(() => {
    if (nodes.length > 0) {
      const updatedNodes = assignNodeNumbers(nodes);
      // Controlla se ci sono differenze prima di aggiornare (evita loop infiniti)
      const hasChanges = updatedNodes.some((node, index) => {
        const original = nodes[index];
        if (!original) return false;
        const originalNumber = original.data.nodeNumber;
        const newNumber = node.data.nodeNumber;
        const originalVar = original.type === 'input' ? original.data.variableName : null;
        const newVar = node.type === 'input' ? node.data.variableName : null;
        
        return originalNumber !== newNumber || originalVar !== newVar;
      });
      
      if (hasChanges) {
        setNodes(updatedNodes);
      }
    }
  }, [nodesKey]); // Solo quando cambiano ID o posizioni

  // Add Node from Palette
  const addNode = async (type: string, preconfiguredNodeName?: string) => {
    // Mappa speciale per label personalizzati
    const labelMap: Record<string, string> = {
      'llm_case': 'LLM-Case',
      'llm_condition': 'LLM-Condition',
      'json_condition': 'LLM-Condition', // per retrocompatibilità
      'llm_qualifier': 'llm_qualifier',
    };
    
    // Determina il label: usa la mappa se esiste, altrimenti formatta il type
    const label = labelMap[type] || (type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, '-'));
    
    const baseData: any = { label };
    
    // Set default values for specific node types
    let nodeData: any = { ...baseData };
    if (type === 'replay') {
      nodeData = { ...baseData, response: '', question: '' };
    }
    
    // Se è un nodo preconfigurato, carica la configurazione completa dal database
    if (preconfiguredNodeName && userId) {
      const config = await loadNodeConfiguration(type, preconfiguredNodeName);
      if (config) {
        // Applica TUTTI i parametri salvati
        nodeData = {
          ...nodeData,
          prompt: config.prompt || '',
          fixed_prompt: config.fixed_prompt || '',
          nodeName: preconfiguredNodeName,
          // Parametri specifici per IntentLLM
          intents: config.intents || [],
          max_interactions: config.max_interactions,
          input_variable: config.input_variable,
          output_variable: config.output_variable,
          intents_variable: config.intents_variable,
          // Parametri specifici per llm_qualifier
          decision_variable: config.decision_variable,
          // Parametri API comuni
          model: config.model,
          temperature: config.temperature,
          max_tokens: config.max_tokens,
          // Label se presente
          label: config.label || nodeData.label
        };
      }
    }
    
    // Non impostiamo variableName qui - verrà assegnato da assignNodeNumbers
    
    const newNode: Node = {
      id: `${type}-${Date.now()}`,
      type,
      position: { x: Math.random() * 500 + 100, y: Math.random() * 300 + 100 },
      data: nodeData,
    };
    setNodes((nds) => {
      const updated = [...nds, newNode];
      return assignNodeNumbers(updated);
    });
  };

  // Handle Flow Generation from Service
  const handleServiceFlowGeneration = (flowData: any) => {
    if (flowData && flowData.nodes && flowData.edges) {
      // Add generated nodes and edges to existing flow
      const uniqueEdges = deduplicateEdges(flowData.edges);
      setNodes((nds) => [...nds, ...flowData.nodes]);
      setEdges((eds) => [...eds, ...uniqueEdges]);
      
      console.log(`✅ Generated flow from service: ${flowData.node_count} nodes, ${flowData.edge_count} edges`);
    }
  };

  // Delete Selected Node
  const deleteNode = () => {
    if (selectedNode) {
      setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
      setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
      setSelectedNode(null);
    }
  };

  // Clear Canvas
  const clearCanvas = () => {
    if (confirm('⚠️ Vuoi eliminare TUTTI i nodi e collegamenti?')) {
      setNodes([]);
      setEdges([]);
      setSelectedNode(null);
      alert('✅ Canvas pulito!');
    }
  };

  // Validate Edge Labels Match Node Options
  const validateEdgeLabels = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    nodes.forEach(node => {
      // Trova tutti gli edge uscenti da questo nodo (escluso "altro")
      const outgoingEdges = edges.filter(e => e.source === node.id && e.sourceHandle !== 'altro');
      
      if (node.type === 'proposition') {
        const options = (node.data.options || []) as { label: string }[];
        const optionLabels = options.map(o => o.label).filter(Boolean);
        
        // Verifica che ogni opzione abbia un edge corrispondente
        optionLabels.forEach((optLabel, idx) => {
          const expectedEdge = outgoingEdges[idx];
          if (expectedEdge && expectedEdge.label !== optLabel) {
            errors.push(`⚠️ Proposition #${node.data.nodeNumber || '?'}: Opzione "${optLabel}" ha edge con label "${expectedEdge.label || 'vuota'}"`);
          }
        });
        
        // Verifica che non ci siano edge in più delle opzioni
        if (outgoingEdges.length > optionLabels.length) {
          errors.push(`⚠️ Proposition #${node.data.nodeNumber || '?'}: ${outgoingEdges.length} rami ma solo ${optionLabels.length} opzioni definite`);
        }
      }
      
      if (node.type === 'case') {
        const cases = (node.data.cases || []) as { label: string }[];
        const caseLabels = cases.map(c => c.label).filter(Boolean);
        
        caseLabels.forEach((caseLabel, idx) => {
          const expectedEdge = outgoingEdges[idx];
          if (expectedEdge && expectedEdge.label !== caseLabel) {
            errors.push(`⚠️ Case #${node.data.nodeNumber || '?'}: Caso "${caseLabel}" ha edge con label "${expectedEdge.label || 'vuota'}"`);
          }
        });
        
        if (outgoingEdges.length > caseLabels.length) {
          errors.push(`⚠️ Case #${node.data.nodeNumber || '?'}: ${outgoingEdges.length} rami ma solo ${caseLabels.length} casi definiti`);
        }
      }
      
      if (node.type === 'IntentLLM') {
        // IntentLLM ha SOLO 2 uscite fisse (non 1 per intent):
        // 1. Intent rilevato (qualsiasi)
        // 2. No intent (max interazioni)
        // Gli intents specifici sono gestiti dalla variabile llm_output_intent_XX
        // NON validare edge vs intents per IntentLLM!
        
        // Valida solo che abbia 2 edge
        if (outgoingEdges.length !== 2) {
          errors.push(`⚠️ IntentLLM #${node.data.nodeNumber || '?'}: Deve avere esattamente 2 uscite (intent rilevato / no intent), trovate ${outgoingEdges.length}`);
        }
      }
    });
    
    return { valid: errors.length === 0, errors };
  };

  // Helper function to get fixed edges WITHOUT modifying state
  const getFixedEdges = (currentEdges: typeof edges): typeof edges => {
    const newEdges = [...currentEdges];
      
      nodes.forEach(node => {
        const outgoingEdges = newEdges.filter(e => e.source === node.id && e.sourceHandle !== 'altro');
        
        // 🔥 CRITICAL: Ordina gli edge per POSIZIONE TARGET (top-to-bottom, left-to-right)
        // Questo garantisce che case[0] = primo nodo collegato VISIVAMENTE, non cronologicamente!
        const sortedOutgoingEdges = [...outgoingEdges].sort((a, b) => {
          const targetNodeA = nodes.find(n => n.id === a.target);
          const targetNodeB = nodes.find(n => n.id === b.target);
          
          if (!targetNodeA || !targetNodeB) return 0;
          
          // Ordina per posizione Y (top-to-bottom), poi X (left-to-right)
          if (targetNodeA.position.y !== targetNodeB.position.y) {
            return targetNodeA.position.y - targetNodeB.position.y;
          }
          return targetNodeA.position.x - targetNodeB.position.x;
        });
        
        if (node.type === 'proposition') {
          const options = (node.data.options || []) as { label: string }[];
          sortedOutgoingEdges.forEach((edge, idx) => {
            if (idx < options.length && options[idx].label) {
              const edgeIndex = newEdges.findIndex(e => e.id === edge.id);
              if (edgeIndex >= 0) {
                newEdges[edgeIndex] = { ...newEdges[edgeIndex], label: options[idx].label };
              }
            }
          });
        }
        
        if (node.type === 'case') {
          const cases = (node.data.cases || []) as { label: string }[];
          sortedOutgoingEdges.forEach((edge, idx) => {
            if (idx < cases.length && cases[idx].label) {
              const edgeIndex = newEdges.findIndex(e => e.id === edge.id);
              if (edgeIndex >= 0) {
                newEdges[edgeIndex] = { ...newEdges[edgeIndex], label: cases[idx].label };
              }
            }
          });
        }
        
        // IntentLLM: NON modificare le label degli edge!
        // IntentLLM ha SOLO 2 uscite fisse (intent trovato / no intent)
        // Gli intents specifici sono gestiti dalla variabile llm_output_intent_XX
        // e processati da un LLM-Case successivo.
        // Le label degli edge devono rimanere quelle impostate in onConnect (semplici, generiche)
      });
      
      return newEdges;
  };
  
  // Auto-fix Edge Labels to Match Node Options (modifies state)
  const autoFixEdgeLabels = () => {
    setEdges(currentEdges => getFixedEdges(currentEdges));
  };

  // Toggle Simulator with Validation
  const handleToggleSimulator = () => {
    if (!showSimulator) {
      // Prima di aprire, valida gli edge
      const validation = validateEdgeLabels();
      if (!validation.valid) {
        const message = `🔍 Trovate incongruenze tra opzioni e rami:\n\n${validation.errors.join('\n')}\n\n` +
          `Vuoi:\n` +
          `• OK = Correggi automaticamente le label dei rami\n` +
          `• Annulla = Continua comunque senza correggere`;
        
        if (confirm(message)) {
          autoFixEdgeLabels();
          alert('✅ Label dei rami aggiornate! Ora corrispondono alle opzioni.');
        }
      }
    }
    setShowSimulator(!showSimulator);
  };

  // Update Node Data
  const updateNodeData = (field: string, value: any) => {
    if (!selectedNode) return;
    setNodes((nds) =>
      nds.map((n) =>
        n.id === selectedNode.id
          ? { ...n, data: { ...n.data, [field]: value } }
          : n
      )
    );
    setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, [field]: value } });
  };

  // Import Botpress Flow (supporta .bpz e .json)
  const importBotpressFlow = async (file: File) => {
    try {
      const result = await importBotpressFile(file);
      
      const fixedNodes = fixNodePositions(result.nodes, result.edges);
      const uniqueEdges = deduplicateEdges(result.edges);
      // Assegna numeri ai nodi importati
      const numberedNodes = assignNodeNumbers(fixedNodes);
      setNodes(numberedNodes);
      setEdges(uniqueEdges);
      
      // Salva metadata
      if (result.botSettings) setBotSettings(result.botSettings);
      if (result.flowVariables) setFlowVariables(result.flowVariables);
      if (result.intents) setIntents(result.intents);
      if (result.knowledgeBase) setKnowledgeBase(result.knowledgeBase);
      if (result.documents) setDocuments(result.documents);
      
      const flowList = result.flowNames.length > 1 ? `\nFlow: ${result.flowNames.join(', ')}` : '';
      const metaInfo = [
        result.botSettings ? `Bot: ${result.botSettings.botId.substring(0, 8)}...` : '',
        result.intents && result.intents.length > 0 ? `${result.intents.length} intents` : '',
        result.documents && result.documents.length > 0 ? `${result.documents.length} docs` : '',
      ].filter(Boolean).join(', ');
      
      alert(`✅ Importati ${result.nodes.length} nodi da Botpress!${flowList}\n${metaInfo}`);
      setShowMetadataPanel(true); // Mostra automaticamente il pannello metadata
    } catch (error: any) {
      console.error('Errore import Botpress:', error);
      alert(`❌ ${error.message || 'Errore nel file Botpress'}`);
    }
  };

  // Export PIGNA Flow (formato completo per LLM)
  const exportFlow = () => {
    // Chiedi il nome del file all'utente
    const fileName = prompt('💾 Nome del file da salvare:', flowName);
    if (!fileName || !fileName.trim()) {
      return; // Annullato o nome vuoto
    }

    const cleanFileName = fileName.trim().replace(/[^a-zA-Z0-9_-]/g, '_'); // Pulisci caratteri non validi per nomi file
    
    // 🔥 CRITICAL: Fix edge labels BEFORE exporting per garantire coerenza!
    const fixedEdges = getFixedEdges(edges);
    
    const flowData = {
      id: flowName,
      version: '1.0',
      metadata: {
        created: new Date().toISOString(),
        engine: 'PIGNA Flow Studio',
        format: 'pigna-flow-v1',
      },
      nodes: nodes.map(n => ({ 
        id: n.id, 
        type: n.type, 
        data: n.data, 
        position: n.position 
      })),
      edges: fixedEdges.map(e => ({ 
        id: e.id, 
        source: e.source, 
        target: e.target,
        sourceHandle: e.sourceHandle,  // 🔥 CRITICAL: preserve which output is connected
        targetHandle: e.targetHandle,  // 🔥 CRITICAL: preserve which input is connected
        label: e.label,
        type: e.type,
        style: e.style,
        animated: e.animated,
      })),
    };
    const blob = new Blob([JSON.stringify(flowData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${cleanFileName}.pigna-flow.json`;
    a.click();
    URL.revokeObjectURL(url); // Pulisci l'URL dopo il download
  };

  // Validate and Sync Flow
  const validateAndSyncFlow = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Check 1: Almeno un nodo START
    const startNodes = nodes.filter(n => n.type === 'start');
    if (startNodes.length === 0) {
      errors.push('❌ Manca il nodo START');
    }
    if (startNodes.length > 1) {
      errors.push('⚠️ Più di un nodo START trovato (ne serve solo uno)');
    }
    
    // Check 2: Nodi isolati (senza collegamenti)
    const connectedNodeIds = new Set<string>();
    edges.forEach(e => {
      connectedNodeIds.add(e.source);
      connectedNodeIds.add(e.target);
    });
    
    const isolatedNodes = nodes.filter(n => 
      n.type !== 'start' && n.type !== 'end' && !connectedNodeIds.has(n.id)
    );
    
    if (isolatedNodes.length > 0) {
      errors.push(`⚠️ ${isolatedNodes.length} nodo/i isolato/i (senza collegamenti)`);
    }
    
    // Check 3: Nodi senza uscita (escluso END)
    const nodesWithOutput = nodes.filter(n => n.type !== 'end');
    nodesWithOutput.forEach(n => {
      const hasOutgoingEdge = edges.some(e => e.source === n.id);
      if (!hasOutgoingEdge && n.type !== 'end') {
        errors.push(`⚠️ Nodo "${n.data.label || n.id}" senza collegamenti in uscita`);
      }
    });
    
    return { valid: errors.length === 0, errors };
  };

  // Save to Database (con fallback a localStorage se non autenticato)
  const saveFlow = async () => {
    const newName = prompt('💾 Nome del flow:', flowName);
    if (!newName) return; // Annullato
    
    // 🔥 CRITICAL: Fix edge labels BEFORE saving per garantire coerenza!
    const fixedEdges = getFixedEdges(edges);
    
    const flowData = {
      id: newName,
      version: '1.0',
      metadata: {
        created: new Date().toISOString(),
        engine: 'PIGNA Flow Studio',
        format: 'pigna-flow-v1',
      },
      nodes: nodes.map(n => ({ 
        id: n.id, 
        type: n.type, 
        data: n.data, 
        position: n.position 
      })),
      edges: fixedEdges.map(e => ({ 
        id: e.id, 
        source: e.source, 
        target: e.target,
        sourceHandle: e.sourceHandle,  // 🔥 CRITICAL: preserve which output is connected
        targetHandle: e.targetHandle,  // 🔥 CRITICAL: preserve which input is connected
        label: e.label,
        type: e.type,
        style: e.style,
        animated: e.animated,
      })),
      timestamp: new Date().toISOString(),
    };

    // Se autenticato, salva nel database
    if (userId) {
      setIsSavingFlow(true);
      try {
        await flowApi.createFlow({
          name: newName,
          flow_data: flowData,
          version: '1.0',
        }, userId);
        setFlowName(newName);
        alert(`✅ Flow "${newName}" salvato nel database!`);
      } catch (error: any) {
        console.error('Errore salvataggio flow:', error);
        alert(`❌ Errore salvataggio: ${error.message || 'Errore sconosciuto'}`);
        // Fallback a localStorage
        localStorage.setItem(`pigna-flow-${newName}`, JSON.stringify(flowData));
        setFlowName(newName);
        alert(`⚠️ Flow salvato in localStorage (fallback)`);
      } finally {
        setIsSavingFlow(false);
      }
    } else {
      // Fallback a localStorage se non autenticato
      localStorage.setItem(`pigna-flow-${newName}`, JSON.stringify(flowData));
      setFlowName(newName);
      alert(`✅ Flow "${newName}" salvato in localStorage!`);
    }
  };

  // Load from LocalStorage - Apre pannello libreria
  const loadFlow = () => {
    setShowFlowLibrary(true);
  };

  // Handler per caricare flow dalla libreria
  // Funzione per riposizionare nodi con spacing professionale
  const fixNodePositions = (nodes: any[], edges: any[]) => {
    if (!nodes || nodes.length === 0) return nodes;
    
    // Crea mappa id -> nodo
    const nodeMap: any = {};
    nodes.forEach(node => nodeMap[node.id] = node);
    
    // Crea grafo delle dipendenze
    const graph: any = {};
    const inDegree: any = {};
    nodes.forEach(node => inDegree[node.id] = 0);
    
    edges.forEach((edge: any) => {
      const source = edge.source;
      const target = edge.target;
      if (source && target) {
        if (!graph[source]) graph[source] = [];
        graph[source].push(target);
        inDegree[target] = (inDegree[target] || 0) + 1;
      }
    });
    
    // Topological sort
    const queue = Object.keys(inDegree).filter(id => inDegree[id] === 0);
    const sortedIds: string[] = [];
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      sortedIds.push(current);
      
      (graph[current] || []).forEach((neighbor: string) => {
        inDegree[neighbor]--;
        if (inDegree[neighbor] === 0) {
          queue.push(neighbor);
        }
      });
    }
    
    // Aggiungi nodi non raggiunti (cicli)
    nodes.forEach(node => {
      if (!sortedIds.includes(node.id)) {
        sortedIds.push(node.id);
      }
    });
    
    // Riposiziona con spacing 200px
    const X_CENTER = 400;
    const Y_START = 100;
    const Y_SPACING = 200;
    
    return nodes.map(node => {
      const index = sortedIds.indexOf(node.id);
      return {
        ...node,
        position: {
          x: X_CENTER,
          y: Y_START + (index * Y_SPACING)
        }
      };
    });
  };

  const handleLoadFlowFromLibrary = (flowData: any) => {
    const fixedNodes = fixNodePositions(flowData.nodes || [], flowData.edges || []);
    const uniqueEdges = deduplicateEdges(flowData.edges || []);
    // Assegna numeri ai nodi caricati
    const numberedNodes = assignNodeNumbers(fixedNodes);
    setNodes(numberedNodes);
    setEdges(uniqueEdges);
    setFlowName(flowData.id || 'loaded-flow');
    alert(`✅ Flow "${flowData.id}" caricato e riposizionato!`);
  };

  // 🪄 Import flow generato dall'AI Assistant
  const handleImportAIFlow = (flowData: any) => {
    console.log('🪄 Importing AI-generated flow:', flowData);
    
    // Estrai nodi e edges
    const rawNodes = flowData.nodes || [];
    const rawEdges = flowData.edges || [];
    
    if (rawNodes.length === 0) {
      alert('⚠️ Il flow generato non contiene nodi!');
      return;
    }
    
    // Riposiziona i nodi
    const fixedNodes = fixNodePositions(rawNodes, rawEdges);
    const uniqueEdges = deduplicateEdges(rawEdges);
    
    // Assegna numeri ai nodi
    const numberedNodes = assignNodeNumbers(fixedNodes);
    
    // Imposta nel canvas
    setNodes(numberedNodes);
    setEdges(uniqueEdges);
    setFlowName(flowData.flow_name || flowData.id || 'ai-generated-flow');
    
    // Chiudi l'assistant per vedere il canvas
    setShowAIAssistant(false);
    
    console.log('✅ AI Flow imported:', { nodes: numberedNodes.length, edges: uniqueEdges.length });
  };

  // Legacy functions - now handled by GenerateFlowPanel
  /*
  const saveUserPrompt = (promptText: string) => {
    const name = prompt('💾 Nome per questo prompt:', `Prompt ${savedUserPrompts.length + 1}`);
    if (!name) return;
    
    const newPrompt = {
      id: `up-${Date.now()}`,
      name: name.trim(),
      prompt: promptText,
      timestamp: new Date().toISOString()
    };
    
    const updated = [...savedUserPrompts, newPrompt];
    setSavedUserPrompts(updated);
    localStorage.setItem('pigna-flow-user-prompts', JSON.stringify(updated));
    alert(`✅ Prompt "${name}" salvato!`);
  };
  
  const deleteUserPrompt = (id: string) => {
    const prompt = savedUserPrompts.find(p => p.id === id);
    if (!prompt || !confirm(`⚠️ Eliminare "${prompt.name}"?`)) return;
    
    const updated = savedUserPrompts.filter(p => p.id !== id);
    setSavedUserPrompts(updated);
    localStorage.setItem('pigna-flow-user-prompts', JSON.stringify(updated));
    alert(`✅ Prompt eliminato!`);
  };
  */

  // Generate All ACTION Code
  const generateAllActionCode = async () => {
    const actionNodes = nodes.filter(n => n.type === 'action' && !n.data.action?.code);
    
    if (actionNodes.length === 0) {
      alert('✅ Tutti i nodi ACTION hanno già il codice!');
      return;
    }
    
    if (!confirm(`🤖 Generare codice per ${actionNodes.length} nodi ACTION?`)) {
      return;
    }
    
    // Raccogli tutte le variabili disponibili
    const availableVars = nodes
      .filter(n => n.type === 'input' && n.data.variableName)
      .map(n => n.data.variableName);
    
    let successCount = 0;
    
    for (const node of actionNodes) {
      try {
        const description = node.data.action?.description || node.data.label || 'Elaborazione dati';
        
        const response = await fetch(`${API_URL}/flow/generate-action-code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            description,
            available_variables: availableVars
          })
        });
        
        if (!response.ok) throw new Error('Errore generazione');
        
        const data = await response.json();
        
        if (data.success && data.code) {
          // Aggiorna il nodo con il codice generato
          setNodes(prevNodes => prevNodes.map(n => {
            if (n.id === node.id) {
              return {
                ...n,
                data: {
                  ...n.data,
                  action: {
                    ...n.data.action,
                    code: data.code
                  }
                }
              };
            }
            return n;
          }));
          successCount++;
        }
      } catch (error: any) {
        console.error(`Errore generazione codice per ${node.id}:`, error);
      }
    }
    
    alert(`✅ Codice generato per ${successCount}/${actionNodes.length} nodi ACTION!`);
  };

  // Generate Flow from Text (LLM)
  // Apri pannello Genera Flow
  const generateFlow = () => {
    setShowGeneratePanel(true);
  };
  
  // Funzione separata per la generazione effettiva
  const performGeneration = async (description: string) => {
    
    try {
      // Prepara il body con il prompt personalizzato se disponibile
      const requestBody: any = {
        description: description.trim(),
        llm_provider: 'groq',
        model: 'openai/gpt-oss-120b'
      };
      
      // Aggiungi custom_system_prompt se disponibile
      if (customGeneratePrompt) {
        requestBody.custom_system_prompt = customGeneratePrompt;
      }
      
      const response = await fetch(`${API_URL}/flow/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Errore generazione flow');
      }
      
      const result = await response.json();
      const generatedFlow = result.flow;
      
      // Carica il flow generato con spacing fix
      const fixedNodes = fixNodePositions(generatedFlow.nodes, generatedFlow.edges);
      const uniqueEdges = deduplicateEdges(generatedFlow.edges);
      setNodes(fixedNodes);
      setEdges(uniqueEdges);
      setFlowName(generatedFlow.id || 'generated-flow');
      
      alert(
        `✅ Flow generato con successo!\n\n` +
        `📊 ${generatedFlow.nodes.length} nodi\n` +
        `🔗 ${generatedFlow.edges.length} collegamenti`
      );
    } catch (error: any) {
      console.error('Errore generazione flow:', error);
      alert(`❌ Errore durante la generazione:\n\n${error.message}`);
    }
  };

  // Handler per Sync
  const handleValidateSync = () => {
    const { valid, errors } = validateAndSyncFlow();
    
    if (valid) {
      alert('✅ Flow valido!\n\n📊 Statistiche:\n' +
        `• ${nodes.length} nodi\n` +
        `• ${edges.length} collegamenti\n` +
        `• Nessun errore rilevato`);
    } else {
      alert('⚠️ Problemi rilevati:\n\n' + errors.join('\n') + 
        '\n\n💡 Correggi questi problemi prima di testare il flow.');
    }
  };

  // Promptify: genera descrizione operativa da obiettivo tramite Groq 120B
  // Apri pannello Promptify
  const openPromptify = () => {
    setShowPromptifyPanel(true);
  };

  // Modify: modifica flow esistente con AI
  const openModify = () => {
    setShowModifyPanel(true);
  };

  // Carica i prompt all'avvio del componente
  // Load KB indices
  const loadKbIndices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/flow/kb/list-indices`);
      const data = await response.json();
      
      // Transform indices array to match expected format (without chunks_count)
      const formattedIndices = (data.indices || []).map((index: any) => ({
        id: index.id || index,  // Se è oggetto usa index.id, se è stringa usa index
        name: index.name || index  // Se è oggetto usa index.name, se è stringa usa index
      }));
      
      setKbIndices(formattedIndices);
      console.log(`✅ Caricati ${formattedIndices.length} indici KB`);
    } catch (error) {
      console.error('❌ Errore caricamento indici KB:', error);
    }
  };

  useEffect(() => {
    const savedGenerate = localStorage.getItem('pigna-flow-generate-prompt');
    // const savedPromptify = localStorage.getItem('pigna-flow-promptify-prompt');
    if (savedGenerate) setCustomGeneratePrompt(savedGenerate);
    // if (savedPromptify) setCustomPromptifyPrompt(savedPromptify);
    
    // Load KB indices for RAG node
    loadKbIndices();
    
    // Legacy: user prompts now handled by GenerateFlowPanel
    /*
    const savedUserPromptsStr = localStorage.getItem('pigna-flow-user-prompts');
    if (savedUserPromptsStr) {
      try {
        setSavedUserPrompts(JSON.parse(savedUserPromptsStr));
      } catch (e) {
        console.error('Errore caricamento prompt utente:', e);
      }
    }
    */
  }, []);

  // Handler per aggiornamento prompt dal SettingsPanel
  const handlePromptUpdate = (generatePrompt: string, _promptifyPrompt: string) => {
    setCustomGeneratePrompt(generatePrompt);
    // setCustomPromptifyPrompt(promptifyPrompt); // Not used anymore
  };

  // Gestione "Usa in Genera" da Promptify
  const [promptToGenerate, setPromptToGenerate] = useState<string>('');
  
  const handleUsePromptInGenerate = (generatedPrompt: string) => {
    setPromptToGenerate(generatedPrompt);
    setShowPromptifyPanel(false);
    setShowGeneratePanel(true);
  };

  // Gestione caricamento chat dalla cronologia
  const handleLoadChatFromHistory = (chat: any) => {
    // Converti i timestamp delle stringhe in oggetti Date
    const messagesWithDates = chat.messages.map((m: any) => ({
      ...m,
      timestamp: new Date(m.timestamp)
    }));
    setLoadedChatMessages(messagesWithDates);
    setShowChatHistory(false);
    setShowSimulator(true);
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Left Sidebar - App Sections */}
      <Sidebar 
        activeSection={activeSection} 
        onSectionChange={setActiveSection}
        onSettingsClick={() => {
          // PRIMA torna a 'flows' per assicurarti che il pannello sia visibile
          setActiveSection('flows');
          // POI apri il pannello Settings
          setShowSettings(true);
        }}
      />
      
      <div className="flex flex-col flex-1 min-w-0">
        {/* Header */}
        <FlowHeader
          flowName={flowName}
          onFlowNameChange={setFlowName}
          onSave={saveFlow}
          onExport={exportFlow}
          onLoad={loadFlow}
          onImport={importBotpressFlow}
          onGenerate={generateFlow}
          onPromptify={openPromptify}
          onModify={openModify}
          onGenerateAllCode={generateAllActionCode}
          nodeCount={nodes.length}
          edgeCount={edges.length}
          showProperties={showProperties}
          onToggleProperties={() => setShowProperties(!showProperties)}
          showPalette={showPalette}
          onTogglePalette={() => setShowPalette(!showPalette)}
          onClearCanvas={clearCanvas}
          showSimulator={showSimulator}
          onToggleSimulator={handleToggleSimulator}
          onValidateSync={handleValidateSync}
          hasMetadata={botSettings !== null || Object.keys(flowVariables).length > 0 || intents.length > 0 || documents.length > 0}
          onShowMetadata={() => setShowMetadataPanel(true)}
          showSettings={showSettings}
          onToggleSettings={() => setShowSettings(!showSettings)}
          onGenerateEmbed={() => setShowEmbedGenerator(true)}
          onShowScheduler={() => setShowSchedulerPanel(true)}
          onShowWebhook={() => setShowWebhookPanel(true)}
          onShowCredentials={() => setShowCredentialsPanel(true)}
          onShowExecutions={() => setShowExecutionsPanel(true)}
          onShowTemplates={() => setShowTemplatesPanel(true)}
          onShowVersioning={() => setShowVersioningPanel(true)}
          onShowAIAssistant={() => setShowAIAssistant(true)}
          onShowWorkspaceSettings={() => setShowWorkspaceSettings(true)}
          onLogout={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        />

        {/* Flows Section */}
        {activeSection === 'flows' && (
          <div className="flex flex-1 min-w-0 overflow-hidden">
        {/* Left Sidebar - Node Palette (Collapsibile) */}
        <div 
          className={`${showPalette ? 'w-64' : 'w-16'} bg-white border-r-4 border-gray-300 flex flex-col shadow-2xl transition-all duration-300 ease-in-out`}
        >
          <div className="p-4 border-b-2 border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center justify-between">
              <h2 className={`text-lg font-bold text-gray-900 ${showPalette ? 'block' : 'hidden'}`}>📦 Nodi</h2>
              {showPalette && userId && (
                <div className="flex gap-1">
                  <button
                    onClick={async () => {
                      console.log('🔄 Refresh nodi preconfigurati...');
                      loadPreconfiguredNodes();
                    }}
                    className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded"
                    title="Ricarica nodi preconfigurati"
                  >
                    🔄
                  </button>
                  <button
                    onClick={async () => {
                      console.log('🐛 DEBUG: Chiamata endpoint debug...');
                      try {
                        const debug = await promptApi.debugFixedPrompts(userId);
                        console.log('🐛 DEBUG RESULT:', JSON.stringify(debug, null, 2));
                        alert(`Debug: ${debug.fixed_prompts_count} fixed prompts trovati. Controlla la console per i dettagli.`);
                      } catch (error: any) {
                        console.error('❌ Errore debug:', error);
                        alert(`Errore debug: ${error.message}`);
                      }
                    }}
                    className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded"
                    title="Debug: mostra tutti i fixed prompts nel database"
                  >
                    🐛
                  </button>
                </div>
              )}
            </div>
            <div className={`text-2xl ${showPalette ? 'hidden' : 'block text-center'}`}>📦</div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {/* Standard Node Templates */}
            {nodeTemplates.map((template) => (
              <button
                key={template.type}
                onClick={() => addNode(template.type)}
                className={`w-full ${template.color} hover:opacity-80 text-white ${showPalette ? 'px-4 py-3' : 'px-2 py-2'} rounded-xl font-semibold ${showPalette ? 'text-left' : 'text-center text-xs'} transition-all shadow-lg hover:shadow-xl`}
                title={!showPalette ? template.label : ''}
              >
                {showPalette ? template.label : template.label.split(' ')[0]}
              </button>
            ))}
            
            {/* Specialized Nodes Section - Collapsible */}
            {showPalette && (
              <div className="mt-3">
                <button
                  onClick={() => setShowSpecializedNodes(!showSpecializedNodes)}
                  className="w-full bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white px-4 py-2.5 rounded-xl font-semibold text-left transition-all shadow-md flex items-center justify-between"
                >
                  <span>🔧 Specializzati</span>
                  <span className="text-lg">{showSpecializedNodes ? '▼' : '▶'}</span>
                </button>
                
                {showSpecializedNodes && (
                  <div className="mt-2 space-y-2 pl-2 border-l-2 border-gray-400">
                    {specializedNodeTemplates.map((template) => (
                      <button
                        key={template.type}
                        onClick={() => addNode(template.type)}
                        className={`w-full ${template.color} hover:opacity-80 text-white px-3 py-2 rounded-lg font-medium text-left text-sm transition-all shadow-md hover:shadow-lg`}
                        title={template.label}
                      >
                        {template.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Preconfigured Nodes Section - SEMPRE VISIBILE quando palette è aperta */}
            {showPalette && (
              <div className="mt-4">
                <div className="pt-4 pb-2 border-t-2 border-gray-300 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-gray-700 px-2">⭐ Preconfigurati</h3>
                  {preconfiguredNodes.length > 0 && (
                    <div className="text-xs text-gray-500 px-2">({preconfiguredNodes.length})</div>
                  )}
                </div>
                {(() => {
                  console.log('🎨 Rendering sezione Preconfigurati:', {
                    showPalette,
                    preconfiguredNodesLength: preconfiguredNodes.length,
                    preconfiguredNodes: JSON.stringify(preconfiguredNodes, null, 2)
                  });
                  return null;
                })()}
                {preconfiguredNodes.length > 0 ? (
                  <>
                    {preconfiguredNodes.map((node, idx) => {
                      console.log(`🎨 Rendering nodo preconfigurato ${idx}:`, node);
                      return (
                        <button
                          key={node.id || `node-${idx}`}
                          onClick={() => {
                            console.log('🎯 Click su nodo preconfigurato:', node);
                            addNode(node.nodeType, node.nodeName);
                          }}
                          className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-80 text-white px-4 py-2 rounded-xl font-semibold text-left text-sm transition-all shadow-lg hover:shadow-xl mb-2"
                          title={`${node.label} (${node.nodeType}/${node.nodeName})`}
                        >
                          ⭐ {node.label}
                        </button>
                      );
                    })}
                  </>
                ) : (
                  <div className="px-3 py-4 text-xs text-gray-500 italic bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 mb-2">
                    <div className="font-semibold text-gray-700 mb-1">Nessun nodo preconfigurato</div>
                    {userId ? (
                      <div>
                        Salva un nodo in <strong>NodeTesting</strong> con il checkbox <strong>"⭐ Visibile in Studio"</strong> selezionato.
                        <br />
                        <button
                          onClick={() => {
                            console.log('🔄 Refresh manuale nodi preconfigurati...');
                            loadPreconfiguredNodes();
                          }}
                          className="mt-2 px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-xs"
                        >
                          🔄 Ricarica
                        </button>
                      </div>
                    ) : (
                      <div>Autenticati per vedere i nodi preconfigurati.</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      {/* Main Canvas */}
      <div className="flex-1 min-w-0 flex flex-col">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onEdgesDelete={onEdgesDelete}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.1}
          maxZoom={2}
          defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
          deleteKeyCode={['Backspace', 'Delete', 'Del', 'Canc']}
          elementsSelectable={true}
          edgesFocusable={true}
          selectNodesOnDrag={false}
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#94a3b8" />
          <Controls className="!left-4 !bottom-4" />
          <MiniMap className={`${showProperties ? '!right-[340px]' : '!right-4'} !bottom-4`} nodeStrokeWidth={3} zoomable pannable />
        </ReactFlow>
      </div>

      {/* Chat Simulator o Test Run Panel */}
      {showSimulator && (() => {
        // Controlla se il flow ha un nodo START
        const hasStartNode = nodes.some(n => n.type === 'start');
        const hasTriggerNode = nodes.some(n => 
          n.type && ['schedule_trigger', 'webhook_trigger', 'rss_feed'].includes(n.type)
        );
        
        // Se ha START → Chat Simulator interattivo
        // Se ha trigger automatico senza START → Test Run Panel
        if (hasStartNode || (!hasTriggerNode && nodes.length > 0)) {
          return (
            <ChatSimulator
              flowDefinition={{
                id: flowName,
                nodes: assignNodeNumbers(nodes),
                edges,
              }}
              flowName={flowName}
              onClose={() => {
                setShowSimulator(false);
                setLoadedChatMessages(undefined);
              }}
              onOpenHistory={() => setShowChatHistory(true)}
              loadedMessages={loadedChatMessages}
              isEmpty={nodes.length === 0}
            />
          );
        } else {
          // Flow automatico senza START → Test Run Panel
          return (
            <TestRunPanel
              flowDefinition={{
                id: flowName,
                nodes: assignNodeNumbers(nodes),
                edges,
              }}
              flowName={flowName}
              onClose={() => setShowSimulator(false)}
            />
          );
        }
      })()}

      {/* Settings Panel - Prompt Settings */}
      {showSettings && !showSimulator && (
        <SettingsPanel 
          onClose={() => setShowSettings(false)} 
          onPromptUpdate={handlePromptUpdate}
        />
      )}

      {/* Right Sidebar - Properties */}
      {showProperties && !showSimulator && !showSettings && (
      <div className="w-80 shrink-0 bg-white border-l-4 border-gray-300 flex flex-col overflow-hidden shadow-2xl">
        <div className="p-6 border-b-2 border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Proprietà
          </h2>
          {selectedNode && (
            <button
              onClick={deleteNode}
              className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition"
              title="Elimina nodo"
            >
              <Trash2 size={20} />
            </button>
          )}
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {selectedNode ? (
            <div className="space-y-4">
              {/* Tipo nodo e numero */}
              <div className="bg-gradient-to-r from-blue-100 to-blue-50 p-4 rounded-xl border-2 border-blue-300">
                <label className="block text-sm font-bold text-blue-800 mb-2">🏷️ Tipo</label>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-900 font-semibold bg-white px-3 py-2 rounded-lg border border-blue-200">
                    {selectedNode.type}
                  </div>
                  {selectedNode.data.nodeNumber && (
                    <div className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg border border-gray-200">
                      Nodo #{selectedNode.data.nodeNumber}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-100 to-green-50 p-4 rounded-xl border-2 border-green-300">
                <label className="block text-sm font-bold text-green-800 mb-2">📝 Label</label>
                <input
                  type="text"
                  value={selectedNode.data.label || ''}
                  onChange={(e) => updateNodeData('label', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-green-200 rounded-lg"
                />
              </div>

              {(selectedNode.type === 'start' || selectedNode.type === 'say') && (
                <div className="bg-gradient-to-r from-blue-100 to-blue-50 p-4 rounded-xl border-2 border-blue-300">
                  <label className="block text-sm font-bold text-blue-800 mb-2">
                    💬 {selectedNode.type === 'start' ? 'Messaggio di Benvenuto' : 'Messaggio'}
                  </label>
                  
                  <VariableAutocomplete
                    value={selectedNode.data.message || ''}
                    onChange={(value) => updateNodeData('message', value)}
                    availableVariables={getAvailableVariables()}
                    placeholder={selectedNode.type === 'start' ? 'Buongiorno! Come posso aiutarti?' : 'Scrivi il messaggio...'}
                    className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg"
                    multiline={true}
                    rows={4}
                  />
                  <p className="text-xs text-blue-600 mt-2">
                    💡 Usa <code className="bg-blue-200 px-1 rounded">{'{{variabile}}'}</code> per inserire variabili o clicca sul bottone Variabili
                  </p>
                </div>
              )}

              {selectedNode.type === 'input' && (
                <div className="bg-gradient-to-r from-purple-100 to-purple-50 p-4 rounded-xl border-2 border-purple-300">
                  <label className="block text-sm font-bold text-purple-800 mb-2">🏷️ Nome Variabile</label>
                  <input
                    type="text"
                    value={selectedNode.data.variableName || 'user_input'}
                    onChange={(e) => updateNodeData('variableName', e.target.value)}
                    className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg mb-3"
                    placeholder="es: nome_utente, eta, citta"
                  />
                  <div className="text-xs text-purple-600 mb-3">
                    💡 Usa <code className="bg-purple-200 px-1 rounded">{`{{${selectedNode.data.variableName || 'user_input'}}}`}</code> nei nodi SAY per richiamarla
                  </div>
                  <label className="block text-sm font-bold text-purple-800 mb-2">⌨️ Prompt (opzionale)</label>
                  <VariableAutocomplete
                    value={selectedNode.data.prompt || ''}
                    onChange={(value) => updateNodeData('prompt', value)}
                    availableVariables={getAvailableVariables(selectedNode.data.variableName)}
                    placeholder="Messaggio da mostrare prima dell'input"
                    className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg"
                    multiline={true}
                    rows={3}
                  />
                  <p className="text-xs text-purple-600 mt-2">
                    💡 Usa <code className="bg-purple-200 px-1 rounded">{'{{variabile}}'}</code> per inserire variabili (esclusa la variabile di questo nodo)
                  </p>
                  
                  {/* Validazione Input */}
                  <label className="block text-sm font-bold text-purple-800 mb-2 mt-4">✅ Validazione Input</label>
                  <select
                    value={selectedNode.data.validation_type || ''}
                    onChange={(e) => updateNodeData('validation_type', e.target.value)}
                    className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg bg-white"
                  >
                    <option value="">Nessuna validazione</option>
                    <option value="email">📧 Email (formato email)</option>
                    <option value="phone">📱 Telefono (solo numeri)</option>
                    <option value="name">👤 Nome (solo lettere)</option>
                    <option value="number">🔢 Numero (solo cifre)</option>
                    <option value="cap">📮 CAP (5 cifre)</option>
                  </select>
                  <p className="text-xs text-purple-600 mt-2">
                    🛡️ Se l'utente inserisce un valore non valido, gli verrà chiesto di riprovare
                  </p>
                </div>
              )}

              {selectedNode.type === 'replay' && (
                <div className="bg-gradient-to-r from-violet-100 to-purple-50 p-4 rounded-xl border-2 border-violet-300">
                  <label className="block text-sm font-bold text-violet-800 mb-2">💬 Risposta (Messaggio Bot)</label>
                  <VariableAutocomplete
                    value={selectedNode.data.response || ''}
                    onChange={(value) => updateNodeData('response', value)}
                    availableVariables={getAvailableVariables(selectedNode.data.variableName)}
                    placeholder="Risposta del bot all'utente..."
                    className="w-full px-3 py-2 border-2 border-violet-200 rounded-lg mb-4"
                    multiline={true}
                    rows={4}
                  />
                  <p className="text-xs text-violet-600 mb-4">
                    💡 Usa <code className="bg-violet-200 px-1 rounded">{'{{variabile}}'}</code> per inserire variabili o clicca sul bottone Variabili
                  </p>
                  
                  <label className="block text-sm font-bold text-violet-800 mb-2">❓ Domanda (Prompt Input)</label>
                  <VariableAutocomplete
                    value={selectedNode.data.question || ''}
                    onChange={(value) => updateNodeData('question', value)}
                    availableVariables={getAvailableVariables(selectedNode.data.variableName)}
                    placeholder="Domanda da mostrare all'utente..."
                    className="w-full px-3 py-2 border-2 border-violet-200 rounded-lg mb-3"
                    multiline={true}
                    rows={3}
                  />
                  
                  <label className="block text-sm font-bold text-violet-800 mb-2">🏷️ Nome Variabile</label>
                  <input
                    type="text"
                    value={selectedNode.data.variableName || `user_input_${(selectedNode.data.nodeNumber || 0).toString().padStart(2, '0')}`}
                    onChange={(e) => updateNodeData('variableName', e.target.value)}
                    className="w-full px-3 py-2 border-2 border-violet-200 rounded-lg mb-3"
                    placeholder="es: user_input_01 (auto-generato)"
                  />
                  <div className="text-xs text-violet-600 mb-3">
                    💡 Usa <code className="bg-violet-200 px-1 rounded">{`{{${selectedNode.data.variableName || `user_input_${(selectedNode.data.nodeNumber || 0).toString().padStart(2, '0')}`}}}`}</code> nei nodi SAY o REPLAY per richiamarla
                  </div>
                </div>
              )}

              {selectedNode.type === 'proposition' && (
                <div className="bg-gradient-to-r from-fuchsia-100 to-pink-50 p-4 rounded-xl border-2 border-fuchsia-300">
                  <label className="block text-sm font-bold text-fuchsia-800 mb-2">📝 Testo Proposizione</label>
                  <VariableAutocomplete
                    value={selectedNode.data.text || ''}
                    onChange={(value) => updateNodeData('text', value)}
                    availableVariables={getAvailableVariables()}
                    placeholder="Testo da mostrare all'utente con bottoni..."
                    className="w-full px-3 py-2 border-2 border-fuchsia-200 rounded-lg mb-4"
                    multiline={true}
                    rows={4}
                  />
                  <p className="text-xs text-fuchsia-600 mb-4">
                    💡 Usa <code className="bg-fuchsia-200 px-1 rounded">{'{{variabile}}'}</code> per inserire variabili o clicca sul bottone Variabili
                  </p>
                  
                  <div className="flex items-center justify-between mb-2 mt-4">
                    <label className="block text-sm font-bold text-fuchsia-800">
                      🎯 Opzioni Bottoni ({(selectedNode.data.options || []).length})
                    </label>
                    <button
                      onClick={() => {
                        const options = [...(selectedNode.data.options || []), { label: '' }];
                        updateNodeData('options', options);
                      }}
                      className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-bold shadow-sm"
                      title="Aggiungi opzione"
                    >
                      + Aggiungi
                    </button>
                  </div>
                  
                  <div className="text-xs text-fuchsia-700 mb-2">
                    Ogni ramo che colleghi avrà una label. Qui puoi personalizzare le label dei bottoni:
                  </div>
                  
                  <div className="max-h-72 overflow-y-auto space-y-2 mb-3">
                    {(selectedNode.data.options || []).map((option: any, i: number) => (
                      <div key={i} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-fuchsia-200">
                        <span className="text-xs font-bold text-fuchsia-700 w-16">Opzione {i + 1}:</span>
                        <input
                          type="text"
                          value={option.label || ''}
                          onChange={(e) => {
                            const options = [...(selectedNode.data.options || [])];
                            options[i] = { label: e.target.value };
                            updateNodeData('options', options);
                          }}
                          className="flex-1 px-2 py-1 border border-fuchsia-200 rounded text-xs"
                          placeholder={`Label bottone ${i + 1} (es: Sì, No, Maggiori info...)`}
                        />
                        <button
                          onClick={() => {
                            const options = (selectedNode.data.options || []).filter((_: any, idx: number) => idx !== i);
                            updateNodeData('options', options);
                          }}
                          className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-bold"
                          title="Rimuovi opzione"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {(selectedNode.data.options || []).length === 0 && (
                    <p className="text-xs text-fuchsia-600 italic mb-2">Nessuna opzione definita. Clicca "+ Aggiungi"</p>
                  )}
                  
                  <label className="block text-sm font-bold text-fuchsia-800 mb-2 mt-4">🏷️ Variabile Scelta (bottone cliccato)</label>
                  <input
                    type="text"
                    value={selectedNode.data.variableName || `user_choice_${(selectedNode.data.nodeNumber || 0).toString().padStart(2, '0')}`}
                    onChange={(e) => updateNodeData('variableName', e.target.value)}
                    className="w-full px-3 py-2 border-2 border-fuchsia-200 rounded-lg mb-2"
                    placeholder="es: user_choice_01 (auto-generato)"
                  />
                  <div className="text-xs text-fuchsia-600 mb-3">
                    💡 Usa <code className="bg-fuchsia-200 px-1 rounded">{`{{${selectedNode.data.variableName || `user_choice_${(selectedNode.data.nodeNumber || 0).toString().padStart(2, '0')}`}}}`}</code> per la scelta dell'utente (sia clic che testo libero)
                  </div>
                  
                  <div className="mt-3 text-xs text-fuchsia-700 bg-fuchsia-50 p-2 rounded border border-fuchsia-200">
                    💡 Collega i rami dal punto <strong>fuchsia</strong> per i bottoni, dal punto <strong>grigio "altro"</strong> per il testo libero. La variabile cattura entrambi!
                  </div>
                </div>
              )}

              {selectedNode.type === 'rag' && (
                <div className="bg-gradient-to-r from-orange-100 to-orange-50 p-4 rounded-xl border-2 border-orange-300 space-y-4">
                  {/* Indice KB */}
                  <div>
                    <label className="block text-sm font-bold text-orange-800 mb-2">📚 Indice Knowledge Base</label>
                    <select
                      value={selectedNode.data.index_name || ''}
                      onChange={(e) => updateNodeData('index_name', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-orange-200 rounded-lg bg-white"
                    >
                      <option value="">-- Seleziona Indice --</option>
                      {kbIndices.map((index) => (
                        <option key={index.id} value={index.id}>
                          {index.name}
                        </option>
                      ))}
                    </select>
                    {kbIndices.length === 0 && (
                      <p className="text-xs text-orange-600 mt-2">
                        ⚠️ Nessun indice KB disponibile. Avvia il server RAG (porta 8002).
                      </p>
                    )}
                    <button
                      onClick={loadKbIndices}
                      className="mt-2 w-full px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold transition"
                    >
                      🔄 Ricarica Indici
                    </button>
                  </div>

                  {/* Scope Prompt */}
                  <div>
                    <label className="block text-sm font-bold text-orange-800 mb-2">🎯 Ambito/Scope (opzionale)</label>
                    <textarea
                      value={selectedNode.data.scope_prompt || ''}
                      onChange={(e) => updateNodeData('scope_prompt', e.target.value)}
                      placeholder="Rispondi solo se la domanda riguarda immobili, case, appartamenti, vendite o affitti..."
                      className="w-full px-3 py-2 border-2 border-orange-200 rounded-lg text-sm"
                      rows={3}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Se definito, il sistema verificherà che le domande siano in tema prima di rispondere
                    </p>
                  </div>

                  {/* Query Template */}
                  <div>
                    <label className="block text-sm font-bold text-orange-800 mb-2">🔍 Query Template (opzionale)</label>
                    <VariableAutocomplete
                      value={selectedNode.data.query || ''}
                      onChange={(value) => updateNodeData('query', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="(lascia vuoto per usare l'ultimo input automaticamente)"
                      className="w-full px-3 py-2 border-2 border-orange-200 rounded-lg"
                      multiline={true}
                      rows={2}
                    />
                    <p className="text-xs text-green-600 mt-1">
                      ✨ <strong>Auto:</strong> Se lasci vuoto, userà automaticamente l'input del nodo precedente!
                    </p>
                  </div>

                  {/* Max Interactions */}
                  <div>
                    <label className="block text-sm font-bold text-orange-800 mb-2">🔄 Max Interazioni</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={selectedNode.data.max_interactions || 5}
                      onChange={(e) => updateNodeData('max_interactions', parseInt(e.target.value) || 5)}
                      className="w-full px-3 py-2 border-2 border-orange-200 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Dopo X scambi senza uscita, il flusso passa al ramo "max interazioni"
                    </p>
                  </div>

                  {/* Modelli AI */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-orange-800 mb-1">📦 Embedding Model</label>
                      <select
                        value={selectedNode.data.embed_model || 'BAAI/bge-m3'}
                        onChange={(e) => updateNodeData('embed_model', e.target.value)}
                        className="w-full px-2 py-1 border border-orange-200 rounded text-xs"
                      >
                        <option value="BAAI/bge-m3">BAAI/bge-m3</option>
                        <option value="sentence-transformers/all-MiniLM-L6-v2">MiniLM-L6</option>
                        <option value="intfloat/multilingual-e5-large">E5-Large</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-orange-800 mb-1">🤖 LLM Model</label>
                      <select
                        value={selectedNode.data.llm_model || 'openai/gpt-oss-120b'}
                        onChange={(e) => updateNodeData('llm_model', e.target.value)}
                        className="w-full px-2 py-1 border border-orange-200 rounded text-xs"
                      >
                        <option value="openai/gpt-oss-120b">GPT OSS 120B (Local) ⭐</option>
                        <option value="groq/llama-3.1-70b-versatile">Llama 3.1 70B (Local)</option>
                        <option value="groq/llama-3.1-8b-instant">Llama 3.1 8B (Local)</option>
                        <option value="groq/mixtral-8x7b-32768">Mixtral 8x7B (Local)</option>
                        <option value="openai/gpt-4o-mini">GPT-4o Mini</option>
                      </select>
                    </div>
                  </div>

                  {/* System Prompt RAG (personalizzabile) */}
                  <div>
                    <label className="block text-sm font-bold text-orange-800 mb-2">
                      📝 System Prompt LLM
                      <span className="text-xs font-normal text-gray-500 ml-2">(personalizza come il bot risponde)</span>
                    </label>
                    <textarea
                      value={selectedNode.data.system_prompt || ''}
                      onChange={(e) => updateNodeData('system_prompt', e.target.value)}
                      placeholder={`Esempio:
Sei un assistente esperto in [ARGOMENTO].
Rispondi in modo professionale e dettagliato.
Cita sempre le fonti con [1], [2], etc.
Se non trovi informazioni, dillo chiaramente.`}
                      className="w-full px-3 py-2 border-2 border-orange-200 rounded-lg text-sm font-mono"
                      rows={6}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Lascia vuoto per usare il prompt anti-allucinazione di default. 
                      Puoi personalizzare come il bot risponde, il tono, e le istruzioni specifiche.
                    </p>
                  </div>

                  {/* Variabili Output del RAG */}
                  <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                    <label className="block text-xs font-bold text-orange-800 mb-2">📊 Variabili Output (auto-generate)</label>
                    <div className="grid grid-cols-1 gap-1 text-xs">
                      <div>
                        <span className="text-gray-600">Risposta RAG:</span>
                        <code className="ml-1 bg-white px-1 rounded">{`{{rag_response_${(selectedNode.data.nodeNumber || 0).toString().padStart(2, '0')}}}`}</code>
                      </div>
                      <div>
                        <span className="text-gray-600">Query usata:</span>
                        <code className="ml-1 bg-white px-1 rounded">{`{{rag_query_${(selectedNode.data.nodeNumber || 0).toString().padStart(2, '0')}}}`}</code>
                      </div>
                      <div>
                        <span className="text-gray-600">Fonti:</span>
                        <code className="ml-1 bg-white px-1 rounded">{`{{rag_sources_${(selectedNode.data.nodeNumber || 0).toString().padStart(2, '0')}}}`}</code>
                      </div>
                    </div>
                  </div>

                  {/* Info comportamento */}
                  <div className="text-xs bg-green-50 p-3 rounded border border-green-200">
                    <div className="font-bold mb-2 text-green-800">🔄 Comportamento:</div>
                    <div className="text-green-700 space-y-1">
                      <div>• Il nodo RAG <strong>cicla internamente</strong></div>
                      <div>• Mostra risposte e chiede nuove domande</div>
                      <div>• Mantiene <strong>storia chat</strong> per coerenza</div>
                    </div>
                    <div className="font-bold mt-3 mb-1 text-gray-700">🔀 Uscite (solo eccezioni):</div>
                    <div>• <span className="text-gray-500">Sotto sx</span>: fuori scope</div>
                    <div>• <span className="text-red-500">Sotto dx</span>: max interazioni</div>
                  </div>
                </div>
              )}

              {/* RAG SIMPLE NODE - Query semplice alla KB */}
              {selectedNode.type === 'rag_simple' && (
                <div className="bg-gradient-to-r from-teal-100 to-cyan-50 p-4 rounded-xl border-2 border-teal-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">📚</span>
                    <label className="text-lg font-bold text-teal-800">RAG Simple</label>
                  </div>
                  <p className="text-xs text-teal-600 mb-3">
                    Query semplice alla Knowledge Base. Cerca → Risposta → Passa al nodo successivo automaticamente.
                  </p>

                  {/* Indice KB */}
                  <div>
                    <label className="block text-xs font-bold text-teal-700 mb-1">🗂️ Indice Knowledge Base</label>
                    <select
                      value={selectedNode.data.index_name || ''}
                      onChange={(e) => updateNodeData('index_name', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-teal-200 rounded-lg bg-white"
                    >
                      <option value="">-- Seleziona Indice --</option>
                      {kbIndices.map((index) => (
                        <option key={index.id} value={index.id}>
                          {index.name}
                        </option>
                      ))}
                    </select>
                    {kbIndices.length === 0 && (
                      <p className="text-xs text-teal-600 mt-1">
                        ⚠️ Nessun indice. <button onClick={loadKbIndices} className="underline">Ricarica</button>
                      </p>
                    )}
                  </div>

                  {/* Query Template */}
                  <div>
                    <label className="block text-xs font-bold text-teal-700 mb-1">🔍 Query (con variabili)</label>
                    <VariableAutocomplete
                      value={selectedNode.data.query || ''}
                      onChange={(value) => updateNodeData('query', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="{domanda}"
                      className="w-full px-3 py-2 border-2 border-teal-200 rounded text-sm"
                      multiline={false}
                    />
                    <p className="text-xs text-gray-500 mt-1">Usa {'{variabile}'} per passare l'input utente</p>
                  </div>

                  {/* Output Variable */}
                  <div>
                    <label className="block text-xs font-bold text-teal-700 mb-1">💾 Variabile Output</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || 'rag_response'}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      placeholder="rag_response"
                      className="w-full px-3 py-2 border-2 border-teal-200 rounded text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Usa <code className="bg-white px-1 rounded">{`{${selectedNode.data.output_variable || 'rag_response'}}`}</code> nei nodi successivi
                    </p>
                  </div>

                  {/* Info comportamento */}
                  <div className="text-xs bg-teal-50 p-3 rounded border border-teal-200">
                    <div className="font-bold mb-1 text-teal-800">✅ Comportamento SEMPLICE:</div>
                    <div className="text-teal-700">
                      <div>1. Riceve query dalla variabile</div>
                      <div>2. Cerca nel KB</div>
                      <div>3. Salva risposta in output_variable</div>
                      <div>4. <strong>Passa automaticamente</strong> al nodo successivo</div>
                    </div>
                  </div>
                </div>
              )}

              {/* RAG LOOP NODE - Multi-turno con KB */}
              {selectedNode.type === 'rag_loop' && (
                <div className="bg-gradient-to-r from-emerald-100 to-teal-50 p-4 rounded-xl border-2 border-emerald-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🔄📚</span>
                    <label className="text-lg font-bold text-emerald-800">RAG Loop</label>
                  </div>
                  <p className="text-xs text-emerald-600 mb-3">
                    Chatbot multi-turno con Knowledge Base. L'utente può fare più domande fino al limite o scrivere "no".
                  </p>

                  {/* Indice KB */}
                  <div>
                    <label className="block text-xs font-bold text-emerald-700 mb-1">🗂️ Indice Knowledge Base</label>
                    <select
                      value={selectedNode.data.index_name || ''}
                      onChange={(e) => updateNodeData('index_name', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-emerald-200 rounded-lg bg-white"
                    >
                      <option value="">-- Seleziona Indice --</option>
                      {kbIndices.map((index) => (
                        <option key={index.id} value={index.id}>
                          {index.name}
                        </option>
                      ))}
                    </select>
                    {kbIndices.length === 0 && (
                      <p className="text-xs text-emerald-600 mt-1">
                        ⚠️ Nessun indice. <button onClick={loadKbIndices} className="underline font-bold">Ricarica</button>
                      </p>
                    )}
                    {kbIndices.length > 0 && (
                      <button
                        onClick={loadKbIndices}
                        className="mt-2 w-full px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-semibold transition"
                      >
                        🔄 Ricarica Indici ({kbIndices.length})
                      </button>
                    )}
                  </div>

                  {/* Welcome Message */}
                  <div>
                    <label className="block text-xs font-bold text-emerald-700 mb-1">👋 Messaggio di Benvenuto</label>
                    <textarea
                      value={selectedNode.data.welcome_message || 'Ciao! Cosa vuoi sapere sui nostri documenti?'}
                      onChange={(e) => updateNodeData('welcome_message', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border-2 border-emerald-200 rounded text-sm"
                    />
                  </div>

                  {/* Max Interactions */}
                  <div>
                    <label className="block text-xs font-bold text-emerald-700 mb-1">🔁 Max Domande</label>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={selectedNode.data.max_interactions || 5}
                      onChange={(e) => updateNodeData('max_interactions', parseInt(e.target.value) || 5)}
                      className="w-full px-3 py-2 border-2 border-emerald-200 rounded text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Dopo N domande, esce automaticamente</p>
                  </div>

                  {/* Goodbye Message */}
                  <div>
                    <label className="block text-xs font-bold text-emerald-700 mb-1">👋 Messaggio di Arrivederci</label>
                    <input
                      type="text"
                      value={selectedNode.data.goodbye_message || 'Grazie per le domande! Arrivederci!'}
                      onChange={(e) => updateNodeData('goodbye_message', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-emerald-200 rounded text-sm"
                    />
                  </div>

                  {/* Output Variable */}
                  <div>
                    <label className="block text-xs font-bold text-emerald-700 mb-1">💾 Variabile Output</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || 'rag_loop_response'}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-emerald-200 rounded text-sm"
                    />
                  </div>

                  {/* Info comportamento */}
                  <div className="text-xs bg-emerald-50 p-3 rounded border border-emerald-200">
                    <div className="font-bold mb-1 text-emerald-800">🔄 Comportamento LOOP:</div>
                    <div className="text-emerald-700">
                      <div>1. Mostra messaggio di benvenuto</div>
                      <div>2. Utente fa domanda → cerca nel KB → risponde</div>
                      <div>3. Chiede "Hai altre domande?"</div>
                      <div>4. Cicla fino a max domande o utente dice "no"</div>
                      <div>5. Mostra arrivederci e <strong>passa al next</strong></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Mostra Conditions da Botpress */}
              {selectedNode.data.conditions && selectedNode.data.conditions.length > 0 && (
                <div className="bg-gradient-to-r from-amber-100 to-amber-50 p-4 rounded-xl border-2 border-amber-300">
                  <label className="block text-sm font-bold text-amber-800 mb-2">🔀 Condizioni (Botpress)</label>
                  <div className="space-y-2">
                    {selectedNode.data.conditions.map((cond: any, idx: number) => (
                      <div key={idx} className="bg-white p-2 rounded border border-amber-200 text-xs">
                        <div className="font-semibold text-amber-900">{cond.label}</div>
                        <div className="font-mono text-gray-700">{cond.expression}</div>
                        <div className="text-gray-500">→ {cond.target}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Mostra/Edita Action (codice JS) da Botpress o custom */}
              {selectedNode.data.action?.code && (
                <div className="bg-gradient-to-r from-yellow-100 to-yellow-50 p-4 rounded-xl border-2 border-yellow-300">
                  <label className="block text-sm font-bold text-yellow-800 mb-2">⚡ JavaScript Code</label>
                  <div className="text-xs text-gray-600 mb-2">
                    Label: <span className="font-semibold">{selectedNode.data.action.label}</span>
                  </div>
                  
                  {/* Mostra Description se presente */}
                  {selectedNode.data.action?.description && (
                    <div className="mb-3 bg-amber-50 border-2 border-amber-200 rounded-lg p-3">
                      <div className="text-xs font-bold text-amber-800 mb-1">📝 Descrizione:</div>
                      <div className="text-sm text-amber-900 italic">{selectedNode.data.action.description}</div>
                    </div>
                  )}
                  
                  {/* AI Code Generator */}
                  <div className="mb-3 bg-white p-3 rounded-lg border-2 border-blue-200">
                    <label className="block text-xs font-bold text-blue-800 mb-1">🤖 Rigenera con AI</label>
                    <input
                      type="text"
                      key={selectedNode.data.action?.description || 'empty-existing'}
                      placeholder="Es: Calcola l'età e verifica se è maggiorenne"
                      defaultValue={selectedNode.data.action?.description || ''}
                      className="w-full px-3 py-2 border-2 border-blue-200 rounded text-sm mb-2"
                      id={`action-description-existing-${selectedNode.id}`}
                    />
                    <button
                      onClick={async () => {
                        const input = document.getElementById(`action-description-existing-${selectedNode.id}`) as HTMLInputElement;
                        const description = input?.value.trim();
                        if (!description) {
                          alert('❌ Scrivi cosa vuoi fare!');
                          return;
                        }
                        
                        // Raccogli variabili disponibili da nodi INPUT precedenti
                        const availableVars = nodes
                          .filter(n => n.type === 'input' && n.data.variableName)
                          .map(n => n.data.variableName);
                        
                        try {
                          const response = await fetch(`${API_URL}/flow/generate-action-code`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              description,
                              available_variables: availableVars
                            })
                          });
                          
                          if (!response.ok) throw new Error('Errore generazione codice');
                          
                          const data = await response.json();
                          
                          if (data.success && data.code) {
                            const newAction = {
                              ...selectedNode.data.action,
                              code: data.code
                            };
                            updateNodeData('action', newAction);
                            input.value = '';
                          }
                        } catch (error: any) {
                          alert(`❌ Errore: ${error.message}`);
                        }
                      }}
                      className="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold transition"
                    >
                      ✨ Rigenera
                    </button>
                  </div>
                  
                  <textarea
                    value={selectedNode.data.action.code}
                    onChange={(e) => {
                      const newAction = {
                        ...selectedNode.data.action,
                        code: e.target.value
                      };
                      updateNodeData('action', newAction);
                    }}
                    className="w-full px-3 py-2 border-2 border-yellow-200 rounded-lg font-mono text-sm min-h-[200px] bg-gray-900 text-green-400"
                    placeholder="// JavaScript code..."
                  />
                  <p className="text-xs text-gray-600 mt-2">
                    💡 Usa <code className="bg-yellow-200 px-1 rounded">temp.variabile</code> per modificare le variabili del flow
                  </p>
                </div>
              )}

              {/* Mostra Capture (input avanzato) da Botpress */}
              {selectedNode.data.capture && (
                <div className="bg-gradient-to-r from-purple-100 to-purple-50 p-4 rounded-xl border-2 border-purple-300">
                  <label className="block text-sm font-bold text-purple-800 mb-2">📥 Capture Input (Botpress)</label>
                  <div className="space-y-2 text-xs">
                    <div><strong>Entity:</strong> {selectedNode.data.capture.entityName}</div>
                    <div><strong>Question:</strong> {selectedNode.data.capture.question}</div>
                    <div><strong>Variable:</strong> {selectedNode.data.capture.variableId}</div>
                    {selectedNode.data.capture.kbPriority?.enabled && (
                      <div className="bg-purple-200 p-2 rounded mt-2">
                        <strong>🔍 KB Enabled:</strong> {selectedNode.data.capture.kbPriority.kbs?.length || 0} knowledge bases
                      </div>
                    )}
                    {selectedNode.data.capture.prompt && (
                      <div className="bg-gray-100 p-2 rounded mt-2">
                        <strong>LLM:</strong> {selectedNode.data.capture.prompt.model} (temp: {selectedNode.data.capture.prompt.temperature})
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Mostra Knowledge Base da Botpress */}
              {selectedNode.data.knowledgeBase && (
                <div className="bg-gradient-to-r from-orange-100 to-orange-50 p-4 rounded-xl border-2 border-orange-300">
                  <label className="block text-sm font-bold text-orange-800 mb-2">📚 Knowledge Base (Botpress)</label>
                  <div className="bg-white p-2 rounded border border-orange-200 text-xs">
                    <pre className="text-gray-700 whitespace-pre-wrap">{JSON.stringify(selectedNode.data.knowledgeBase, null, 2)}</pre>
                  </div>
                </div>
              )}

              {selectedNode.type === 'api_call' && (
                <div className="bg-gradient-to-r from-teal-100 to-cyan-50 p-4 rounded-xl border-2 border-teal-300">
                  <label className="block text-sm font-bold text-teal-800 mb-3">🌐 API Configuration</label>
                  
                  {/* API URL */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-teal-700 mb-1">🔗 API URL</label>
                    <VariableAutocomplete
                      value={selectedNode.data.api_url || ''}
                      onChange={(value) => updateNodeData('api_url', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder={`${API_URL}/api/endpoint`}
                      className="w-full px-3 py-2 border-2 border-teal-200 rounded text-sm"
                    />
                    <p className="text-xs text-gray-600 mt-1">💡 Usa <code className="bg-teal-200 px-1 rounded">{'{{variabile}}'}</code> per inserire variabili</p>
                  </div>

                  {/* HTTP Method */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-teal-700 mb-1">📡 HTTP Method</label>
                    <select
                      value={selectedNode.data.method || 'POST'}
                      onChange={(e) => updateNodeData('method', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-teal-200 rounded text-sm"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="DELETE">DELETE</option>
                    </select>
                  </div>

                  {/* Body (for POST/PUT) */}
                  {(selectedNode.data.method === 'POST' || selectedNode.data.method === 'PUT' || !selectedNode.data.method) && (
                    <div className="mb-3">
                      <label className="block text-xs font-bold text-teal-700 mb-1">📦 Body (JSON)</label>
                      <VariableAutocomplete
                        value={typeof selectedNode.data.body === 'string' ? selectedNode.data.body : JSON.stringify(selectedNode.data.body || {}, null, 2)}
                        onChange={(value) => {
                          try {
                            const parsed = JSON.parse(value);
                            updateNodeData('body', parsed);
                          } catch {
                            updateNodeData('body', value);
                          }
                        }}
                        availableVariables={getAvailableVariables()}
                        placeholder='{"param": "{{variable}}"}'
                        className="w-full px-3 py-2 border-2 border-teal-200 rounded text-sm font-mono"
                        multiline={true}
                        rows={4}
                      />
                      <p className="text-xs text-gray-600 mt-1">💡 Usa <code className="bg-teal-200 px-1 rounded">{'{{variabile}}'}</code> per inserire variabili nel JSON</p>
                    </div>
                  )}

                  {/* Response Variable */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-teal-700 mb-1">💾 Response Variable</label>
                    <input
                      type="text"
                      placeholder="api_response"
                      value={selectedNode.data.response_variable || ''}
                      onChange={(e) => updateNodeData('response_variable', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-teal-200 rounded text-sm"
                    />
                    <p className="text-xs text-gray-600 mt-1">Nome variabile dove salvare la response</p>
                  </div>

                  {/* Extract Fields */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-teal-700 mb-1">📝 Extract Fields (JSON)</label>
                    <textarea
                      placeholder='{"var_name": "json.path.to.field"}'
                      value={typeof selectedNode.data.extract_fields === 'string' ? selectedNode.data.extract_fields : JSON.stringify(selectedNode.data.extract_fields || {}, null, 2)}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          updateNodeData('extract_fields', parsed);
                        } catch {
                          updateNodeData('extract_fields', e.target.value);
                        }
                      }}
                      className="w-full px-3 py-2 border-2 border-teal-200 rounded text-sm font-mono"
                      rows={3}
                    />
                    <p className="text-xs text-gray-600 mt-1">Mappa: nome_variabile → percorso.nel.json</p>
                  </div>

                  {/* Timeout */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-teal-700 mb-1">⏱️ Timeout (secondi)</label>
                    <input
                      type="number"
                      placeholder="30"
                      value={selectedNode.data.timeout || 30}
                      onChange={(e) => updateNodeData('timeout', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border-2 border-teal-200 rounded text-sm"
                      min="5"
                      max="300"
                    />
                  </div>

                  {/* Error Handling */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-teal-700 mb-1">⚠️ On Error</label>
                    <select
                      value={selectedNode.data.on_error || 'continue'}
                      onChange={(e) => updateNodeData('on_error', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-teal-200 rounded text-sm"
                    >
                      <option value="continue">Continue (salva errore in variabile)</option>
                      <option value="stop">Stop (termina flow)</option>
                    </select>
                  </div>
                </div>
              )}

              {selectedNode.type === 'action' && !selectedNode.data.action?.code && (
                <div className="bg-gradient-to-r from-indigo-100 to-indigo-50 p-4 rounded-xl border-2 border-indigo-300">
                  <label className="block text-sm font-bold text-indigo-800 mb-2">⚡ JavaScript Code</label>
                  
                  {/* DEBUG: Mostra cosa c'è in action */}
                  {!selectedNode.data.action && (
                    <div className="mb-3 bg-red-100 border-2 border-red-300 rounded-lg p-3">
                      <div className="text-xs font-bold text-red-800">⚠️ ATTENZIONE: action è null/undefined!</div>
                      <div className="text-xs text-red-700 mt-1">L'LLM non ha generato il campo action.</div>
                    </div>
                  )}
                  
                  {selectedNode.data.action && !selectedNode.data.action.description && (
                    <div className="mb-3 bg-orange-100 border-2 border-orange-300 rounded-lg p-3">
                      <div className="text-xs font-bold text-orange-800">⚠️ ATTENZIONE: action.description è vuoto!</div>
                      <div className="text-xs text-orange-700 mt-1">Contenuto action: {JSON.stringify(selectedNode.data.action)}</div>
                    </div>
                  )}
                  
                  {/* Mostra Description se presente */}
                  {selectedNode.data.action?.description && (
                    <div className="mb-3 bg-amber-50 border-2 border-amber-200 rounded-lg p-3">
                      <div className="text-xs font-bold text-amber-800 mb-1">📝 Descrizione generata dall'AI:</div>
                      <div className="text-sm text-amber-900 italic">{selectedNode.data.action.description}</div>
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-600 mb-2">
                    Usa <code className="bg-indigo-200 px-1 rounded">temp.variabile</code> per leggere/scrivere variabili
                  </p>
                  
                  {/* AI Code Generator */}
                  <div className="mb-3 bg-white p-3 rounded-lg border-2 border-blue-200">
                    <label className="block text-xs font-bold text-blue-800 mb-1">🤖 Genera Codice con AI</label>
                    <input
                      type="text"
                      key={selectedNode.data.action?.description || 'empty'}
                      placeholder="Es: Calcola l'età dell'utente"
                      defaultValue={selectedNode.data.action?.description || ''}
                      className="w-full px-3 py-2 border-2 border-blue-200 rounded text-sm mb-2"
                      id={`action-description-${selectedNode.id}`}
                    />
                    <button
                      onClick={async () => {
                        const input = document.getElementById(`action-description-${selectedNode.id}`) as HTMLInputElement;
                        const description = input?.value.trim();
                        if (!description) {
                          alert('❌ Scrivi cosa vuoi fare!');
                          return;
                        }
                        
                        // Raccogli variabili disponibili da nodi INPUT precedenti
                        const availableVars = nodes
                          .filter(n => n.type === 'input' && n.data.variableName)
                          .map(n => n.data.variableName);
                        
                        try {
                          const response = await fetch(`${API_URL}/flow/generate-action-code`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              description,
                              available_variables: availableVars
                            })
                          });
                          
                          if (!response.ok) throw new Error('Errore generazione codice');
                          
                          const data = await response.json();
                          
                          if (data.success && data.code) {
                            const newAction = {
                              label: selectedNode.data.label || 'Custom Action',
                              code: data.code
                            };
                            updateNodeData('action', newAction);
                            input.value = '';
                          }
                        } catch (error: any) {
                          alert(`❌ Errore: ${error.message}`);
                        }
                      }}
                      className="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold transition"
                    >
                      ✨ Genera
                    </button>
                  </div>
                  
                  <textarea
                    value={typeof selectedNode.data.action === 'object' && selectedNode.data.action !== null ? (selectedNode.data.action.code || '') : ''}
                    onChange={(e) => {
                      const newAction = {
                        label: selectedNode.data.label || 'Custom Action',
                        code: e.target.value
                      };
                      updateNodeData('action', newAction);
                    }}
                    className="w-full px-3 py-2 border-2 border-indigo-200 rounded-lg font-mono text-sm min-h-[200px]"
                    placeholder={`// Esempio:\nconst year = new Date().getFullYear();\nconst age = year - parseInt(temp.birth_year);\ntemp.age = age;\ntemp.is_adult = age >= 18;`}
                  />
                </div>
              )}

              {selectedNode.type === 'condition' && (
                <div className="bg-gradient-to-r from-yellow-100 to-yellow-50 p-4 rounded-xl border-2 border-yellow-300">
                  <label className="block text-sm font-bold text-yellow-800 mb-2">🔀 Condizione</label>
                  <VariableAutocomplete
                    value={selectedNode.data.variable || ''}
                    onChange={(value) => updateNodeData('variable', value)}
                    availableVariables={getAvailableVariables()}
                    placeholder="Nome variabile (es: user_input)"
                    className="w-full px-2 py-2 border-2 border-yellow-200 rounded-lg mb-3 text-sm"
                  />
                  <label className="block text-sm font-bold text-yellow-800 mb-2">Valore da confrontare</label>
                  <VariableAutocomplete
                    value={selectedNode.data.value || ''}
                    onChange={(value) => updateNodeData('value', value)}
                    availableVariables={getAvailableVariables()}
                    placeholder="es: Francesco o {{altra_variabile}}"
                    className="w-full px-2 py-2 border-2 border-yellow-200 rounded-lg text-sm"
                  />
                  <p className="text-xs text-yellow-600 mt-2">
                    💡 Puoi usare un valore fisso (es: "Francesco") o una variabile <code className="bg-yellow-200 px-1 rounded">{'{{variabile}}'}</code>
                  </p>
                  <div className="mt-3 text-xs text-yellow-700">
                    💡 Collegare 2 edge: il primo se TRUE, il secondo se FALSE
                  </div>
                </div>
              )}

              {selectedNode.type === 'case' && (
                <div className="bg-gradient-to-r from-amber-100 to-amber-50 p-4 rounded-xl border-2 border-amber-300">
                  <label className="block text-sm font-bold text-amber-800 mb-2">🔢 Case/Switch</label>
                  <VariableAutocomplete
                    value={selectedNode.data.variable || ''}
                    onChange={(value) => updateNodeData('variable', value)}
                    availableVariables={getAvailableVariables()}
                    placeholder="Nome variabile (es: user_input_city)"
                    className="w-full px-3 py-2 border-2 border-amber-200 rounded-lg mb-3 pr-32"
                  />
                  
                  <div className="flex items-center justify-between mb-2 mt-4">
                    <label className="block text-sm font-bold text-amber-800">
                      📝 Labels per i rami ({(selectedNode.data.cases || []).length})
                    </label>
                    <button
                      onClick={() => {
                        const cases = [...(selectedNode.data.cases || []), { label: '', value: '' }];
                        updateNodeData('cases', cases);
                      }}
                      className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-bold shadow-sm"
                      title="Aggiungi case"
                    >
                      + Aggiungi
                    </button>
                  </div>
                  
                  <div className="text-xs text-amber-700 mb-2">
                    Ogni ramo che colleghi avrà un numero. Qui puoi personalizzare le label:
                  </div>
                  
                  <div className="max-h-72 overflow-y-auto space-y-2 mb-3">
                    {(selectedNode.data.cases || []).map((caseItem: any, i: number) => (
                      <div key={i} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-amber-200">
                        <span className="text-xs font-bold text-amber-700 w-14 shrink-0">Case {i + 1}:</span>
                        <input
                          type="text"
                          value={caseItem.label || ''}
                          onChange={(e) => {
                            const cases = [...(selectedNode.data.cases || [])];
                            cases[i] = { label: e.target.value, value: e.target.value };
                            updateNodeData('cases', cases);
                          }}
                          className="flex-1 min-w-0 px-2 py-1 border border-amber-200 rounded text-xs"
                          placeholder={`Label ${i + 1}`}
                        />
                        <button
                          onClick={() => {
                            const cases = (selectedNode.data.cases || []).filter((_: any, idx: number) => idx !== i);
                            updateNodeData('cases', cases);
                          }}
                          className="shrink-0 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded text-sm font-bold flex items-center justify-center"
                          title="Rimuovi case"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {(selectedNode.data.cases || []).length === 0 && (
                    <p className="text-xs text-amber-600 italic mb-2">Nessun case definito. Clicca "+ Aggiungi"</p>
                  )}
                  
                  {/* Bottone Salva Case */}
                  {(selectedNode.data.cases || []).length > 0 && (
                    <button
                      onClick={() => {
                        // Forza update del nodo
                        const currentCases = selectedNode.data.cases || [];
                        setNodes((nds) =>
                          nds.map((node) =>
                            node.id === selectedNode.id
                              ? { ...node, data: { ...node.data, cases: [...currentCases] } }
                              : node
                          )
                        );
                        // Feedback visivo
                        alert(`✅ ${currentCases.length} case salvati!`);
                      }}
                      className="w-full mt-3 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-bold shadow-md transition-all"
                    >
                      💾 Salva Case ({(selectedNode.data.cases || []).length})
                    </button>
                  )}
                  
                  <div className="mt-3 text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200">
                    💡 Collega N rami per N casi. Il backend confronterà la variabile con ciascun case.
                  </div>
                </div>
              )}

              {selectedNode.type === 'json_condition' && (
                <div className="bg-gradient-to-r from-emerald-100 to-green-50 p-4 rounded-xl border-2 border-emerald-300">
                  <label className="block text-sm font-bold text-emerald-800 mb-2">🎯 JsonCondition Configuration</label>
                  
                  {/* Variabile Input */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-emerald-700 mb-1">📥 Variabile Input</label>
                    <VariableAutocomplete
                      value={selectedNode.data.variable || ''}
                      onChange={(value) => updateNodeData('variable', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="Nome variabile (es: user_input)"
                      className="w-full px-3 py-2 border-2 border-emerald-200 rounded-lg mb-3 pr-32"
                    />
                  </div>

                  {/* Prompt Base */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-emerald-700 mb-1">💬 Prompt Base</label>
                    <VariableAutocomplete
                      value={selectedNode.data.prompt || ''}
                      onChange={(value) => updateNodeData('prompt', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="Inserisci il prompt base che descrive il contesto e l'obiettivo..."
                      className="w-full px-3 py-2 border-2 border-emerald-300 rounded-lg bg-white"
                      multiline={true}
                      rows={6}
                    />
                    <p className="text-xs text-emerald-600 mt-1">
                      💡 Questo prompt descrive il contesto e l'obiettivo. La parte JSON verrà aggiunta automaticamente quando clicchi "Generate Json output Part". Usa <code className="bg-emerald-200 px-1 rounded">{'{{variabile}}'}</code> per inserire variabili.
                    </p>
                  </div>

                  {/* Generate Json output Part Button */}
                  <div className="mb-3">
                    <button
                      type="button"
                      onClick={async () => {
                        const cases = selectedNode.data.cases || [];
                        if (cases.length === 0) {
                          alert('❌ Aggiungi almeno un case prima di generare la parte JSON!');
                          return;
                        }

                        // Genera la parte JSON del prompt usando LLM
                        try {
                          const casesDescription = cases.map((c: any, i: number) => 
                            `Case ${i+1}: ${c.label || c.value || ''} (valore: ${c.value || ''})`
                          ).join('\n');

                          const generatePrompt = `Sei un esperto di prompt engineering per LLM.

Devi generare la parte di prompt che istruisce un LLM a restituire un JSON con la struttura:
{
  "matched_case": <numero_case>  // Numero del case che corrisponde (1-based, 1 = primo case)
}

CASI DISPONIBILI:
${casesDescription}

Genera SOLO la parte di prompt che:
1. Spiega all'LLM come analizzare l'input dell'utente
2. Spiega come determinare quale case corrisponde
3. Istruisce l'LLM a restituire SOLO un JSON con la struttura sopra

Sii chiaro, conciso e specifico. Non includere esempi di input utente, solo le istruzioni per l'LLM.`;

                          const response = await fetch(`${API_URL}/flow/generate-json-prompt`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              prompt: generatePrompt,
                              model: 'llama-3.3-70b-versatile',
                            }),
                          });

                          if (!response.ok) {
                            const errorData = await response.json().catch(() => ({ detail: 'Errore sconosciuto' }));
                            throw new Error(errorData.detail || 'Errore nella generazione del prompt JSON');
                          }

                          const data = await response.json();
                          const jsonPart = data.content || '';

                          // Aggiungi la parte JSON al prompt esistente
                          const currentPrompt = selectedNode.data.prompt || '';
                          const newPrompt = currentPrompt 
                            ? `${currentPrompt}\n\n${jsonPart}`
                            : jsonPart;
                          
                          updateNodeData('prompt', newPrompt);
                          alert('✅ Parte JSON generata e aggiunta al prompt!');
                        } catch (error) {
                          console.error('Errore generazione prompt JSON:', error);
                          alert('❌ Errore nella generazione del prompt JSON. Controlla la console.');
                        }
                      }}
                      className="w-full px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-bold shadow-sm transition-colors"
                    >
                      🎯 Generate Json output Part
                    </button>
                    <p className="text-xs text-emerald-600 mt-1">
                      💡 Genera automaticamente la parte del prompt che istruisce l'LLM a restituire JSON. Aggiungi prima i case qui sotto!
                    </p>
                  </div>

                  {/* Cases */}
                  <div className="flex items-center justify-between mb-2 mt-4">
                    <label className="block text-sm font-bold text-emerald-800">
                      📝 Cases ({(selectedNode.data.cases || []).length})
                    </label>
                    <button
                      onClick={() => {
                        const cases = [...(selectedNode.data.cases || []), { label: '', value: '' }];
                        updateNodeData('cases', cases);
                      }}
                      className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-bold shadow-sm"
                      title="Aggiungi case"
                    >
                      + Aggiungi
                    </button>
                  </div>
                  
                  <div className="text-xs text-emerald-700 mb-2">
                    Ogni ramo che colleghi avrà un numero. Qui puoi personalizzare le label e i valori:
                  </div>
                  
                  <div className="max-h-72 overflow-y-auto space-y-2 mb-3">
                    {(selectedNode.data.cases || []).map((caseItem: any, i: number) => (
                      <div key={i} className="flex flex-col gap-2 bg-white p-2 rounded-lg border border-emerald-200">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-emerald-700 w-16">Case {i + 1}:</span>
                          <input
                            type="text"
                            value={caseItem.label || ''}
                            onChange={(e) => {
                              const cases = [...(selectedNode.data.cases || [])];
                              cases[i] = { ...cases[i], label: e.target.value };
                              updateNodeData('cases', cases);
                            }}
                            className="flex-1 px-2 py-1 border border-emerald-200 rounded text-xs"
                            placeholder={`Label ${i + 1} (es: Roma, Milano...)`}
                          />
                          <button
                            onClick={() => {
                              const cases = (selectedNode.data.cases || []).filter((_: any, idx: number) => idx !== i);
                              updateNodeData('cases', cases);
                            }}
                            className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-bold"
                            title="Rimuovi case"
                          >
                            ×
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-emerald-700 w-16">Valore:</span>
                          <input
                            type="text"
                            value={caseItem.value || ''}
                            onChange={(e) => {
                              const cases = [...(selectedNode.data.cases || [])];
                              cases[i] = { ...cases[i], value: e.target.value };
                              updateNodeData('cases', cases);
                            }}
                            className="flex-1 px-2 py-1 border border-emerald-200 rounded text-xs"
                            placeholder={`Valore per matching (es: roma, milano...)`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {(selectedNode.data.cases || []).length === 0 && (
                    <p className="text-xs text-emerald-600 italic mb-2">Nessun case definito. Clicca "+ Aggiungi"</p>
                  )}
                  
                  <div className="mt-3 text-xs text-emerald-700 bg-emerald-50 p-2 rounded border border-emerald-200">
                    💡 Collega N rami per N casi. L'LLM determinerà quale case corrisponde all'input dell'utente.
                  </div>
                </div>
              )}

              {selectedNode.type === 'llm_case' && (
                <div className="bg-gradient-to-r from-cyan-100 to-blue-50 p-4 rounded-xl border-2 border-cyan-300">
                  <label className="block text-sm font-bold text-cyan-800 mb-2">🤖 LLM-Case Configuration</label>
                  
                  {/* Variabile Input */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-cyan-700 mb-1">📥 Variabile Input</label>
                    <VariableAutocomplete
                      value={selectedNode.data.variable || ''}
                      onChange={(value) => updateNodeData('variable', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="Nome variabile (es: user_input)"
                      className="w-full px-3 py-2 border-2 border-cyan-200 rounded-lg mb-3 pr-32"
                    />
                  </div>

                  {/* Prompt Base */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-cyan-700 mb-1">💬 Prompt Base</label>
                    <VariableAutocomplete
                      value={selectedNode.data.prompt || ''}
                      onChange={(value) => updateNodeData('prompt', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="Inserisci il prompt base che descrive il contesto e l'obiettivo..."
                      className="w-full px-3 py-2 border-2 border-cyan-300 rounded-lg bg-white"
                      multiline={true}
                      rows={6}
                    />
                    <p className="text-xs text-cyan-600 mt-1">
                      💡 Questo prompt descrive il contesto e l'obiettivo. La parte JSON verrà aggiunta automaticamente quando clicchi "Generate Json output Part". Usa <code className="bg-cyan-200 px-1 rounded">{'{{variabile}}'}</code> per inserire variabili.
                    </p>
                  </div>

                  {/* Generate Json output Part Button */}
                  <div className="mb-3">
                    <button
                      type="button"
                      onClick={async () => {
                        const cases = selectedNode.data.cases || [];
                        if (cases.length === 0) {
                          alert('❌ Aggiungi almeno un case prima di generare la parte JSON!');
                          return;
                        }

                        // Genera la parte JSON del prompt usando LLM
                        try {
                          const casesDescription = cases.map((c: any, i: number) => 
                            `Case ${i+1}: ${c.label || c.value || ''} (valore: ${c.value || ''})`
                          ).join('\n');

                          const generatePrompt = `Sei un esperto di prompt engineering per LLM.

Devi generare la parte di prompt che istruisce un LLM a restituire un JSON con la struttura:
{
  "matched_case": <numero_case>  // Numero del case che corrisponde (1-based, 1 = primo case)
}

CASI DISPONIBILI:
${casesDescription}

Genera SOLO la parte di prompt che:
1. Spiega all'LLM come analizzare l'input dell'utente
2. Spiega come determinare quale case corrisponde
3. Istruisce l'LLM a restituire SOLO un JSON con la struttura sopra

Sii chiaro, conciso e specifico. Non includere esempi di input utente, solo le istruzioni per l'LLM.`;

                          const response = await fetch(`${API_URL}/flow/generate-json-prompt`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              prompt: generatePrompt,
                              model: 'llama-3.3-70b-versatile',
                            }),
                          });

                          if (!response.ok) {
                            const errorData = await response.json().catch(() => ({ detail: 'Errore sconosciuto' }));
                            throw new Error(errorData.detail || 'Errore nella generazione del prompt JSON');
                          }

                          const data = await response.json();
                          const jsonPart = data.content || '';

                          // Aggiungi la parte JSON al prompt esistente
                          const currentPrompt = selectedNode.data.prompt || '';
                          const newPrompt = currentPrompt 
                            ? `${currentPrompt}\n\n${jsonPart}`
                            : jsonPart;
                          
                          updateNodeData('prompt', newPrompt);
                          alert('✅ Parte JSON generata e aggiunta al prompt!');
                        } catch (error) {
                          console.error('Errore generazione prompt JSON:', error);
                          alert('❌ Errore nella generazione del prompt JSON. Controlla la console.');
                        }
                      }}
                      className="w-full px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-bold shadow-sm transition-colors"
                    >
                      🎯 Generate Json output Part
                    </button>
                    <p className="text-xs text-cyan-600 mt-1">
                      💡 Genera automaticamente la parte del prompt che istruisce l'LLM a restituire JSON. Aggiungi prima i case qui sotto!
                    </p>
                  </div>

                  {/* Cases */}
                  <div className="flex items-center justify-between mb-2 mt-4">
                    <label className="block text-sm font-bold text-cyan-800">
                      📝 Cases ({(selectedNode.data.cases || []).length})
                    </label>
                    <button
                      onClick={() => {
                        const cases = [...(selectedNode.data.cases || []), { label: '', value: '' }];
                        updateNodeData('cases', cases);
                      }}
                      className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-bold shadow-sm"
                      title="Aggiungi case"
                    >
                      + Aggiungi
                    </button>
                  </div>
                  
                  <div className="text-xs text-cyan-700 mb-2">
                    Ogni ramo che colleghi avrà un numero. Qui puoi personalizzare le label e i valori:
                  </div>
                  
                  <div className="max-h-72 overflow-y-auto space-y-2 mb-3">
                    {(selectedNode.data.cases || []).map((caseItem: any, i: number) => (
                      <div key={i} className="flex flex-col gap-2 bg-white p-2 rounded-lg border border-cyan-200">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-cyan-700 w-16">Case {i + 1}:</span>
                          <input
                            type="text"
                            value={caseItem.label || ''}
                            onChange={(e) => {
                              const cases = [...(selectedNode.data.cases || [])];
                              cases[i] = { ...cases[i], label: e.target.value };
                              updateNodeData('cases', cases);
                            }}
                            className="flex-1 px-2 py-1 border border-cyan-200 rounded text-xs"
                            placeholder={`Label ${i + 1} (es: Roma, Milano...)`}
                          />
                          <button
                            onClick={() => {
                              const cases = (selectedNode.data.cases || []).filter((_: any, idx: number) => idx !== i);
                              updateNodeData('cases', cases);
                            }}
                            className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-bold"
                            title="Rimuovi case"
                          >
                            ×
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-cyan-700 w-16">Valore:</span>
                          <input
                            type="text"
                            value={caseItem.value || ''}
                            onChange={(e) => {
                              const cases = [...(selectedNode.data.cases || [])];
                              cases[i] = { ...cases[i], value: e.target.value };
                              updateNodeData('cases', cases);
                            }}
                            className="flex-1 px-2 py-1 border border-cyan-200 rounded text-xs"
                            placeholder={`Valore per matching (es: roma, milano...)`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {(selectedNode.data.cases || []).length === 0 && (
                    <p className="text-xs text-cyan-600 italic mb-2">Nessun case definito. Clicca "+ Aggiungi"</p>
                  )}
                  
                  <div className="mt-3 text-xs text-cyan-700 bg-cyan-50 p-2 rounded border border-cyan-200">
                    💡 Collega N rami per N casi. L'LLM determinerà quale case corrisponde all'input dell'utente.
                  </div>
                </div>
              )}

              {/* LLM SIMPLE NODE - Per elaborazione diretta */}
              {selectedNode.type === 'llm_simple' && (
                <div className="bg-gradient-to-r from-violet-100 to-purple-50 p-4 rounded-xl border-2 border-violet-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🧠</span>
                    <label className="text-lg font-bold text-violet-800">LLM Simple</label>
                  </div>
                  <p className="text-xs text-violet-600 mb-3">
                    Elaborazione diretta con LLM. Ideale per riassunti, trasformazioni, generazione testo.
                  </p>

                  {/* System Prompt */}
                  <div>
                    <label className="block text-xs font-bold text-violet-700 mb-1">📋 System Prompt (Istruzioni)</label>
                    <textarea
                      value={selectedNode.data.system_prompt || ''}
                      onChange={(e) => updateNodeData('system_prompt', e.target.value)}
                      placeholder="Sei un giornalista esperto. Riassumi le notizie in modo chiaro e conciso."
                      className="w-full px-3 py-2 border-2 border-violet-200 rounded text-sm"
                      rows={3}
                    />
                  </div>

                  {/* User Prompt Template */}
                  <div>
                    <label className="block text-xs font-bold text-violet-700 mb-1">💬 User Prompt (con variabili)</label>
                    <VariableAutocomplete
                      value={selectedNode.data.user_prompt || ''}
                      onChange={(value) => updateNodeData('user_prompt', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="Ecco le notizie da elaborare: {rss_items}&#10;&#10;Genera un riassunto di massimo 300 parole."
                      className="w-full px-3 py-2 border-2 border-violet-200 rounded text-sm"
                      multiline={true}
                      rows={5}
                    />
                    <p className="text-xs text-gray-500 mt-1">Usa {'{variabile}'} per inserire dati dinamici</p>
                  </div>

                  {/* Model Selection */}
                  <div>
                    <label className="block text-xs font-bold text-violet-700 mb-1">🤖 Modello LLM</label>
                    <select
                      value={selectedNode.data.model || 'groq/llama-3.3-70b-versatile'}
                      onChange={(e) => updateNodeData('model', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-violet-200 rounded text-sm"
                    >
                      <optgroup label="Groq (Veloce)">
                        <option value="groq/llama-3.3-70b-versatile">Llama 3.3 70B (Consigliato)</option>
                        <option value="groq/llama-3.1-8b-instant">Llama 3.1 8B (Velocissimo)</option>
                        <option value="groq/mixtral-8x7b-32768">Mixtral 8x7B</option>
                      </optgroup>
                      <optgroup label="OpenAI">
                        <option value="openai/gpt-4o-mini">GPT-4o Mini</option>
                        <option value="openai/gpt-4o">GPT-4o</option>
                      </optgroup>
                      <optgroup label="Anthropic">
                        <option value="anthropic/claude-3-haiku">Claude 3 Haiku (Veloce)</option>
                        <option value="anthropic/claude-3-sonnet">Claude 3 Sonnet</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* Temperature */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-violet-700 mb-1">🌡️ Temperature</label>
                      <input
                        type="number"
                        value={selectedNode.data.temperature ?? 0.7}
                        onChange={(e) => updateNodeData('temperature', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border-2 border-violet-200 rounded text-sm"
                        min={0}
                        max={1}
                        step={0.1}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-violet-700 mb-1">📏 Max Tokens</label>
                      <input
                        type="number"
                        value={selectedNode.data.max_tokens || 1000}
                        onChange={(e) => updateNodeData('max_tokens', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border-2 border-violet-200 rounded text-sm"
                        min={100}
                        max={4000}
                      />
                    </div>
                  </div>

                  {/* Output Variable */}
                  <div>
                    <label className="block text-xs font-bold text-violet-700 mb-1">📤 Salva Risultato in</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || 'llm_result'}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      placeholder="llm_result"
                      className="w-full px-3 py-2 border-2 border-violet-200 rounded text-sm font-mono"
                    />
                  </div>

                  <div className="bg-violet-50 p-3 rounded-lg border border-violet-200">
                    <p className="text-xs text-violet-700">
                      💡 <strong>Esempio per News:</strong><br/>
                      System: "Sei un giornalista. Riassumi le notizie."<br/>
                      User: "Notizie: {'{rss_items}'} - Genera un riassunto."
                    </p>
                  </div>
                </div>
              )}

              {/* LLM Loop Properties */}
              {selectedNode.type === 'llm_loop' && (
                <div className="bg-gradient-to-r from-indigo-100 to-blue-50 p-4 rounded-xl border-2 border-indigo-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🔄</span>
                    <label className="text-lg font-bold text-indigo-800">LLM Loop</label>
                  </div>
                  <p className="text-xs text-indigo-600 mb-3">
                    Conversazione multi-turno con LLM. L'utente può fare fino a N domande, poi il bot saluta.
                  </p>

                  {/* Welcome Message */}
                  <div>
                    <label className="block text-xs font-bold text-indigo-700 mb-1">👋 Messaggio di Benvenuto</label>
                    <input
                      type="text"
                      value={selectedNode.data.welcome_message || ''}
                      onChange={(e) => updateNodeData('welcome_message', e.target.value)}
                      placeholder="Ciao! Come posso aiutarti?"
                      className="w-full px-3 py-2 border-2 border-indigo-200 rounded text-sm"
                    />
                  </div>

                  {/* System Prompt */}
                  <div>
                    <label className="block text-xs font-bold text-indigo-700 mb-1">📋 System Prompt</label>
                    <textarea
                      value={selectedNode.data.system_prompt || ''}
                      onChange={(e) => updateNodeData('system_prompt', e.target.value)}
                      placeholder="Sei un assistente gentile e competente. Rispondi in modo chiaro e utile."
                      className="w-full px-3 py-2 border-2 border-indigo-200 rounded text-sm"
                      rows={3}
                    />
                  </div>

                  {/* Max Interactions */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-indigo-700 mb-1">🔢 Max Domande</label>
                      <input
                        type="number"
                        value={selectedNode.data.max_interactions ?? 5}
                        onChange={(e) => updateNodeData('max_interactions', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border-2 border-indigo-200 rounded text-sm"
                        min={1}
                        max={20}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-indigo-700 mb-1">🤖 Modello</label>
                      <select
                        value={selectedNode.data.model || 'groq/llama-3.3-70b-versatile'}
                        onChange={(e) => updateNodeData('model', e.target.value)}
                        className="w-full px-3 py-2 border-2 border-indigo-200 rounded text-sm"
                      >
                        <option value="groq/llama-3.3-70b-versatile">Llama 3.3 70B</option>
                        <option value="groq/llama-3.1-8b-instant">Llama 3.1 8B (Veloce)</option>
                        <option value="openai/gpt-4o-mini">GPT-4o Mini</option>
                      </select>
                    </div>
                  </div>

                  {/* Exit Phrases */}
                  <div>
                    <label className="block text-xs font-bold text-indigo-700 mb-1">⛔ Frasi di Uscita (separate da virgola)</label>
                    <input
                      type="text"
                      value={(selectedNode.data.exit_phrases || ['no', 'basta', 'fine', 'grazie']).join(', ')}
                      onChange={(e) => updateNodeData('exit_phrases', e.target.value.split(',').map((s: string) => s.trim()))}
                      placeholder="no, basta, fine, grazie"
                      className="w-full px-3 py-2 border-2 border-indigo-200 rounded text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Se l'utente scrive una di queste, il loop termina</p>
                  </div>

                  {/* Goodbye Message */}
                  <div>
                    <label className="block text-xs font-bold text-indigo-700 mb-1">👋 Messaggio di Arrivederci</label>
                    <input
                      type="text"
                      value={selectedNode.data.goodbye_message || ''}
                      onChange={(e) => updateNodeData('goodbye_message', e.target.value)}
                      placeholder="Grazie per la conversazione! Arrivederci!"
                      className="w-full px-3 py-2 border-2 border-indigo-200 rounded text-sm"
                    />
                  </div>

                  {/* Output Variable */}
                  <div>
                    <label className="block text-xs font-bold text-indigo-700 mb-1">📤 Variabile Output</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || 'llm_loop_response'}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      placeholder="llm_loop_response"
                      className="w-full px-3 py-2 border-2 border-indigo-200 rounded text-sm font-mono"
                    />
                  </div>

                  <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-200">
                    <p className="text-xs text-indigo-700">
                      💡 <strong>Comportamento:</strong><br/>
                      1. Mostra messaggio di benvenuto<br/>
                      2. L'utente fa una domanda, il bot risponde con LLM<br/>
                      3. Ripete fino a max domande o se l'utente dice "no/basta"<br/>
                      4. Mostra messaggio di arrivederci e passa al nodo successivo
                    </p>
                  </div>
                </div>
              )}

              {selectedNode.type === 'llm_qualifier' && (
                <div className="bg-gradient-to-r from-pink-100 to-rose-50 p-4 rounded-xl border-2 border-pink-300">
                  <label className="block text-sm font-bold text-pink-800 mb-2">🎯 LLM Qualifier Configuration</label>
                  
                  {/* Variabile Input */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-pink-700 mb-1">📥 Variabile Input</label>
                    <VariableAutocomplete
                      value={selectedNode.data.input_variable || ''}
                      onChange={(value) => updateNodeData('input_variable', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="Seleziona variabile (es: user_input)"
                      className="w-full px-3 py-2 border-2 border-pink-200 rounded-lg"
                    />
                    <p className="text-xs text-pink-600 mt-1">
                      💡 Variabile che contiene l'input dell'utente per la conversazione LLM
                    </p>
                  </div>

                  {/* Prompt Personalizzato */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-bold text-pink-700">💬 Prompt Personalizzato</label>
                      <button
                        type="button"
                        onClick={() => {
                          const suggestionText = `ESEMPIO per Agenzia Immobiliare:

Sei un assistente amichevole di un'agenzia immobiliare. Intrattieni l'utente in modo naturale e piacevole, rompendo il ghiaccio prima di approcciarlo con obiettivi più specifici.

CONTESTO BUSINESS:
- Siamo un'agenzia immobiliare specializzata in vendita e affitto di case/appartamenti
- L'utente è 'operativo' quando parla di: cercare casa, vendere casa, affittare, vedere appartamenti, prezzi immobiliari, zone specifiche
- L'utente NON è operativo quando parla di: argomenti generici, tempo, sport, hobby non correlati

ESEMPIO per Centro Benessere:

Sei un assistente amichevole di un centro benessere. Intrattieni l'utente in modo naturale e piacevole.

CONTESTO BUSINESS:
- Siamo un centro benessere che offre massaggi, trattamenti estetici, spa
- L'utente è 'operativo' quando parla di: prenotare massaggio, trattamenti, orari, prezzi servizi benessere
- L'utente NON è operativo quando parla di: case, immobili, argomenti non correlati al benessere

IMPORTANTE: Definisci chiaramente nel prompt:
1. Il tuo dominio di business
2. Cosa significa 'essere operativo' nel tuo contesto
3. Cosa è rilevante vs non rilevante

Le istruzioni per il formato JSON verranno aggiunte automaticamente.`;
                          updateNodeData('prompt', suggestionText);
                        }}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-lg transition-colors bg-pink-200 hover:bg-pink-300 text-pink-800 border border-pink-300"
                        title="Inserisci il suggerimento nel campo prompt"
                      >
                        <Edit className="w-3 h-3" />
                        <span>Usa Suggerimento</span>
                      </button>
                    </div>
                    <VariableAutocomplete
                      value={selectedNode.data.prompt || ''}
                      onChange={(value) => updateNodeData('prompt', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="Clicca 'Usa Suggerimento' per inserire un esempio di prompt personalizzato..."
                      className="w-full px-3 py-2 border-2 border-pink-300 rounded-lg bg-white"
                      multiline={true}
                      rows={10}
                    />
                    <p className="text-xs text-pink-600 mt-1">
                      💡 <strong>Personalizza il prompt con il contesto del tuo business!</strong> Definisci chiaramente quando l'utente è "operativo" (rilevante per il tuo business) vs quando sta solo chiacchierando. Le istruzioni JSON verranno aggiunte automaticamente se non le includi. Usa <code className="bg-pink-200 px-1 rounded">{'{{variabile}}'}</code> per inserire variabili.
                    </p>
                  </div>

                  {/* Numero Interazioni */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-pink-700 mb-1">🔢 Numero Interazioni</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={selectedNode.data.max_interactions || 3}
                      onChange={(e) => updateNodeData('max_interactions', parseInt(e.target.value) || 3)}
                      className="w-full px-3 py-2 border-2 border-pink-200 rounded-lg"
                    />
                    <p className="text-xs text-pink-600 mt-1">
                      💡 Numero massimo di domande/risposte prima di passare al nodo successivo
                    </p>
                  </div>

                  {/* Variabile Output */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-pink-700 mb-1">📤 Variabile Output (Risposta Testuale)</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || ''}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-pink-200 rounded-lg"
                      placeholder="es: llm_qualifier_response"
                    />
                    <p className="text-xs text-pink-600 mt-1">
                      💡 Nome della variabile dove salvare la risposta testuale del modello LLM
                    </p>
                  </div>

                  {/* Variabile Decisione */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-pink-700 mb-1">🎯 Variabile Decisione (Yes/No)</label>
                    <input
                      type="text"
                      value={selectedNode.data.decision_variable || ''}
                      onChange={(e) => updateNodeData('decision_variable', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-pink-200 rounded-lg"
                      placeholder="es: llm_qualifier_decision"
                    />
                    <p className="text-xs text-pink-600 mt-1">
                      💡 Nome della variabile dove salvare la decisione Yes/No (determina la direzione del flusso)
                    </p>
                  </div>

                  <div className="mt-4 p-3 bg-pink-50 border-2 border-pink-200 rounded-lg">
                    <p className="text-xs font-bold text-pink-800 mb-2">📋 Formato Risposta LLM</p>
                    <p className="text-xs text-pink-700">
                      L'LLM deve generare due risposte:
                    </p>
                    <ol className="text-xs text-pink-700 mt-2 ml-4 list-decimal">
                      <li>Risposta testuale per l'utente</li>
                      <li>JSON con {"{"}"decision": "Yes"{"}"} o {"{"}"decision": "No"{"}"}</li>
                    </ol>
                    <p className="text-xs text-pink-600 mt-2">
                      💡 Collega 2 edge: il primo per Yes (verde), il secondo per No (rosso)
                    </p>
                  </div>
                </div>
              )}

              {selectedNode.type === 'IntentLLM' && (
                <div className="bg-gradient-to-r from-purple-100 to-violet-50 p-4 rounded-xl border-2 border-purple-300">
                  <label className="block text-sm font-bold text-purple-800 mb-2">🎯 Intent LLM Configuration</label>
                  
                  {/* 🪄 AI WIZARD - Generazione Automatica */}
                  <div className="mb-4 bg-gradient-to-r from-yellow-50 to-amber-50 p-3 rounded-lg border-2 border-yellow-300">
                    <label className="block text-xs font-bold text-amber-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🪄</span>
                      AI Wizard - Genera Configurazione Automaticamente
                    </label>
                    <textarea
                      value={selectedNode.data.wizard_description || ''}
                      onChange={(e) => updateNodeData('wizard_description', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-yellow-300 rounded-lg text-sm mb-2"
                      rows={4}
                      placeholder="Descrivi cosa vuoi fare in linguaggio naturale:&#10;&#10;Es: 'Voglio un chatbot immobiliare che capisca se l'utente vuole comprare a breve termine (entro 3 mesi), comprare a medio-lungo termine, affittare, o solo avere informazioni. Deve parlare in modo amichevole e fare al massimo 10 domande.'"
                    />
                    <button
                      type="button"
                      onClick={async (e) => {
                        const description = selectedNode.data.wizard_description;
                        if (!description || description.trim().length < 20) {
                          alert('❌ Descrivi cosa vuoi fare (almeno 20 caratteri)!');
                          return;
                        }

                        // Mostra loading
                        const button = e.currentTarget as HTMLButtonElement;
                        const originalText = button.innerHTML;
                        button.disabled = true;
                        button.innerHTML = '<span class="animate-pulse">⏳ Generando...</span>';

                        console.log('🪄 Wizard: Inizio generazione config...');
                        console.log('📝 Descrizione:', description);
                        console.log('🌐 API URL:', `${API_URL}/flow/generate-intent-config`);

                        try {
                          const response = await fetch(`${API_URL}/flow/generate-intent-config`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              description: description,
                              model: 'llama-3.3-70b-versatile',
                            }),
                          });

                          console.log('📥 Response status:', response.status);

                          if (!response.ok) {
                            const errorData = await response.json().catch(() => ({ detail: 'Errore sconosciuto' }));
                            console.error('❌ Response error:', errorData);
                            throw new Error(errorData.detail || `HTTP ${response.status}`);
                          }

                          const data = await response.json();
                          console.log('✅ Data ricevuta:', data);
                          
                          // Popola i campi con i valori generati
                          if (data.prompt) {
                            console.log('📝 Setting prompt:', data.prompt.substring(0, 100) + '...');
                            updateNodeData('prompt', data.prompt);
                          }
                          if (data.intents) {
                            console.log('🎯 Setting intents:', data.intents);
                            updateNodeData('intents', data.intents);
                          }
                          if (data.max_interactions) {
                            console.log('🔢 Setting max_interactions:', data.max_interactions);
                            updateNodeData('max_interactions', data.max_interactions);
                          }
                          
                          alert('✅ Configurazione generata! Scroll giù per vedere i campi compilati.');
                        } catch (error) {
                          console.error('❌ Errore generazione config:', error);
                          alert(`❌ Errore: ${error instanceof Error ? error.message : 'Errore sconosciuto'}\n\nControlla la console (F12) per dettagli.`);
                        } finally {
                          // Ripristina bottone
                          button.disabled = false;
                          button.innerHTML = originalText;
                        }
                      }}
                      className="w-full px-4 py-2 bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-white rounded-lg font-bold shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-xl">⚡</span>
                      Genera Automaticamente con AI
                    </button>
                    <p className="text-xs text-amber-700 mt-2 italic">
                      💡 L'AI analizzerà la tua descrizione e compilerà automaticamente: prompt, intents, e max_interactions. Poi potrai modificarli manualmente!
                    </p>
                  </div>

                  <div className="border-t-2 border-purple-200 pt-3 mb-3">
                    <p className="text-xs text-purple-600 italic mb-3">
                      ⬇️ Campi generati automaticamente (o modifica manualmente)
                    </p>
                  </div>
                  
                  {/* Variabile Input */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-purple-700 mb-1">📥 Variabile Input</label>
                    <VariableAutocomplete
                      value={selectedNode.data.input_variable || ''}
                      onChange={(value) => updateNodeData('input_variable', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="Seleziona variabile (es: user_input_01)"
                      className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg"
                    />
                    <p className="text-xs text-purple-600 mt-1">
                      💡 Variabile che contiene l'input dell'utente per la rilevazione intenzioni
                    </p>
                  </div>

                  {/* Prompt Personalizzato */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-purple-700 mb-1">💬 Prompt Personalizzato</label>
                    <VariableAutocomplete
                      value={selectedNode.data.prompt || ''}
                      onChange={(value) => updateNodeData('prompt', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="Inserisci il prompt personalizzato per la rilevazione intenzioni..."
                      className="w-full px-3 py-2 border-2 border-purple-300 rounded-lg bg-white"
                      multiline={true}
                      rows={6}
                    />
                    <p className="text-xs text-purple-600 mt-1">
                      💡 Prompt personalizzato che definisce il contesto e le intenzioni da rilevare. Usa <code className="bg-purple-200 px-1 rounded">{'{{variabile}}'}</code> per inserire variabili.
                    </p>
                  </div>

                  {/* Prompt Fisso */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-purple-700 mb-1">📋 Prompt Fisso</label>
                    <VariableAutocomplete
                      value={selectedNode.data.fixed_prompt || ''}
                      onChange={(value) => updateNodeData('fixed_prompt', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="Prompt fisso che viene sempre concatenato..."
                      className="w-full px-3 py-2 border-2 border-purple-300 rounded-lg bg-gray-50"
                      multiline={true}
                      rows={8}
                    />
                    <p className="text-xs text-purple-600 mt-1">
                      💡 Questo prompt viene sempre concatenato al prompt personalizzato sopra. Contiene le istruzioni per il formato JSON delle intenzioni.
                    </p>
                  </div>

                  {/* Intents */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-purple-700 mb-1">🎯 Intents (separati da virgola)</label>
                    <input
                      type="text"
                      value={Array.isArray(selectedNode.data.intents) ? selectedNode.data.intents.join(', ') : (selectedNode.data.intents || '')}
                      onChange={(e) => {
                        const intentsArray = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                        updateNodeData('intents', intentsArray);
                      }}
                      className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg"
                      placeholder="comprare_breve_termine, comprare_non_breve_termine, affittare, vendere, informazioni"
                    />
                    <p className="text-xs text-purple-600 mt-1">
                      💡 Lista di intenzioni separate da virgola. Es: comprare_breve_termine, comprare_non_breve_termine, affittare, vendere, informazioni
                    </p>
                  </div>

                  {/* Numero Interazioni */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-purple-700 mb-1">🔢 Numero Interazioni Massimo</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={selectedNode.data.max_interactions || 10}
                      onChange={(e) => updateNodeData('max_interactions', parseInt(e.target.value) || 10)}
                      className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg"
                    />
                    <p className="text-xs text-purple-600 mt-1">
                      💡 Numero massimo di interazioni prima di uscire verso il ramo "max-interazioni"
                    </p>
                  </div>

                  {/* Variabile Intent (Principale) */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-purple-700 mb-1">🎯 Nome Variabile di Intento</label>
                    <input
                      type="text"
                      value={selectedNode.data.intent_variable || `llm_output_intent_${(selectedNode.data.nodeNumber || 0).toString().padStart(2, '0')}`}
                      onChange={(e) => updateNodeData('intent_variable', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg"
                      placeholder="es: llm_output_intent_01 (auto-generato)"
                    />
                    <p className="text-xs text-purple-600 mt-1">
                      💡 Nome della variabile dove salvare l'intento rilevato (es: "comprare", "vendere", "affittare")
                    </p>
                    <p className="text-xs text-purple-500 mt-1 italic">
                      💡 Usa <code className="bg-purple-100 px-1 rounded">{`{{${selectedNode.data.intent_variable || `llm_output_intent_${(selectedNode.data.nodeNumber || 0).toString().padStart(2, '0')}`}}}`}</code> negli altri nodi per accedere all'intento rilevato!
                    </p>
                  </div>

                  {/* Variabile Output */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-purple-700 mb-1">📤 Variabile Output (Risposta LLM)</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || ''}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg"
                      placeholder="es: llm_response_01"
                    />
                    <p className="text-xs text-purple-600 mt-1">
                      💡 Nome della variabile dove salvare la risposta testuale del modello LLM
                    </p>
                  </div>

                  {/* Variabile Intents (JSON completo) */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-purple-700 mb-1">📊 Variabile Intents (JSON completo)</label>
                    <input
                      type="text"
                      value={selectedNode.data.intents_variable || ''}
                      onChange={(e) => updateNodeData('intents_variable', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg"
                      placeholder="es: detected_intents_01"
                    />
                    <p className="text-xs text-purple-600 mt-1">
                      💡 Nome della variabile dove salvare gli intents rilevati (JSON con stato di tutte le intenzioni)
                    </p>
                    <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-200">
                      <p className="text-xs font-semibold text-purple-800 mb-1">📌 Variabili create automaticamente:</p>
                      <ul className="text-xs text-purple-700 space-y-1 ml-2">
                        <li>• <code className="bg-purple-100 px-1 rounded">{selectedNode.data.intent_variable || `llm_output_intent_${(selectedNode.data.nodeNumber || 0).toString().padStart(2, '0')}`}</code> - Nome dell'intent rilevato (es: "comprare", "vendere") ⭐ PRINCIPALE</li>
                        <li>• <code className="bg-purple-100 px-1 rounded">{selectedNode.data.intents_variable || 'detected_intents_XX'}</code> - JSON completo con tutti gli intents</li>
                        <li>• <code className="bg-purple-100 px-1 rounded">{selectedNode.data.output_variable || 'llm_response_XX'}</code> - Risposta testuale del LLM</li>
                      </ul>
                    </div>
                  </div>

                  {/* Parametri API */}
                  <div className="mb-3">
                    <label className="block text-xs font-bold text-purple-700 mb-1">⚙️ Modello LLM</label>
                    <input
                      type="text"
                      value={selectedNode.data.model || 'openai/gpt-oss-120b'}
                      onChange={(e) => updateNodeData('model', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg"
                      placeholder="es: openai/gpt-oss-120b"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-bold text-purple-700 mb-1">🌡️ Temperature</label>
                    <input
                      type="number"
                      min="0"
                      max="2"
                      step="0.1"
                      value={selectedNode.data.temperature || 0.7}
                      onChange={(e) => updateNodeData('temperature', parseFloat(e.target.value) || 0.7)}
                      className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-bold text-purple-700 mb-1">📏 Max Tokens</label>
                    <input
                      type="number"
                      min="100"
                      max="2000"
                      value={selectedNode.data.max_tokens || 500}
                      onChange={(e) => updateNodeData('max_tokens', parseInt(e.target.value) || 500)}
                      className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg"
                    />
                  </div>

                  <div className="mt-4 p-3 bg-purple-50 border-2 border-purple-200 rounded-lg">
                    <p className="text-xs font-bold text-purple-800 mb-2">📋 Come Funziona</p>
                    <p className="text-xs text-purple-700">
                      Il nodo IntentLLM:
                    </p>
                    <ol className="text-xs text-purple-700 mt-2 ml-4 list-decimal">
                      <li>Rileva le intenzioni dall'input dell'utente</li>
                      <li>Se rileva un'intenzione, passa direttamente al ramo corrispondente (senza mostrare messaggi)</li>
                      <li>Se non rileva intenzioni, continua la conversazione fino a max_interactions</li>
                      <li>Se raggiunge max_interactions senza rilevare, passa al ramo "max-interazioni"</li>
                    </ol>
                    <p className="text-xs text-purple-600 mt-2">
                      💡 Collega N edge per N intents (ogni edge deve avere come label il nome dell'intent). Aggiungi anche un edge con label "max-interazioni" per quando si raggiunge il limite.
                    </p>
                  </div>
                </div>
              )}

              {/* ========================================
                  n8n-STYLE NODES - PANNELLI PROPRIETÀ
                  ======================================== */}

              {/* HTTP REQUEST NODE */}
              {selectedNode.type === 'http_request' && (
                <div className="bg-gradient-to-r from-sky-100 to-blue-50 p-4 rounded-xl border-2 border-sky-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🌐</span>
                    <label className="text-lg font-bold text-sky-800">HTTP Request</label>
                  </div>

                  {/* Method + URL */}
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <label className="block text-xs font-bold text-sky-700 mb-1">Method</label>
                      <select
                        value={selectedNode.data.method || 'GET'}
                        onChange={(e) => updateNodeData('method', e.target.value)}
                        className="w-full px-2 py-2 border-2 border-sky-200 rounded text-sm font-bold"
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="PATCH">PATCH</option>
                        <option value="DELETE">DELETE</option>
                        <option value="HEAD">HEAD</option>
                        <option value="OPTIONS">OPTIONS</option>
                      </select>
                    </div>
                    <div className="col-span-3">
                      <label className="block text-xs font-bold text-sky-700 mb-1">🔗 URL</label>
                      <VariableAutocomplete
                        value={selectedNode.data.url || ''}
                        onChange={(value) => updateNodeData('url', value)}
                        availableVariables={getAvailableVariables()}
                        placeholder="https://api.example.com/endpoint"
                        className="w-full px-3 py-2 border-2 border-sky-200 rounded text-sm"
                      />
                    </div>
                  </div>

                  {/* Authentication */}
                  <div>
                    <label className="block text-xs font-bold text-sky-700 mb-1">🔐 Authentication</label>
                    <select
                      value={selectedNode.data.auth_type || 'none'}
                      onChange={(e) => updateNodeData('auth_type', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-sky-200 rounded text-sm"
                    >
                      <option value="none">Nessuna</option>
                      <option value="basic">Basic Auth</option>
                      <option value="bearer">Bearer Token</option>
                      <option value="api_key">API Key (Header)</option>
                      <option value="api_key_query">API Key (Query Param)</option>
                    </select>
                  </div>

                  {selectedNode.data.auth_type === 'basic' && (
                    <div className="grid grid-cols-2 gap-2 bg-sky-50 p-3 rounded-lg">
                      <div>
                        <label className="block text-xs font-bold text-sky-700 mb-1">Username</label>
                        <input
                          type="text"
                          value={selectedNode.data.auth_username || ''}
                          onChange={(e) => updateNodeData('auth_username', e.target.value)}
                          className="w-full px-2 py-1 border border-sky-200 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-sky-700 mb-1">Password</label>
                        <input
                          type="password"
                          value={selectedNode.data.auth_password || ''}
                          onChange={(e) => updateNodeData('auth_password', e.target.value)}
                          className="w-full px-2 py-1 border border-sky-200 rounded text-sm"
                        />
                      </div>
                    </div>
                  )}

                  {selectedNode.data.auth_type === 'bearer' && (
                    <div className="bg-sky-50 p-3 rounded-lg">
                      <label className="block text-xs font-bold text-sky-700 mb-1">Bearer Token</label>
                      <VariableAutocomplete
                        value={selectedNode.data.auth_token || ''}
                        onChange={(value) => updateNodeData('auth_token', value)}
                        availableVariables={getAvailableVariables()}
                        placeholder="eyJhbGciOiJIUzI1NiIs..."
                        className="w-full px-2 py-1 border border-sky-200 rounded text-sm font-mono"
                      />
                    </div>
                  )}

                  {(selectedNode.data.auth_type === 'api_key' || selectedNode.data.auth_type === 'api_key_query') && (
                    <div className="grid grid-cols-2 gap-2 bg-sky-50 p-3 rounded-lg">
                      <div>
                        <label className="block text-xs font-bold text-sky-700 mb-1">Key Name</label>
                        <input
                          type="text"
                          value={selectedNode.data.api_key_name || 'X-API-Key'}
                          onChange={(e) => updateNodeData('api_key_name', e.target.value)}
                          placeholder="X-API-Key"
                          className="w-full px-2 py-1 border border-sky-200 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-sky-700 mb-1">Key Value</label>
                        <VariableAutocomplete
                          value={selectedNode.data.api_key_value || ''}
                          onChange={(value) => updateNodeData('api_key_value', value)}
                          availableVariables={getAvailableVariables()}
                          placeholder="your-api-key"
                          className="w-full px-2 py-1 border border-sky-200 rounded text-sm font-mono"
                        />
                      </div>
                    </div>
                  )}

                  {/* Headers */}
                  <div>
                    <label className="block text-xs font-bold text-sky-700 mb-1">📋 Headers (JSON)</label>
                    <textarea
                      value={typeof selectedNode.data.headers === 'string' ? selectedNode.data.headers : JSON.stringify(selectedNode.data.headers || {}, null, 2)}
                      onChange={(e) => {
                        try {
                          updateNodeData('headers', JSON.parse(e.target.value));
                        } catch {
                          updateNodeData('headers', e.target.value);
                        }
                      }}
                      placeholder='{"Content-Type": "application/json"}'
                      className="w-full px-3 py-2 border-2 border-sky-200 rounded text-sm font-mono"
                      rows={3}
                    />
                  </div>

                  {/* Query Parameters */}
                  <div>
                    <label className="block text-xs font-bold text-sky-700 mb-1">❓ Query Parameters (JSON)</label>
                    <textarea
                      value={typeof selectedNode.data.query_params === 'string' ? selectedNode.data.query_params : JSON.stringify(selectedNode.data.query_params || {}, null, 2)}
                      onChange={(e) => {
                        try {
                          updateNodeData('query_params', JSON.parse(e.target.value));
                        } catch {
                          updateNodeData('query_params', e.target.value);
                        }
                      }}
                      placeholder='{"page": "1", "limit": "10"}'
                      className="w-full px-3 py-2 border-2 border-sky-200 rounded text-sm font-mono"
                      rows={2}
                    />
                  </div>

                  {/* Body (for POST/PUT/PATCH) */}
                  {['POST', 'PUT', 'PATCH'].includes(selectedNode.data.method || '') && (
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-xs font-bold text-sky-700">📦 Request Body</label>
                        <select
                          value={selectedNode.data.body_type || 'json'}
                          onChange={(e) => updateNodeData('body_type', e.target.value)}
                          className="text-xs border border-sky-200 rounded px-2 py-1"
                        >
                          <option value="json">JSON</option>
                          <option value="form">Form Data</option>
                          <option value="raw">Raw Text</option>
                        </select>
                      </div>
                      <VariableAutocomplete
                        value={typeof selectedNode.data.body === 'string' ? selectedNode.data.body : JSON.stringify(selectedNode.data.body || {}, null, 2)}
                        onChange={(value) => {
                          try {
                            updateNodeData('body', JSON.parse(value));
                          } catch {
                            updateNodeData('body', value);
                          }
                        }}
                        availableVariables={getAvailableVariables()}
                        placeholder='{"key": "{{variable}}"}'
                        className="w-full px-3 py-2 border-2 border-sky-200 rounded text-sm font-mono"
                        multiline={true}
                        rows={4}
                      />
                    </div>
                  )}

                  {/* Options */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs font-bold text-sky-700 mb-1">⏱️ Timeout (sec)</label>
                      <input
                        type="number"
                        value={selectedNode.data.timeout || 30}
                        onChange={(e) => updateNodeData('timeout', parseInt(e.target.value))}
                        className="w-full px-2 py-1 border-2 border-sky-200 rounded text-sm"
                        min={1}
                        max={300}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-sky-700 mb-1">🔄 Retry Count</label>
                      <input
                        type="number"
                        value={selectedNode.data.retry_count || 0}
                        onChange={(e) => updateNodeData('retry_count', parseInt(e.target.value))}
                        className="w-full px-2 py-1 border-2 border-sky-200 rounded text-sm"
                        min={0}
                        max={10}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-sky-700 mb-1">📥 Response</label>
                      <select
                        value={selectedNode.data.response_type || 'json'}
                        onChange={(e) => updateNodeData('response_type', e.target.value)}
                        className="w-full px-2 py-1 border-2 border-sky-200 rounded text-sm"
                      >
                        <option value="json">JSON</option>
                        <option value="text">Text</option>
                        <option value="binary">Binary</option>
                      </select>
                    </div>
                  </div>

                  {/* Output Variable */}
                  <div>
                    <label className="block text-xs font-bold text-sky-700 mb-1">💾 Salva Response in Variabile</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || ''}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      placeholder="http_response"
                      className="w-full px-3 py-2 border-2 border-sky-200 rounded text-sm"
                    />
                  </div>

                  <p className="text-xs text-sky-600">
                    💡 Usa <code className="bg-sky-200 px-1 rounded">{'{{variabile}}'}</code> per valori dinamici
                  </p>
                </div>
              )}

              {/* EMAIL SEND NODE */}
              {selectedNode.type === 'email_send' && (
                <div className="bg-gradient-to-r from-rose-100 to-pink-50 p-4 rounded-xl border-2 border-rose-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">📧</span>
                    <label className="text-lg font-bold text-rose-800">Email Send</label>
                  </div>

                  {/* To */}
                  <div>
                    <label className="block text-xs font-bold text-rose-700 mb-1">📬 To (destinatari)</label>
                    <VariableAutocomplete
                      value={Array.isArray(selectedNode.data.to_emails) ? selectedNode.data.to_emails.join(', ') : (selectedNode.data.to_emails || '')}
                      onChange={(value) => updateNodeData('to_emails', value.split(',').map((e: string) => e.trim()).filter(Boolean))}
                      availableVariables={getAvailableVariables()}
                      placeholder="email1@example.com, email2@example.com"
                      className="w-full px-3 py-2 border-2 border-rose-200 rounded text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Separa più email con virgola</p>
                  </div>

                  {/* CC / BCC */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-bold text-rose-700 mb-1">📋 CC</label>
                      <input
                        type="text"
                        value={selectedNode.data.cc_emails || ''}
                        onChange={(e) => updateNodeData('cc_emails', e.target.value)}
                        placeholder="cc@example.com"
                        className="w-full px-2 py-1 border border-rose-200 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-rose-700 mb-1">🔒 BCC</label>
                      <input
                        type="text"
                        value={selectedNode.data.bcc_emails || ''}
                        onChange={(e) => updateNodeData('bcc_emails', e.target.value)}
                        placeholder="bcc@example.com"
                        className="w-full px-2 py-1 border border-rose-200 rounded text-sm"
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-xs font-bold text-rose-700 mb-1">📋 Subject</label>
                    <VariableAutocomplete
                      value={selectedNode.data.subject || ''}
                      onChange={(value) => updateNodeData('subject', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="Oggetto email"
                      className="w-full px-3 py-2 border-2 border-rose-200 rounded text-sm"
                    />
                  </div>

                  {/* Body */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-xs font-bold text-rose-700">📝 Body</label>
                      <select
                        value={selectedNode.data.body_type || 'text'}
                        onChange={(e) => updateNodeData('body_type', e.target.value)}
                        className="text-xs border border-rose-200 rounded px-2 py-1"
                      >
                        <option value="text">Plain Text</option>
                        <option value="html">HTML</option>
                      </select>
                    </div>
                    <VariableAutocomplete
                      value={selectedNode.data.body || ''}
                      onChange={(value) => updateNodeData('body', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="Contenuto dell'email..."
                      className="w-full px-3 py-2 border-2 border-rose-200 rounded text-sm"
                      multiline={true}
                      rows={6}
                    />
                  </div>

                  {/* Email Provider Selection */}
                  <div className="bg-rose-50 p-3 rounded-lg space-y-3">
                    <div>
                      <label className="block text-xs font-bold text-rose-700 mb-1">📮 Provider Email</label>
                      <select
                        value={selectedNode.data.email_provider || 'smtp'}
                        onChange={(e) => updateNodeData('email_provider', e.target.value)}
                        className="w-full px-3 py-2 border-2 border-rose-200 rounded text-sm"
                      >
                        <option value="smtp">📧 SMTP (Gmail, Custom)</option>
                        <option value="mailgun">📬 Mailgun</option>
                        <option value="sendgrid">📨 SendGrid</option>
                        <option value="resend">✉️ Resend</option>
                      </select>
                    </div>

                    {/* SMTP Config */}
                    {(selectedNode.data.email_provider || 'smtp') === 'smtp' && (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-rose-600 mb-1">Host</label>
                            <input
                              type="text"
                              value={selectedNode.data.smtp_host || ''}
                              onChange={(e) => updateNodeData('smtp_host', e.target.value)}
                              placeholder="smtp.gmail.com"
                              className="w-full px-2 py-1 border border-rose-200 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-rose-600 mb-1">Port</label>
                            <input
                              type="number"
                              value={selectedNode.data.smtp_port || 587}
                              onChange={(e) => updateNodeData('smtp_port', parseInt(e.target.value))}
                              className="w-full px-2 py-1 border border-rose-200 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-rose-600 mb-1">Username</label>
                            <input
                              type="text"
                              value={selectedNode.data.smtp_user || ''}
                              onChange={(e) => updateNodeData('smtp_user', e.target.value)}
                              placeholder="user@gmail.com"
                              className="w-full px-2 py-1 border border-rose-200 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-rose-600 mb-1">Password</label>
                            <input
                              type="password"
                              value={selectedNode.data.smtp_password || ''}
                              onChange={(e) => updateNodeData('smtp_password', e.target.value)}
                              placeholder="••••••••"
                              className="w-full px-2 py-1 border border-rose-200 rounded text-sm"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-1 text-xs text-rose-600">
                            <input
                              type="checkbox"
                              checked={selectedNode.data.smtp_ssl || false}
                              onChange={(e) => updateNodeData('smtp_ssl', e.target.checked)}
                            />
                            SSL/TLS
                          </label>
                          <label className="flex items-center gap-1 text-xs text-rose-600">
                            <input
                              type="checkbox"
                              checked={selectedNode.data.smtp_starttls !== false}
                              onChange={(e) => updateNodeData('smtp_starttls', e.target.checked)}
                            />
                            STARTTLS
                          </label>
                        </div>
                      </div>
                    )}

                    {/* Mailgun Config */}
                    {selectedNode.data.email_provider === 'mailgun' && (
                      <div className="space-y-2">
                        <div>
                          <label className="block text-xs text-rose-600 mb-1">🔑 API Key</label>
                          <input
                            type="password"
                            value={selectedNode.data.mailgun_api_key || ''}
                            onChange={(e) => updateNodeData('mailgun_api_key', e.target.value)}
                            placeholder="key-xxxxxxxxxxxxxxxx"
                            className="w-full px-2 py-1 border border-rose-200 rounded text-sm font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-rose-600 mb-1">🌐 Domain</label>
                          <input
                            type="text"
                            value={selectedNode.data.mailgun_domain || ''}
                            onChange={(e) => updateNodeData('mailgun_domain', e.target.value)}
                            placeholder="mg.tuodominio.com"
                            className="w-full px-2 py-1 border border-rose-200 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-rose-600 mb-1">🇪🇺 Region</label>
                          <select
                            value={selectedNode.data.mailgun_region || 'us'}
                            onChange={(e) => updateNodeData('mailgun_region', e.target.value)}
                            className="w-full px-2 py-1 border border-rose-200 rounded text-sm"
                          >
                            <option value="us">🇺🇸 US (api.mailgun.net)</option>
                            <option value="eu">🇪🇺 EU (api.eu.mailgun.net)</option>
                          </select>
                        </div>
                        <p className="text-xs text-gray-500">
                          💡 Ottieni API key da <a href="https://app.mailgun.com/app/account/security/api_keys" target="_blank" className="text-blue-500 underline">Mailgun Dashboard</a>
                        </p>
                      </div>
                    )}

                    {/* SendGrid Config */}
                    {selectedNode.data.email_provider === 'sendgrid' && (
                      <div className="space-y-2">
                        <div>
                          <label className="block text-xs text-rose-600 mb-1">🔑 API Key</label>
                          <input
                            type="password"
                            value={selectedNode.data.sendgrid_api_key || ''}
                            onChange={(e) => updateNodeData('sendgrid_api_key', e.target.value)}
                            placeholder="SG.xxxxxxxxxxxxxxxx"
                            className="w-full px-2 py-1 border border-rose-200 rounded text-sm font-mono"
                          />
                        </div>
                        <p className="text-xs text-gray-500">
                          💡 Ottieni API key da <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" className="text-blue-500 underline">SendGrid Dashboard</a>
                        </p>
                      </div>
                    )}

                    {/* Resend Config */}
                    {selectedNode.data.email_provider === 'resend' && (
                      <div className="space-y-2">
                        <div>
                          <label className="block text-xs text-rose-600 mb-1">🔑 API Key</label>
                          <input
                            type="password"
                            value={selectedNode.data.resend_api_key || ''}
                            onChange={(e) => updateNodeData('resend_api_key', e.target.value)}
                            placeholder="re_xxxxxxxxxxxxxxxx"
                            className="w-full px-2 py-1 border border-rose-200 rounded text-sm font-mono"
                          />
                        </div>
                        <p className="text-xs text-gray-500">
                          💡 Ottieni API key da <a href="https://resend.com/api-keys" target="_blank" className="text-blue-500 underline">Resend Dashboard</a>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* From */}
                  <div>
                    <label className="block text-xs font-bold text-rose-700 mb-1">👤 From (mittente)</label>
                    <input
                      type="text"
                      value={selectedNode.data.from_email || ''}
                      onChange={(e) => updateNodeData('from_email', e.target.value)}
                      placeholder="noreply@myapp.com"
                      className="w-full px-3 py-2 border-2 border-rose-200 rounded text-sm"
                    />
                  </div>
                </div>
              )}

              {/* WAIT NODE */}
              {selectedNode.type === 'wait' && (
                <div className="bg-gradient-to-r from-slate-100 to-gray-50 p-4 rounded-xl border-2 border-slate-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">⏳</span>
                    <label className="text-lg font-bold text-slate-800">Wait / Delay</label>
                  </div>

                  {/* Wait Type */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">⏱️ Tipo Attesa</label>
                    <select
                      value={selectedNode.data.wait_type || 'seconds'}
                      onChange={(e) => updateNodeData('wait_type', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded text-sm"
                    >
                      <option value="seconds">Secondi</option>
                      <option value="minutes">Minuti</option>
                      <option value="hours">Ore</option>
                      <option value="until">Fino a data/ora</option>
                    </select>
                  </div>

                  {selectedNode.data.wait_type !== 'until' ? (
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        ⏲️ Durata ({selectedNode.data.wait_type || 'secondi'})
                      </label>
                      <input
                        type="number"
                        value={selectedNode.data.wait_seconds || 5}
                        onChange={(e) => updateNodeData('wait_seconds', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border-2 border-slate-200 rounded text-sm text-2xl font-bold text-center"
                        min={1}
                        max={86400}
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">📅 Attendi fino a</label>
                      <input
                        type="datetime-local"
                        value={selectedNode.data.wait_until || ''}
                        onChange={(e) => updateNodeData('wait_until', e.target.value)}
                        className="w-full px-3 py-2 border-2 border-slate-200 rounded text-sm"
                      />
                    </div>
                  )}

                  <p className="text-xs text-slate-600">
                    💡 Il flusso si ferma qui per il tempo specificato prima di continuare.
                  </p>
                </div>
              )}

              {/* SET VARIABLE NODE */}
              {selectedNode.type === 'set' && (
                <div className="bg-gradient-to-r from-lime-100 to-green-50 p-4 rounded-xl border-2 border-lime-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">📝</span>
                    <label className="text-lg font-bold text-lime-800">Set Variable</label>
                  </div>

                  {/* Variable Name */}
                  <div>
                    <label className="block text-xs font-bold text-lime-700 mb-1">📌 Nome Variabile</label>
                    <input
                      type="text"
                      value={selectedNode.data.variable_name || ''}
                      onChange={(e) => updateNodeData('variable_name', e.target.value)}
                      placeholder="my_variable"
                      className="w-full px-3 py-2 border-2 border-lime-200 rounded text-sm font-mono"
                    />
                  </div>

                  {/* Value Type */}
                  <div>
                    <label className="block text-xs font-bold text-lime-700 mb-1">🔧 Tipo Valore</label>
                    <select
                      value={selectedNode.data.value_type || 'static'}
                      onChange={(e) => updateNodeData('value_type', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-lime-200 rounded text-sm"
                    >
                      <option value="static">Valore Statico</option>
                      <option value="expression">Espressione</option>
                      <option value="json">JSON Object</option>
                    </select>
                  </div>

                  {/* Value */}
                  <div>
                    <label className="block text-xs font-bold text-lime-700 mb-1">
                      {selectedNode.data.value_type === 'expression' ? '🧮 Espressione' : '📦 Valore'}
                    </label>
                    <VariableAutocomplete
                      value={selectedNode.data.value || ''}
                      onChange={(value) => updateNodeData('value', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder={selectedNode.data.value_type === 'expression' ? '{{var1}} + {{var2}}' : 'valore'}
                      className="w-full px-3 py-2 border-2 border-lime-200 rounded text-sm font-mono"
                      multiline={selectedNode.data.value_type === 'json'}
                      rows={selectedNode.data.value_type === 'json' ? 4 : 1}
                    />
                  </div>

                  <p className="text-xs text-lime-600">
                    💡 La variabile sarà disponibile nei nodi successivi come <code className="bg-lime-200 px-1 rounded">{`{{${selectedNode.data.variable_name || 'nome'}}}`}</code>
                  </p>
                </div>
              )}

              {/* AGENT NODE (Multi-Agent) */}
              {selectedNode.type === 'agent' && (
                <div className="bg-gradient-to-r from-purple-100 to-pink-50 p-4 rounded-xl border-2 border-purple-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🤖</span>
                    <label className="text-lg font-bold text-purple-800">Multi-Agent</label>
                  </div>

                  {/* Agent ID */}
                  <div>
                    <label className="block text-xs font-bold text-purple-700 mb-1">🆔 Agent ID</label>
                    <input
                      type="text"
                      value={selectedNode.data.agent_id || ''}
                      onChange={(e) => updateNodeData('agent_id', e.target.value)}
                      placeholder="my-agent-id"
                      className="w-full px-3 py-2 border-2 border-purple-200 rounded text-sm font-mono"
                    />
                    <p className="text-xs text-purple-600 mt-1">
                      💡 ID dell'agente registrato (deve essere registrato con /agents/register)
                    </p>
                  </div>

                  {/* Input Message */}
                  <div>
                    <label className="block text-xs font-bold text-purple-700 mb-1">📥 Input Message</label>
                    <VariableAutocomplete
                      value={selectedNode.data.input_message || ''}
                      onChange={(value) => updateNodeData('input_message', value)}
                      availableVariables={getAvailableVariables()}
                      placeholder="Messaggio da inviare all'agente (usa {{variabile}})"
                      className="w-full px-3 py-2 border-2 border-purple-200 rounded text-sm"
                      multiline
                      rows={3}
                    />
                    <p className="text-xs text-purple-600 mt-1">
                      💡 Se vuoto, usa l'input dell'utente corrente
                    </p>
                  </div>

                  {/* Output Variable */}
                  <div>
                    <label className="block text-xs font-bold text-purple-700 mb-1">📤 Variabile Output</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || 'agent_response'}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      placeholder="agent_response"
                      className="w-full px-3 py-2 border-2 border-purple-200 rounded text-sm font-mono"
                    />
                    <p className="text-xs text-purple-600 mt-1">
                      💡 La risposta dell'agente sarà salvata in questa variabile (usabile come <code className="bg-purple-200 px-1 rounded">{'{{agent_response}}'}</code>)
                    </p>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-4">
                    <p className="text-xs text-purple-800 font-semibold mb-1">📚 Come funziona:</p>
                    <ul className="text-xs text-purple-700 space-y-1 list-disc list-inside">
                      <li>Registra un flow come agente con <code className="bg-purple-200 px-1 rounded">/agents/register</code></li>
                      <li>Chiama l'agente da questo nodo passando input</li>
                      <li>La risposta sarà disponibile nella variabile output</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* CODE (PYTHON) NODE */}
              {selectedNode.type === 'code' && (
                <div className="bg-gradient-to-r from-emerald-100 to-green-50 p-4 rounded-xl border-2 border-emerald-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🐍</span>
                    <label className="text-lg font-bold text-emerald-800">Python Code</label>
                  </div>

                  {/* Code Editor */}
                  <div>
                    <label className="block text-xs font-bold text-emerald-700 mb-1">📝 Codice Python</label>
                    <textarea
                      value={selectedNode.data.code || '# Accedi alle variabili con: variables["nome_var"]\n# Ritorna il risultato con: return {"output": value}\n\nresult = "Hello World"\nreturn {"output": result}'}
                      onChange={(e) => updateNodeData('code', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-emerald-200 rounded text-sm font-mono bg-gray-900 text-green-400"
                      rows={12}
                      spellCheck={false}
                    />
                  </div>

                  {/* Input Variables */}
                  <div>
                    <label className="block text-xs font-bold text-emerald-700 mb-1">📥 Input Variables (disponibili nel codice)</label>
                    <input
                      type="text"
                      value={Array.isArray(selectedNode.data.input_vars) ? selectedNode.data.input_vars.join(', ') : (selectedNode.data.input_vars || '')}
                      onChange={(e) => updateNodeData('input_vars', e.target.value.split(',').map((v: string) => v.trim()).filter(Boolean))}
                      placeholder="var1, var2, var3"
                      className="w-full px-3 py-2 border-2 border-emerald-200 rounded text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Separa con virgola. Accessibili come variables["var1"]</p>
                  </div>

                  {/* Output Variable */}
                  <div>
                    <label className="block text-xs font-bold text-emerald-700 mb-1">📤 Output Variable</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || 'code_result'}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      placeholder="code_result"
                      className="w-full px-3 py-2 border-2 border-emerald-200 rounded text-sm font-mono"
                    />
                  </div>

                  {/* Timeout */}
                  <div>
                    <label className="block text-xs font-bold text-emerald-700 mb-1">⏱️ Timeout (secondi)</label>
                    <input
                      type="number"
                      value={selectedNode.data.timeout || 30}
                      onChange={(e) => updateNodeData('timeout', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border-2 border-emerald-200 rounded text-sm"
                      min={1}
                      max={300}
                    />
                  </div>

                  <div className="bg-yellow-50 p-2 rounded text-xs text-yellow-800">
                    ⚠️ Il codice viene eseguito in un sandbox sicuro con timeout. Non tutti i moduli Python sono disponibili.
                  </div>
                </div>
              )}

              {/* RSS FEED NODE */}
              {selectedNode.type === 'rss_feed' && (
                <div className="bg-gradient-to-r from-orange-100 to-amber-50 p-4 rounded-xl border-2 border-orange-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">📰</span>
                    <label className="text-lg font-bold text-orange-800">RSS Feed Reader</label>
                  </div>

                  {/* Feed URLs */}
                  <div>
                    <label className="block text-xs font-bold text-orange-700 mb-1">🔗 Feed URLs</label>
                    <textarea
                      value={Array.isArray(selectedNode.data.feed_urls) ? selectedNode.data.feed_urls.join('\n') : (selectedNode.data.feed_urls || '')}
                      onChange={(e) => updateNodeData('feed_urls', e.target.value.split('\n').map((u: string) => u.trim()).filter(Boolean))}
                      placeholder="https://blog.example.com/feed.xml&#10;https://news.ycombinator.com/rss"
                      className="w-full px-3 py-2 border-2 border-orange-200 rounded text-sm font-mono"
                      rows={3}
                    />
                    <p className="text-xs text-gray-500 mt-1">Un URL per riga (RSS o Atom)</p>
                  </div>

                  {/* 🔍 KEYWORD FILTER - NEW! */}
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <label className="block text-xs font-bold text-amber-700 mb-1">🔍 Filtra per Parole Chiave (OR)</label>
                    <input
                      type="text"
                      value={selectedNode.data.keywords || ''}
                      onChange={(e) => updateNodeData('keywords', e.target.value)}
                      placeholder="AI, startup, tecnologia, innovazione"
                      className="w-full px-3 py-2 border-2 border-amber-200 rounded text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Parole separate da virgola. Passa se contiene ALMENO UNA keyword.</p>
                    
                    <div className="mt-2">
                      <label className="block text-xs font-bold text-amber-700 mb-1">📍 Cerca in:</label>
                      <select
                        value={selectedNode.data.keywords_match || 'title_content'}
                        onChange={(e) => updateNodeData('keywords_match', e.target.value)}
                        className="w-full px-3 py-2 border-2 border-amber-200 rounded text-sm"
                      >
                        <option value="title">Solo Titolo</option>
                        <option value="content">Solo Contenuto</option>
                        <option value="title_content">Titolo + Contenuto</option>
                      </select>
                    </div>
                  </div>

                  {/* Options */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-orange-700 mb-1">📊 Max Items (dopo filtro)</label>
                      <input
                        type="number"
                        value={selectedNode.data.max_items || 5}
                        onChange={(e) => updateNodeData('max_items', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border-2 border-orange-200 rounded text-sm"
                        min={1}
                        max={50}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-orange-700 mb-1">⏰ Solo ultime N ore</label>
                      <input
                        type="number"
                        value={selectedNode.data.filter_hours || 0}
                        onChange={(e) => updateNodeData('filter_hours', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border-2 border-orange-200 rounded text-sm"
                        min={0}
                        placeholder="0 = tutti"
                      />
                    </div>
                  </div>

                  {/* Output Variable */}
                  <div>
                    <label className="block text-xs font-bold text-orange-700 mb-1">💾 Salva Items in Variabile</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || 'rss_items'}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      placeholder="rss_items"
                      className="w-full px-3 py-2 border-2 border-orange-200 rounded text-sm font-mono"
                    />
                  </div>

                  <p className="text-xs text-orange-600">
                    💡 Output: array di items con title, link, published, content, author
                  </p>
                </div>
              )}

              {/* SWITCH NODE */}
              {selectedNode.type === 'switch' && (
                <div className="bg-gradient-to-r from-violet-100 to-purple-50 p-4 rounded-xl border-2 border-violet-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🔀</span>
                    <label className="text-lg font-bold text-violet-800">Switch</label>
                  </div>

                  {/* Variable to Check */}
                  <div>
                    <label className="block text-xs font-bold text-violet-700 mb-1">📌 Variabile da Valutare</label>
                    <select
                      value={selectedNode.data.variable || ''}
                      onChange={(e) => updateNodeData('variable', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-violet-200 rounded text-sm"
                    >
                      <option value="">-- Seleziona Variabile --</option>
                      {getAvailableVariables().map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </div>

                  {/* Cases */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-xs font-bold text-violet-700">🔢 Cases</label>
                      <button
                        onClick={() => {
                          const cases = selectedNode.data.cases || [];
                          updateNodeData('cases', [...cases, { value: '', label: `Case ${cases.length + 1}` }]);
                        }}
                        className="text-xs bg-violet-500 text-white px-2 py-1 rounded hover:bg-violet-600"
                      >
                        + Aggiungi Case
                      </button>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(selectedNode.data.cases || []).map((c: any, idx: number) => (
                        <div key={idx} className="flex gap-2 items-center bg-violet-50 p-2 rounded">
                          <span className="text-xs font-bold text-violet-600 w-6">{idx + 1}.</span>
                          <input
                            type="text"
                            value={c.value || ''}
                            onChange={(e) => {
                              const cases = [...(selectedNode.data.cases || [])];
                              cases[idx] = { ...cases[idx], value: e.target.value };
                              updateNodeData('cases', cases);
                            }}
                            placeholder="valore"
                            className="flex-1 px-2 py-1 border border-violet-200 rounded text-sm"
                          />
                          <button
                            onClick={() => {
                              const cases = (selectedNode.data.cases || []).filter((_: any, i: number) => i !== idx);
                              updateNodeData('cases', cases);
                            }}
                            className="text-red-500 hover:text-red-700 text-lg"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedNode.data.has_default || false}
                      onChange={(e) => updateNodeData('has_default', e.target.checked)}
                    />
                    <label className="text-xs text-violet-700">Includi ramo "default" (se nessun case matcha)</label>
                  </div>

                  <p className="text-xs text-violet-600">
                    💡 Ogni case avrà un'uscita separata. Collega gli edge ai nodi corrispondenti.
                  </p>
                </div>
              )}

              {/* FILTER NODE */}
              {selectedNode.type === 'filter' && (
                <div className="bg-gradient-to-r from-cyan-100 to-teal-50 p-4 rounded-xl border-2 border-cyan-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🔍</span>
                    <label className="text-lg font-bold text-cyan-800">Filter</label>
                  </div>

                  {/* Field to Filter */}
                  <div>
                    <label className="block text-xs font-bold text-cyan-700 mb-1">📌 Campo da Filtrare</label>
                    <input
                      type="text"
                      value={selectedNode.data.field || ''}
                      onChange={(e) => updateNodeData('field', e.target.value)}
                      placeholder="item.status"
                      className="w-full px-3 py-2 border-2 border-cyan-200 rounded text-sm font-mono"
                    />
                  </div>

                  {/* Operator */}
                  <div>
                    <label className="block text-xs font-bold text-cyan-700 mb-1">⚙️ Operatore</label>
                    <select
                      value={selectedNode.data.operator || 'equals'}
                      onChange={(e) => updateNodeData('operator', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-cyan-200 rounded text-sm"
                    >
                      <option value="equals">Uguale (==)</option>
                      <option value="not_equals">Diverso (!=)</option>
                      <option value="contains">Contiene</option>
                      <option value="not_contains">Non Contiene</option>
                      <option value="starts_with">Inizia Con</option>
                      <option value="ends_with">Finisce Con</option>
                      <option value="greater">Maggiore (&gt;)</option>
                      <option value="greater_eq">Maggiore o Uguale (&gt;=)</option>
                      <option value="less">Minore (&lt;)</option>
                      <option value="less_eq">Minore o Uguale (&lt;=)</option>
                      <option value="is_empty">È Vuoto</option>
                      <option value="is_not_empty">Non È Vuoto</option>
                      <option value="regex">Regex Match</option>
                    </select>
                  </div>

                  {/* Value */}
                  {!['is_empty', 'is_not_empty'].includes(selectedNode.data.operator || '') && (
                    <div>
                      <label className="block text-xs font-bold text-cyan-700 mb-1">📦 Valore</label>
                      <VariableAutocomplete
                        value={selectedNode.data.value || ''}
                        onChange={(value) => updateNodeData('value', value)}
                        availableVariables={getAvailableVariables()}
                        placeholder="valore da confrontare"
                        className="w-full px-3 py-2 border-2 border-cyan-200 rounded text-sm"
                      />
                    </div>
                  )}

                  {/* Case Sensitive */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedNode.data.case_sensitive || false}
                      onChange={(e) => updateNodeData('case_sensitive', e.target.checked)}
                    />
                    <label className="text-xs text-cyan-700">Case Sensitive</label>
                  </div>

                  <p className="text-xs text-cyan-600">
                    💡 Il filtro ha 2 uscite: ✅ Match (true) e ❌ No Match (false)
                  </p>
                </div>
              )}

              {/* AGGREGATE NODE */}
              {selectedNode.type === 'aggregate' && (
                <div className="bg-gradient-to-r from-fuchsia-100 to-pink-50 p-4 rounded-xl border-2 border-fuchsia-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">📊</span>
                    <label className="text-lg font-bold text-fuchsia-800">Aggregate</label>
                  </div>

                  {/* Input Variable (array) */}
                  <div>
                    <label className="block text-xs font-bold text-fuchsia-700 mb-1">📥 Array Input</label>
                    <select
                      value={selectedNode.data.input_variable || ''}
                      onChange={(e) => updateNodeData('input_variable', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-fuchsia-200 rounded text-sm"
                    >
                      <option value="">-- Seleziona Variabile --</option>
                      {getAvailableVariables().map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </div>

                  {/* Operation */}
                  <div>
                    <label className="block text-xs font-bold text-fuchsia-700 mb-1">🔧 Operazione</label>
                    <select
                      value={selectedNode.data.operation || 'count'}
                      onChange={(e) => updateNodeData('operation', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-fuchsia-200 rounded text-sm"
                    >
                      <option value="count">Count (conta elementi)</option>
                      <option value="sum">Sum (somma)</option>
                      <option value="avg">Average (media)</option>
                      <option value="min">Min (minimo)</option>
                      <option value="max">Max (massimo)</option>
                      <option value="first">First (primo elemento)</option>
                      <option value="last">Last (ultimo elemento)</option>
                      <option value="concat">Concat (unisci in stringa)</option>
                      <option value="unique">Unique (valori unici)</option>
                    </select>
                  </div>

                  {/* Field (for sum, avg, min, max) */}
                  {['sum', 'avg', 'min', 'max'].includes(selectedNode.data.operation || '') && (
                    <div>
                      <label className="block text-xs font-bold text-fuchsia-700 mb-1">📌 Campo Numerico</label>
                      <input
                        type="text"
                        value={selectedNode.data.field || ''}
                        onChange={(e) => updateNodeData('field', e.target.value)}
                        placeholder="item.price"
                        className="w-full px-3 py-2 border-2 border-fuchsia-200 rounded text-sm font-mono"
                      />
                    </div>
                  )}

                  {/* Separator (for concat) */}
                  {selectedNode.data.operation === 'concat' && (
                    <div>
                      <label className="block text-xs font-bold text-fuchsia-700 mb-1">🔗 Separatore</label>
                      <input
                        type="text"
                        value={selectedNode.data.separator || ', '}
                        onChange={(e) => updateNodeData('separator', e.target.value)}
                        placeholder=", "
                        className="w-full px-3 py-2 border-2 border-fuchsia-200 rounded text-sm"
                      />
                    </div>
                  )}

                  {/* Output Variable */}
                  <div>
                    <label className="block text-xs font-bold text-fuchsia-700 mb-1">💾 Output Variable</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || 'aggregate_result'}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      placeholder="aggregate_result"
                      className="w-full px-3 py-2 border-2 border-fuchsia-200 rounded text-sm font-mono"
                    />
                  </div>
                </div>
              )}

              {/* SCHEDULE TRIGGER NODE */}
              {selectedNode.type === 'schedule_trigger' && (
                <div className="bg-gradient-to-r from-amber-100 to-yellow-50 p-4 rounded-xl border-2 border-amber-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">⏰</span>
                    <label className="text-lg font-bold text-amber-800">Schedule Trigger</label>
                  </div>

                  {/* Trigger Type */}
                  <div>
                    <label className="block text-xs font-bold text-amber-700 mb-1">⚙️ Tipo Trigger</label>
                    <select
                      value={selectedNode.data.trigger_type || 'interval'}
                      onChange={(e) => updateNodeData('trigger_type', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-amber-200 rounded text-sm"
                    >
                      <option value="interval">Intervallo</option>
                      <option value="cron">Cron Expression</option>
                      <option value="daily">Ogni Giorno</option>
                      <option value="weekly">Ogni Settimana</option>
                    </select>
                  </div>

                  {/* Interval */}
                  {selectedNode.data.trigger_type === 'interval' && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-bold text-amber-700 mb-1">⏱️ Ogni</label>
                        <input
                          type="number"
                          value={selectedNode.data.interval_value || 5}
                          onChange={(e) => updateNodeData('interval_value', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border-2 border-amber-200 rounded text-sm"
                          min={1}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-amber-700 mb-1">Unità</label>
                        <select
                          value={selectedNode.data.interval_unit || 'minutes'}
                          onChange={(e) => updateNodeData('interval_unit', e.target.value)}
                          className="w-full px-3 py-2 border-2 border-amber-200 rounded text-sm"
                        >
                          <option value="seconds">Secondi</option>
                          <option value="minutes">Minuti</option>
                          <option value="hours">Ore</option>
                          <option value="days">Giorni</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Cron */}
                  {selectedNode.data.trigger_type === 'cron' && (
                    <div>
                      <label className="block text-xs font-bold text-amber-700 mb-1">🕐 Cron Expression</label>
                      <input
                        type="text"
                        value={selectedNode.data.cron_expression || '0 9 * * *'}
                        onChange={(e) => updateNodeData('cron_expression', e.target.value)}
                        placeholder="0 9 * * *"
                        className="w-full px-3 py-2 border-2 border-amber-200 rounded text-sm font-mono"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Formato: minuto ora giorno mese giorno_settimana
                      </p>
                    </div>
                  )}

                  {/* Daily */}
                  {selectedNode.data.trigger_type === 'daily' && (
                    <div>
                      <label className="block text-xs font-bold text-amber-700 mb-1">🕐 Ora Esecuzione</label>
                      <input
                        type="time"
                        value={selectedNode.data.daily_time || '09:00'}
                        onChange={(e) => updateNodeData('daily_time', e.target.value)}
                        className="w-full px-3 py-2 border-2 border-amber-200 rounded text-sm"
                      />
                    </div>
                  )}

                  {/* Weekly */}
                  {selectedNode.data.trigger_type === 'weekly' && (
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-bold text-amber-700 mb-1">📅 Giorni</label>
                        <div className="flex flex-wrap gap-1">
                          {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((day, idx) => (
                            <button
                              key={day}
                              onClick={() => {
                                const days = selectedNode.data.weekly_days || [];
                                if (days.includes(idx)) {
                                  updateNodeData('weekly_days', days.filter((d: number) => d !== idx));
                                } else {
                                  updateNodeData('weekly_days', [...days, idx].sort());
                                }
                              }}
                              className={`px-2 py-1 text-xs rounded ${
                                (selectedNode.data.weekly_days || []).includes(idx)
                                  ? 'bg-amber-500 text-white'
                                  : 'bg-amber-100 text-amber-700'
                              }`}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-amber-700 mb-1">🕐 Ora</label>
                        <input
                          type="time"
                          value={selectedNode.data.weekly_time || '09:00'}
                          onChange={(e) => updateNodeData('weekly_time', e.target.value)}
                          className="w-full px-3 py-2 border-2 border-amber-200 rounded text-sm"
                        />
                      </div>
                    </div>
                  )}

                  {/* Timezone */}
                  <div>
                    <label className="block text-xs font-bold text-amber-700 mb-1">🌍 Timezone</label>
                    <select
                      value={selectedNode.data.timezone || 'Europe/Rome'}
                      onChange={(e) => updateNodeData('timezone', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-amber-200 rounded text-sm"
                    >
                      <option value="Europe/Rome">Europe/Rome (CET)</option>
                      <option value="Europe/London">Europe/London (GMT)</option>
                      <option value="America/New_York">America/New_York (EST)</option>
                      <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                      <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </div>

                  <div className="bg-amber-50 p-2 rounded text-xs text-amber-800">
                    ⚠️ Il trigger schedulato richiede che il flow sia attivo e configurato nello Scheduler Panel.
                  </div>
                </div>
              )}

              {/* SLACK NODE */}
              {selectedNode.type === 'slack' && (
                <div className="bg-gradient-to-r from-purple-100 to-violet-50 p-4 rounded-xl border-2 border-purple-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">💬</span>
                    <label className="text-lg font-bold text-purple-800">Slack</label>
                  </div>

                  {/* Operation */}
                  <div>
                    <label className="block text-xs font-bold text-purple-700 mb-1">⚙️ Operazione</label>
                    <select
                      value={selectedNode.data.operation || 'send_message'}
                      onChange={(e) => updateNodeData('operation', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-purple-200 rounded text-sm"
                    >
                      <option value="send_message">📤 Invia Messaggio</option>
                      <option value="list_channels">📋 Lista Canali</option>
                      <option value="get_user">👤 Info Utente</option>
                    </select>
                  </div>

                  {/* Channel */}
                  {selectedNode.data.operation !== 'get_user' && (
                    <div>
                      <label className="block text-xs font-bold text-purple-700 mb-1">📢 Canale</label>
                      <VariableAutocomplete
                        value={selectedNode.data.channel || ''}
                        onChange={(value) => updateNodeData('channel', value)}
                        availableVariables={getAvailableVariables()}
                        placeholder="#general o C1234567890"
                        className="w-full px-3 py-2 border-2 border-purple-200 rounded text-sm"
                      />
                    </div>
                  )}

                  {/* Message */}
                  {selectedNode.data.operation === 'send_message' && (
                    <div>
                      <label className="block text-xs font-bold text-purple-700 mb-1">💬 Messaggio</label>
                      <VariableAutocomplete
                        value={selectedNode.data.text || ''}
                        onChange={(value) => updateNodeData('text', value)}
                        availableVariables={getAvailableVariables()}
                        placeholder="Testo del messaggio..."
                        className="w-full px-3 py-2 border-2 border-purple-200 rounded text-sm"
                        multiline={true}
                        rows={4}
                      />
                    </div>
                  )}

                  {/* Thread */}
                  {selectedNode.data.operation === 'send_message' && (
                    <div>
                      <label className="block text-xs font-bold text-purple-700 mb-1">🧵 Thread TS (opzionale)</label>
                      <input
                        type="text"
                        value={selectedNode.data.thread_ts || ''}
                        onChange={(e) => updateNodeData('thread_ts', e.target.value)}
                        placeholder="1234567890.123456"
                        className="w-full px-3 py-2 border-2 border-purple-200 rounded text-sm font-mono"
                      />
                    </div>
                  )}

                  {/* Credentials */}
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <label className="block text-xs font-bold text-purple-700 mb-1">🔐 Credenziali</label>
                    <input
                      type="text"
                      value={selectedNode.data.credential_name || ''}
                      onChange={(e) => updateNodeData('credential_name', e.target.value)}
                      placeholder="Nome credenziale dal Vault"
                      className="w-full px-3 py-2 border border-purple-200 rounded text-sm"
                    />
                    <p className="text-xs text-purple-600 mt-1">
                      💡 Configura il token nel Vault Credenziali
                    </p>
                  </div>

                  {/* Output Variable */}
                  <div>
                    <label className="block text-xs font-bold text-purple-700 mb-1">💾 Output Variable</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || 'slack_result'}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-purple-200 rounded text-sm font-mono"
                    />
                  </div>
                </div>
              )}

              {/* TELEGRAM NODE */}
              {selectedNode.type === 'telegram' && (
                <div className="bg-gradient-to-r from-blue-100 to-cyan-50 p-4 rounded-xl border-2 border-blue-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">📱</span>
                    <label className="text-lg font-bold text-blue-800">Telegram</label>
                  </div>

                  {/* Operation */}
                  <div>
                    <label className="block text-xs font-bold text-blue-700 mb-1">⚙️ Operazione</label>
                    <select
                      value={selectedNode.data.operation || 'send_message'}
                      onChange={(e) => updateNodeData('operation', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-blue-200 rounded text-sm"
                    >
                      <option value="send_message">📤 Invia Messaggio</option>
                      <option value="send_photo">🖼️ Invia Foto</option>
                      <option value="get_updates">📥 Ottieni Updates</option>
                      <option value="get_me">🤖 Info Bot</option>
                    </select>
                  </div>

                  {/* Chat ID */}
                  {['send_message', 'send_photo'].includes(selectedNode.data.operation || 'send_message') && (
                    <div>
                      <label className="block text-xs font-bold text-blue-700 mb-1">👤 Chat ID</label>
                      <VariableAutocomplete
                        value={selectedNode.data.chat_id || ''}
                        onChange={(value) => updateNodeData('chat_id', value)}
                        availableVariables={getAvailableVariables()}
                        placeholder="123456789 o @username"
                        className="w-full px-3 py-2 border-2 border-blue-200 rounded text-sm"
                      />
                    </div>
                  )}

                  {/* Message */}
                  {selectedNode.data.operation === 'send_message' && (
                    <div>
                      <label className="block text-xs font-bold text-blue-700 mb-1">💬 Messaggio</label>
                      <VariableAutocomplete
                        value={selectedNode.data.text || ''}
                        onChange={(value) => updateNodeData('text', value)}
                        availableVariables={getAvailableVariables()}
                        placeholder="Testo del messaggio..."
                        className="w-full px-3 py-2 border-2 border-blue-200 rounded text-sm"
                        multiline={true}
                        rows={4}
                      />
                    </div>
                  )}

                  {/* Photo URL */}
                  {selectedNode.data.operation === 'send_photo' && (
                    <>
                      <div>
                        <label className="block text-xs font-bold text-blue-700 mb-1">🖼️ URL Foto</label>
                        <VariableAutocomplete
                          value={selectedNode.data.photo_url || ''}
                          onChange={(value) => updateNodeData('photo_url', value)}
                          availableVariables={getAvailableVariables()}
                          placeholder="https://example.com/photo.jpg"
                          className="w-full px-3 py-2 border-2 border-blue-200 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-blue-700 mb-1">📝 Didascalia</label>
                        <VariableAutocomplete
                          value={selectedNode.data.caption || ''}
                          onChange={(value) => updateNodeData('caption', value)}
                          availableVariables={getAvailableVariables()}
                          placeholder="Didascalia foto..."
                          className="w-full px-3 py-2 border-2 border-blue-200 rounded text-sm"
                        />
                      </div>
                    </>
                  )}

                  {/* Parse Mode */}
                  <div>
                    <label className="block text-xs font-bold text-blue-700 mb-1">📄 Formato Testo</label>
                    <select
                      value={selectedNode.data.parse_mode || 'HTML'}
                      onChange={(e) => updateNodeData('parse_mode', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-blue-200 rounded text-sm"
                    >
                      <option value="HTML">HTML</option>
                      <option value="Markdown">Markdown</option>
                      <option value="MarkdownV2">MarkdownV2</option>
                    </select>
                  </div>

                  {/* Bot Token */}
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <label className="block text-xs font-bold text-blue-700 mb-1">🔐 Credenziali</label>
                    <input
                      type="text"
                      value={selectedNode.data.credential_name || ''}
                      onChange={(e) => updateNodeData('credential_name', e.target.value)}
                      placeholder="Nome credenziale dal Vault"
                      className="w-full px-3 py-2 border border-blue-200 rounded text-sm"
                    />
                  </div>

                  {/* Output Variable */}
                  <div>
                    <label className="block text-xs font-bold text-blue-700 mb-1">💾 Output Variable</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || 'telegram_result'}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-blue-200 rounded text-sm font-mono"
                    />
                  </div>
                </div>
              )}

              {/* GOOGLE SHEETS NODE */}
              {selectedNode.type === 'google_sheets' && (
                <div className="bg-gradient-to-r from-green-100 to-emerald-50 p-4 rounded-xl border-2 border-green-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">📊</span>
                    <label className="text-lg font-bold text-green-800">Google Sheets</label>
                  </div>

                  {/* Operation */}
                  <div>
                    <label className="block text-xs font-bold text-green-700 mb-1">⚙️ Operazione</label>
                    <select
                      value={selectedNode.data.operation || 'read'}
                      onChange={(e) => updateNodeData('operation', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-green-200 rounded text-sm"
                    >
                      <option value="read">📖 Leggi Dati</option>
                      <option value="append">➕ Aggiungi Righe</option>
                      <option value="update">✏️ Aggiorna Celle</option>
                      <option value="clear">🗑️ Svuota Range</option>
                    </select>
                  </div>

                  {/* Spreadsheet ID */}
                  <div>
                    <label className="block text-xs font-bold text-green-700 mb-1">📄 Spreadsheet ID</label>
                    <input
                      type="text"
                      value={selectedNode.data.spreadsheet_id || ''}
                      onChange={(e) => updateNodeData('spreadsheet_id', e.target.value)}
                      placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                      className="w-full px-3 py-2 border-2 border-green-200 rounded text-sm font-mono"
                    />
                  </div>

                  {/* Range */}
                  <div>
                    <label className="block text-xs font-bold text-green-700 mb-1">📋 Range</label>
                    <input
                      type="text"
                      value={selectedNode.data.range || 'Sheet1!A1:Z1000'}
                      onChange={(e) => updateNodeData('range', e.target.value)}
                      placeholder="Sheet1!A1:D10"
                      className="w-full px-3 py-2 border-2 border-green-200 rounded text-sm"
                    />
                  </div>

                  {/* Values (for append/update) */}
                  {['append', 'update'].includes(selectedNode.data.operation || '') && (
                    <div>
                      <label className="block text-xs font-bold text-green-700 mb-1">📝 Dati (JSON array 2D)</label>
                      <VariableAutocomplete
                        value={typeof selectedNode.data.values === 'string' ? selectedNode.data.values : JSON.stringify(selectedNode.data.values || [], null, 2)}
                        onChange={(value) => {
                          try {
                            updateNodeData('values', JSON.parse(value));
                          } catch {
                            updateNodeData('values', value);
                          }
                        }}
                        availableVariables={getAvailableVariables()}
                        placeholder='[["col1", "col2"], ["val1", "val2"]]'
                        className="w-full px-3 py-2 border-2 border-green-200 rounded text-sm font-mono"
                        multiline={true}
                        rows={4}
                      />
                    </div>
                  )}

                  {/* First Row Header */}
                  {selectedNode.data.operation === 'read' && (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedNode.data.first_row_header || false}
                        onChange={(e) => updateNodeData('first_row_header', e.target.checked)}
                      />
                      <label className="text-xs text-green-700">Prima riga come header (ritorna oggetti)</label>
                    </div>
                  )}

                  {/* Credentials */}
                  <div className="bg-green-50 p-3 rounded-lg">
                    <label className="block text-xs font-bold text-green-700 mb-1">🔐 Credenziali OAuth</label>
                    <input
                      type="text"
                      value={selectedNode.data.credential_name || ''}
                      onChange={(e) => updateNodeData('credential_name', e.target.value)}
                      placeholder="Nome credenziale dal Vault"
                      className="w-full px-3 py-2 border border-green-200 rounded text-sm"
                    />
                  </div>

                  {/* Output Variable */}
                  <div>
                    <label className="block text-xs font-bold text-green-700 mb-1">💾 Output Variable</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || 'sheets_result'}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-green-200 rounded text-sm font-mono"
                    />
                  </div>
                </div>
              )}

              {/* AIRTABLE NODE */}
              {selectedNode.type === 'airtable' && (
                <div className="bg-gradient-to-r from-yellow-100 to-amber-50 p-4 rounded-xl border-2 border-yellow-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">📋</span>
                    <label className="text-lg font-bold text-yellow-800">Airtable</label>
                  </div>

                  {/* Operation */}
                  <div>
                    <label className="block text-xs font-bold text-yellow-700 mb-1">⚙️ Operazione</label>
                    <select
                      value={selectedNode.data.operation || 'list'}
                      onChange={(e) => updateNodeData('operation', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-yellow-200 rounded text-sm"
                    >
                      <option value="list">📋 Lista Record</option>
                      <option value="get">🔍 Ottieni Record</option>
                      <option value="create">➕ Crea Record</option>
                      <option value="update">✏️ Aggiorna Record</option>
                      <option value="delete">🗑️ Elimina Record</option>
                    </select>
                  </div>

                  {/* Base ID */}
                  <div>
                    <label className="block text-xs font-bold text-yellow-700 mb-1">🗄️ Base ID</label>
                    <input
                      type="text"
                      value={selectedNode.data.base_id || ''}
                      onChange={(e) => updateNodeData('base_id', e.target.value)}
                      placeholder="appXXXXXXXXXXXXXX"
                      className="w-full px-3 py-2 border-2 border-yellow-200 rounded text-sm font-mono"
                    />
                  </div>

                  {/* Table Name */}
                  <div>
                    <label className="block text-xs font-bold text-yellow-700 mb-1">📄 Nome Tabella</label>
                    <input
                      type="text"
                      value={selectedNode.data.table_name || ''}
                      onChange={(e) => updateNodeData('table_name', e.target.value)}
                      placeholder="Table 1"
                      className="w-full px-3 py-2 border-2 border-yellow-200 rounded text-sm"
                    />
                  </div>

                  {/* Record ID (for get/update/delete) */}
                  {['get', 'update', 'delete'].includes(selectedNode.data.operation || '') && (
                    <div>
                      <label className="block text-xs font-bold text-yellow-700 mb-1">🆔 Record ID</label>
                      <VariableAutocomplete
                        value={selectedNode.data.record_id || ''}
                        onChange={(value) => updateNodeData('record_id', value)}
                        availableVariables={getAvailableVariables()}
                        placeholder="recXXXXXXXXXXXXXX"
                        className="w-full px-3 py-2 border-2 border-yellow-200 rounded text-sm font-mono"
                      />
                    </div>
                  )}

                  {/* Filter Formula (for list) */}
                  {selectedNode.data.operation === 'list' && (
                    <div>
                      <label className="block text-xs font-bold text-yellow-700 mb-1">🔍 Filter Formula</label>
                      <VariableAutocomplete
                        value={selectedNode.data.filter_formula || ''}
                        onChange={(value) => updateNodeData('filter_formula', value)}
                        availableVariables={getAvailableVariables()}
                        placeholder="{Status} = 'Active'"
                        className="w-full px-3 py-2 border-2 border-yellow-200 rounded text-sm"
                      />
                    </div>
                  )}

                  {/* Fields (for create/update) */}
                  {['create', 'update'].includes(selectedNode.data.operation || '') && (
                    <div>
                      <label className="block text-xs font-bold text-yellow-700 mb-1">📝 Campi (JSON)</label>
                      <VariableAutocomplete
                        value={typeof selectedNode.data.fields === 'string' ? selectedNode.data.fields : JSON.stringify(selectedNode.data.fields || {}, null, 2)}
                        onChange={(value) => {
                          try {
                            updateNodeData('fields', JSON.parse(value));
                          } catch {
                            updateNodeData('fields', value);
                          }
                        }}
                        availableVariables={getAvailableVariables()}
                        placeholder='{"Name": "{{nome}}", "Status": "Active"}'
                        className="w-full px-3 py-2 border-2 border-yellow-200 rounded text-sm font-mono"
                        multiline={true}
                        rows={4}
                      />
                    </div>
                  )}

                  {/* Max Records (for list) */}
                  {selectedNode.data.operation === 'list' && (
                    <div>
                      <label className="block text-xs font-bold text-yellow-700 mb-1">📊 Max Record</label>
                      <input
                        type="number"
                        value={selectedNode.data.max_records || 100}
                        onChange={(e) => updateNodeData('max_records', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border-2 border-yellow-200 rounded text-sm"
                        min={1}
                        max={1000}
                      />
                    </div>
                  )}

                  {/* Credentials */}
                  <div className="bg-yellow-50 p-3 rounded-lg">
                    <label className="block text-xs font-bold text-yellow-700 mb-1">🔐 Credenziali</label>
                    <input
                      type="text"
                      value={selectedNode.data.credential_name || ''}
                      onChange={(e) => updateNodeData('credential_name', e.target.value)}
                      placeholder="Nome credenziale dal Vault"
                      className="w-full px-3 py-2 border border-yellow-200 rounded text-sm"
                    />
                  </div>

                  {/* Output Variable */}
                  <div>
                    <label className="block text-xs font-bold text-yellow-700 mb-1">💾 Output Variable</label>
                    <input
                      type="text"
                      value={selectedNode.data.output_variable || 'airtable_result'}
                      onChange={(e) => updateNodeData('output_variable', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-yellow-200 rounded text-sm font-mono"
                    />
                  </div>
                </div>
              )}

              {/* WEBHOOK TRIGGER NODE */}
              {selectedNode.type === 'webhook_trigger' && (
                <div className="bg-gradient-to-r from-indigo-100 to-purple-50 p-4 rounded-xl border-2 border-indigo-300 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🔗</span>
                    <label className="text-lg font-bold text-indigo-800">Webhook Trigger</label>
                  </div>

                  {/* Method */}
                  <div>
                    <label className="block text-xs font-bold text-indigo-700 mb-1">📤 Metodo HTTP</label>
                    <select
                      value={selectedNode.data.method || 'POST'}
                      onChange={(e) => updateNodeData('method', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-indigo-200 rounded text-sm"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                    </select>
                  </div>

                  {/* Path */}
                  <div>
                    <label className="block text-xs font-bold text-indigo-700 mb-1">🔗 Path</label>
                    <input
                      type="text"
                      value={selectedNode.data.path || ''}
                      onChange={(e) => updateNodeData('path', e.target.value)}
                      placeholder="my-webhook"
                      className="w-full px-3 py-2 border-2 border-indigo-200 rounded text-sm font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      URL: /webhook/trigger/{'<'}id{'>'}
                    </p>
                  </div>

                  {/* Auth Type */}
                  <div>
                    <label className="block text-xs font-bold text-indigo-700 mb-1">🔐 Autenticazione</label>
                    <select
                      value={selectedNode.data.auth_type || 'none'}
                      onChange={(e) => updateNodeData('auth_type', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-indigo-200 rounded text-sm"
                    >
                      <option value="none">Nessuna (pubblico)</option>
                      <option value="api_key">API Key</option>
                      <option value="signature">HMAC Signature</option>
                    </select>
                  </div>

                  {selectedNode.data.auth_type === 'api_key' && (
                    <div>
                      <label className="block text-xs font-bold text-indigo-700 mb-1">🔑 API Key</label>
                      <input
                        type="password"
                        value={selectedNode.data.api_key || ''}
                        onChange={(e) => updateNodeData('api_key', e.target.value)}
                        placeholder="Chiave API richiesta"
                        className="w-full px-3 py-2 border-2 border-indigo-200 rounded text-sm font-mono"
                      />
                    </div>
                  )}

                  <div className="bg-indigo-50 p-2 rounded text-xs text-indigo-800">
                    ⚠️ Configura il webhook nel pannello Webhook per attivarlo.
                  </div>
                </div>
              )}

              {selectedNode.type === 'end' && (
                <div className="bg-gradient-to-r from-red-100 to-red-50 p-4 rounded-xl border-2 border-red-300">
                  <label className="block text-sm font-bold text-red-800 mb-2">🏁 Messaggio Finale</label>
                  <VariableAutocomplete
                    value={selectedNode.data.message || ''}
                    onChange={(value) => updateNodeData('message', value)}
                    availableVariables={getAvailableVariables()}
                    placeholder="Messaggio finale (opzionale)"
                    className="w-full px-3 py-2 border-2 border-red-200 rounded-lg"
                    multiline={true}
                    rows={3}
                  />
                  <p className="text-xs text-red-600 mt-2">
                    💡 Usa <code className="bg-red-200 px-1 rounded">{'{{variabile}}'}</code> per inserire variabili o clicca sul bottone Variabili
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
              <div className="text-7xl mb-4">👆</div>
              <div className="text-xl font-bold">Seleziona un nodo</div>
              <div className="text-sm mt-2">per modificare le sue proprietà</div>
            </div>
          )}
        </div>
      </div>
      )}
      </div>
        )}

        {/* Knowledge Base Section */}
        {activeSection === 'knowledge' && (
          <KnowledgeBasePanel onSectionChange={setActiveSection} />
        )}

        {/* Index Management Section */}
        {activeSection === 'indices' && (
          <IndexManagementPanel />
        )}

        {/* Services Section */}
        {activeSection === 'services' && (
          <div className="flex-1 bg-gradient-to-br from-teal-50 to-cyan-50 p-8">
            <div className="max-w-6xl mx-auto">
              <h1 className="text-3xl font-bold text-teal-900 mb-4 flex items-center gap-3">
                <span>🔌</span>
                Servizi / API
              </h1>
              
              {/* TAB NAVIGATION */}
              <div className="flex space-x-2 mb-6">
                <button
                  onClick={() => setServicesTab('catalog')}
                  className={`px-6 py-3 rounded-lg font-bold transition-all ${
                    servicesTab === 'catalog'
                      ? 'bg-teal-600 text-white shadow-lg'
                      : 'bg-white text-teal-700 hover:bg-teal-100'
                  }`}
                >
                  📚 Service Catalog
                </button>
                <button
                  onClick={() => setServicesTab('connections')}
                  className={`px-6 py-3 rounded-lg font-bold transition-all ${
                    servicesTab === 'connections'
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'bg-white text-purple-700 hover:bg-purple-100'
                  }`}
                >
                  🔌 API Connections
                </button>
              </div>

              {/* TAB: Service Catalog */}
              {servicesTab === 'catalog' && (
                <>
                  <p className="text-teal-700 mb-8">
                    Catalogo di servizi e API disponibili per generare automaticamente flow completi.
                  </p>
                  
                  <div className="bg-white rounded-xl shadow-xl p-8 border-2 border-teal-300">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      ✨ Genera Flow da Servizi
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Seleziona un servizio dal catalogo e genera automaticamente un flow completo con:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                      <li>📥 Nodi INPUT per raccogliere i parametri necessari</li>
                      <li>🌐 Nodo API_CALL configurato per chiamare il servizio</li>
                      <li>💬 Nodi SAY per mostrare i risultati all'utente</li>
                      <li>🔄 Gestione automatica di variabili e flusso di dati</li>
                    </ul>
                    <button
                      onClick={() => setShowServicesPanel(true)}
                      className="w-full px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-xl text-lg flex items-center justify-center gap-3"
                    >
                      <span>🔍</span>
                      Sfoglia Catalogo Servizi
                    </button>
                    
                    <div className="mt-6 p-4 bg-teal-50 rounded-lg border border-teal-200">
                      <p className="text-sm text-teal-800">
                        💡 <strong>Suggerimento:</strong> Puoi anche aggiungere manualmente nodi API_CALL dalla palette e configurarli per chiamare qualsiasi API esterna.
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-8 bg-white rounded-xl shadow-md p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">📚 Servizi Disponibili</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      I servizi sono definiti in <code className="bg-gray-100 px-2 py-1 rounded">backend/services_registry.json</code>.
                      Puoi aggiungere i tuoi servizi modificando questo file.
                    </p>
                    <div className="flex gap-4">
                      <a
                        href={`${API_URL}/docs#/Services`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition text-sm font-semibold"
                      >
                        📖 API Documentation
                      </a>
                      <a
                        href={`${API_URL}/services/list`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition text-sm font-semibold"
                      >
                        🔍 View JSON
                      </a>
                    </div>
                  </div>
                </>
              )}

              {/* TAB: API Connections */}
              {servicesTab === 'connections' && (
                <ServicesAPIPanel />
              )}
            </div>
          </div>
        )}

        {/* Help Section */}
        {activeSection === 'help' && (
          <div className="flex-1 bg-gradient-to-br from-green-50 to-teal-50 p-8 overflow-auto">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">💡 Aiuto & Documentazione</h1>
              <p className="text-gray-600 mb-8">Tutto quello che ti serve per usare OYSTAR Flow Studio al meglio.</p>
              
              <div className="space-y-6">
                {/* Quick Start */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">🚀 Quick Start</h2>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    <li>Clicca su <strong>Genera</strong> per creare un flusso con AI</li>
                    <li>Oppure trascina nodi dalla palette a sinistra</li>
                    <li>Collega i nodi per definire il percorso conversazionale</li>
                    <li>Clicca su <strong>Test</strong> per provare il chatbot</li>
                    <li>Salva il flusso per riutilizzarlo in futuro</li>
                  </ol>
                </div>

                {/* Node Types */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">📦 Tipi di Nodi</h2>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><strong>START:</strong> Punto di inizio del flusso</div>
                    <div><strong>SAY:</strong> Il bot invia un messaggio</div>
                    <div><strong>INPUT:</strong> Richiede input dall'utente</div>
                    <div><strong>CONDITION:</strong> Condizione if/else</div>
                    <div><strong>CASE:</strong> Switch multiplo</div>
                    <div><strong>ACTION:</strong> Esegue codice JavaScript</div>
                    <div><strong>RAG:</strong> Cerca nella knowledge base</div>
                    <div><strong>END:</strong> Termina la conversazione</div>
                  </div>
                </div>

                {/* Keyboard Shortcuts */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">⌨️ Scorciatoie</h2>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><kbd className="bg-gray-200 px-2 py-1 rounded">Backspace</kbd> Elimina nodo selezionato</div>
                    <div><kbd className="bg-gray-200 px-2 py-1 rounded">Delete</kbd> Elimina nodo/edge selezionato</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Fine wrapper flex-col flex-1 */}
      
      {/* Metadata Panel Modal */}
      {showMetadataPanel && (
        <MetadataPanel
          botSettings={botSettings}
          flowVariables={flowVariables}
          intents={intents}
          knowledgeBase={knowledgeBase}
          documents={documents}
          onClose={() => setShowMetadataPanel(false)}
        />
      )}

      {/* Flow Library Panel */}
      {showFlowLibrary && (
        <FlowLibraryPanel
          onClose={() => setShowFlowLibrary(false)}
          onLoadFlow={handleLoadFlowFromLibrary}
        />
      )}

      {/* Generate Flow Panel */}
      {showGeneratePanel && (
        <GenerateFlowPanel
          onClose={() => {
            setShowGeneratePanel(false);
            setPromptToGenerate('');
          }}
          onGenerate={performGeneration}
          initialDescription={promptToGenerate}
        />
      )}

      {/* Promptify Panel */}
      {showPromptifyPanel && (
        <PromptifyPanel
          onClose={() => setShowPromptifyPanel(false)}
          onUseInGenerate={handleUsePromptInGenerate}
        />
      )}

      {/* Modify Flow Panel */}
      {showModifyPanel && (
        <ModifyFlowPanel
          currentFlow={{ id: flowName, version: '1.0', metadata: {}, nodes, edges }}
          onModified={(modifiedFlow) => {
            const uniqueEdges = deduplicateEdges(modifiedFlow.edges || []);
            setNodes(modifiedFlow.nodes || []);
            setEdges(uniqueEdges);
            setShowModifyPanel(false);
            alert('✅ Flow modificato con successo!');
          }}
          onClose={() => setShowModifyPanel(false)}
        />
      )}

      {/* Chat History Panel */}
      {showChatHistory && (
        <ChatHistoryPanel
          onClose={() => setShowChatHistory(false)}
          onLoadChat={handleLoadChatFromHistory}
        />
      )}

      {/* Services Panel */}
      {showServicesPanel && (
        <ServicesPanel
          isOpen={showServicesPanel}
          onClose={() => setShowServicesPanel(false)}
          onGenerateFlow={handleServiceFlowGeneration}
        />
      )}

      {/* Embed Code Generator */}
      {showEmbedGenerator && (
        <EmbedCodeGenerator
          flowDefinition={{
            id: flowName,
            nodes: assignNodeNumbers(nodes),  // Assicura che tutti i nodi abbiano nodeNumber
            edges,
          }}
          flowName={flowName}
          onClose={() => setShowEmbedGenerator(false)}
        />
      )}

      {/* Scheduler Panel (n8n-style) */}
      <SchedulerPanel
        isOpen={showSchedulerPanel}
        onClose={() => setShowSchedulerPanel(false)}
        currentFlowId={flowName}
        currentFlowName={flowName}
        currentFlowDefinition={{
          id: flowName,
          nodes: assignNodeNumbers(nodes),
          edges,
        }}
      />

      {/* Webhook Panel (n8n-style) */}
      <WebhookPanel
        isOpen={showWebhookPanel}
        onClose={() => setShowWebhookPanel(false)}
        currentFlowId={flowName}
        currentFlowName={flowName}
        currentFlowDefinition={{
          id: flowName,
          nodes: assignNodeNumbers(nodes),
          edges,
        }}
      />

      {/* Credentials Vault Panel */}
      <CredentialsPanel
        isOpen={showCredentialsPanel}
        onClose={() => setShowCredentialsPanel(false)}
        userId={user?.sub || 'default'}
      />

      {/* Executions History Panel */}
      <ExecutionsPanel
        isOpen={showExecutionsPanel}
        onClose={() => setShowExecutionsPanel(false)}
        userId={user?.sub || 'default'}
        currentFlowId={flowName}
      />

      {/* Templates Library Panel */}
      <TemplatesPanel
        isOpen={showTemplatesPanel}
        onClose={() => setShowTemplatesPanel(false)}
        onUseTemplate={(templateNodes, templateEdges, newFlowName) => {
          setNodes(templateNodes);
          setEdges(templateEdges);
          setFlowName(newFlowName);
        }}
        userId={user?.sub || 'default'}
      />

      {/* Versioning Panel */}
      <VersioningPanel
        isOpen={showVersioningPanel}
        onClose={() => setShowVersioningPanel(false)}
        flowId={flowName}
        flowName={flowName}
        currentNodes={nodes}
        currentEdges={edges}
        onRollback={(rollbackNodes, rollbackEdges) => {
          setNodes(rollbackNodes);
          setEdges(rollbackEdges);
        }}
        userId={user?.sub || 'default'}
      />

      {/* AI Assistant - Help Chatbot + Flow Generator */}
      <AIAssistant
        isOpen={showAIAssistant}
        onClose={() => setShowAIAssistant(false)}
        onImportFlow={handleImportAIFlow}
        userEmail={user?.email}
      />

      {/* Workspace Settings - Email, Branding */}
      <WorkspaceSettings
        isOpen={showWorkspaceSettings}
        onClose={() => setShowWorkspaceSettings(false)}
      />
    </div>
  );
}
